(function () {
  "use strict";
  if (window.__CG_GLAMAR_BOOTSTRAPPED) {
    return;
  }
  window.__CG_GLAMAR_BOOTSTRAPPED = true;

  /*
    CLIENT HANDOVER GUIDE - ASAYA AI SKIN ANALYSIS

    What this file owns:
    - Boots the GlamAR skin-analysis SDK inside the Shopify page.
    - Starts the camera scan flow and normalizes SDK result payloads.
    - Renders the result, questionnaire, diagnosis, and routine screens.
    - Gates scan results behind Kwikpass login when the shopper is anonymous.
    - Fetches live Asaya PIM products from the Pixelbin public misc proxy.
    - Converts sheet/PRD rules into the fixed AM/PM Asaya routine shown to users.
    - Resolves Shopify variant IDs and adds single products or the full routine
      to cart without opening the cart drawer until the full routine succeeds.

    Runtime flow:
    1. boot() runs once after DOM ready, removes old debug overlays, binds resize
       handlers, and wires landing/intro buttons.
    2. activateCameraStage() switches the wrapper into full camera mode, loads
       the GlamAR SDK via initSDK(), and asks the SDK to start skin analysis.
    3. bindSDKEvents() and bindPostMessageFallback() listen for SDK lifecycle,
       recommendation, scan metadata, and final result payloads.
    4. renderSkinAnalysisResult() stores the normalized scan, renders the result
       page, closes the SDK canvas, and either shows results directly or starts
       the Kwikpass login gate.
    5. showQuizStage() collects user answers. renderPersonalizedDiagnosis()
       combines scan data and quiz answers into readable diagnosis copy.
    6. renderRoutinePage() fetches PIM products and mapPimResourcesToRoutinePlan()
       applies the Asaya sheet rules to build a routine.
    7. renderRoutinePageWithPlan() renders the recommended set, build-your-own
       routine, body-care upsell, summary, share buttons, and cart controls.

    Recommendation contract:
    - ASAYA_CONCERN_CATEGORY_CONFIG is the client-facing concern/category
      switchboard. Toggle enabled true/false there to control whether a concern
      participates in scan parsing, diagnosis, PIM filtering, and sheet rules.
    - VALID_API_CONCERN_SET is derived from enabled categories in that config.
    - ASAYA_SHEET_RECOMMENDATION_RULES is the client sheet contract. A rule is
      active when the scan score for its concern is below the configured
      threshold. Lower scan score means higher need.
    - ASAYA_PIM_PRODUCT_TITLE_ALIASES maps sheet shorthand to exact PIM titles.
      Aliases should only bridge names to available products. Avoid using this
      map to silently substitute unrelated products.
    - ASAYA_DISCONTINUED_PRODUCT_TITLES prevents retired products from being
      shown. If a discontinued title has aliases, findPimResourceByTitle()
      keeps searching those aliases before reporting a missing product.
    - Dark circles is present in ASAYA_CONCERN_CATEGORY_CONFIG but disabled by
      default. Set dark_circles.enabled = true to enable it end to end.

    Routine construction:
    - getAsayaRuleContext() creates the decision context from scan concerns,
      quiz priority, pregnancy/postpartum answer, breakout answer, and skin type.
    - getAsayaSheetRuleSelection() chooses the first thresholded sheet rule from
      the sorted scan concerns. It drives the "Picked because ..." reason copy.
    - getAsayaRoutineRules() creates fixed slots:
      AM: Cleanser, Targeted Treatment, Moisturiser, Sunscreen.
      PM: Cleanser, Targeted Treatment, Repair.
    - addAsayaRuleProduct() fills each slot by trying sheet titles first, then
      scaffold fallbacks. A product can appear once per section/category slot.
    - isPimResourceExcludedForAsayaProfile() applies profile safety rules, such
      as avoiding salicylic/retinoid/kojic acid when the hormonal flag is set,
      and avoiding dry-skin exclusions.
    - appendBodyExtraToRoutinePlan() adds the body-care recommendation based on
      the final quiz answer, after the core routine is built.

    Cart behavior:
    - resolveRoutineCartVariantId() prefers explicit variant IDs, then looks up
      /products/{handle}.js and caches the first available variant.
    - addRoutineProductToCart() adds one card quietly.
    - addRoutineProductsToCart() adds the whole routine in a batched cart update,
      marks per-product buttons, and opens the cart only when all items succeed.

    Login and data sync:
    - isKwikpassFullyLoggedIn() checks Kwikpass token and Shopify customer
      signals. If it returns false, renderSkinAnalysisResult() starts the login
      gate and opens Kwikpass.
    - unlockSkinAnalysisAfterLogin() restores the result page after login and
      dispatches the result event. Boltic sync is currently disabled there by
      the commented syncBolticLoginRecordAfterLogin() call.
    - syncBolticLoginRecordAfterLogin() is still available for future enablement
      and sends phone plus raw SDK payload/metadata to the Boltic proxy.

    Method index by ownership:
    - Basic DOM/render safety: $(), getWrapperNode(), getAppNode(),
      getStageNode(), resetStageScroll(), scrollWrapperIntoView(),
      escapeHTML(), escapeAttr(), getPoweredFooterHTML().
    - Generic scan formatting: clampScore(), normalizeConcernKey(), toTitle(),
      getSeverityClass(), getSeverityLabel(), getHealthLabel(),
      safeCssSize(), getDefaultRegions(), getConcernImage(),
      getConcernDetailHTML().
    - Concern category toggles: isConcernCategoryEnabled(),
      getEnabledConcernCategorySet(), getEnabledConcernDiagnosisConfig(),
      getConcernCategoryAliasMap().
    - SDK payload normalization: getEventPayload(), getSkinAnalysisOption(),
      getSkinAnalysisValue(), getSkinOutput(), normalizeSkinResult(),
      handleRecommendationPayload(), handleScanMetadataPayload(),
      handleSkinAnalysisPayload(), tryRenderFromRawPayload().
    - Quiz state and navigation: getQuizAnswer(), shouldShowQuizQuestion(),
      getVisibleQuizQuestions(), getVisibleQuizPosition(),
      getNextVisibleQuizIndex(), getPreviousVisibleQuizIndex(),
      getLastVisibleQuizIndex(), ensureVisibleQuizIndex(),
      getQuizProgressHTML(), showQuizStage(), renderQuizStep(),
      submitQuizText(), goToNextQuizStep(), goToPreviousQuizStep(),
      wireQuizControls().
    - Diagnosis profile and copy: pushUnique(), getConcernConfig(),
      getConcernDisplayName(), getSkinTypeDisplayName(),
      getTopScanConcernItems(), getDiagnosisRuleLabels(),
      getQuizBreakoutDisplay(), getDiagnosisProfile(),
      getPrimaryConcernName(), getDiagnosisSkinType(),
      getRoutineSkinTypeCopy(), getDiagnosisBreakoutText(),
      getDiagnosisTriggerText(), hasDiagnosisKey(), getDiagnosisTipsHTML(),
      renderPersonalizedDiagnosis().
    - API token mapping and routine filters: normalizeApiSkinToken(),
      mapQuizSkinTypeToApi(), collectApiSkinTypes(),
      mapNormalizedConcernToApiKey(), mapQuizPriorityToApiConcern(),
      collectApiConcerns(), collectRoutineFilterSkinTypes(),
      collectRoutineFilterConcerns(), getRoutineFilterContext().
    - Shopify variant/cart identity: extractShopifyVariantIdFromRoutineProduct(),
      getExplicitRoutineVariantId(), getRoutineProductHandle(),
      getRoutineFallbackSkuId(), setRoutineProductCartVariantId(),
      resolveRoutineCartVariantId(), preloadRoutineCartContext().
    - Money, period, and section formatting: formatRoutinePriceDisplay(),
      getRoutinePriceAmount(), formatRoutineMoney(),
      getRoutineCurrencySymbol(), formatRoutineAmount(),
      normalizeRoutineTimePeriod(), getRoutineStepTimePeriod(),
      getRoutineSectionLabel(), getRoutineSectionOrder().
    - PIM parsing and lookup: getPimAttribute(), getPimAttributeList(),
      getPimAttributeFirst(), normalizePimList(), normalizePimSkinType(),
      normalizePimConcern(), getPimProductSkinTypes(),
      getPimProductConcerns(), getPimProductCategory(),
      getPimProductCurrency(), getPimProductUrl(), getPimProductImage(),
      getPimProductTimePeriod(), normalizePimProductTitle(),
      createPimProductLookup(), recordMissingPimProduct(),
      getPimProductTitleCandidates(), findPimResourceByTitle(),
      findFirstPimResourceByTitles(), findFirstEligiblePimResourceByTitles().
    - Asaya eligibility and sheet rules: getAsayaProfileSkinTypesForGate(),
      hasAsayaProfileSkinType(), isPimResourceExcludedForAsayaProfile(),
      getAsayaSheetRuleByConcern(), getAsayaSheetConcernItems(),
      getAsayaSheetRuleSelection(), getAsayaRuleContext(),
      getAsayaRecommendedSetTitles(), getAsayaConcernScore(),
      isAsayaActiveConcern(), hasSingleSevereAsayaConcern().
    - Asaya routine building: hasAnyRoutineOverlap(),
      getPimProductMatchScore(), filterPimResourcesForRoutine(),
      getRoutineCategoryOrder(), getSheetRuleWhyCopy(),
      createRoutineProductFromPimResource(), getAsayaPlanSection(),
      getAsayaSectionStepByCategory(), addAsayaRuleProduct(),
      setAsayaRecommendedSet(), getAsayaRuleProductTitlesByCategory(),
      getAsayaScaffoldTitlesForCategory(), getAsayaTreatmentTitles(),
      getAsayaRoutineRules(), finalizeAsayaRoutinePlan(),
      mapPimResourcesToRoutinePlan().
    - Product reason copy and legacy routine mapping:
      normalizeRoutineWhySentence(), getApiRoutineReason(),
      stringifyRoutineSignal(), getRoutineTextSignal(),
      getRoutineConcernFromSignal(), getDiagnosisConcernItem(),
      getPrimaryDiagnosisConcernItem(), formatConcernScanWhy(),
      getQuizPriorityWhy(), getHormonalPigmentationWhy(),
      getRoutineWhyFromScanOrQuiz(), getRoutineWhyCopy(),
      mapRoutineApiStepsToRoutine().
    - Body-care and recommended-set add-ons: getSelectedBodyExtraConfig(),
      getBodyExtraWhyCopy(), createBodyExtraRoutineProduct(),
      getRecommendedSetWhyCopy(), getDummyRecommendedSet(),
      appendBodyExtraToRoutinePlan().
    - Routine data and HTML rendering: fetchRoutineRecommendation(),
      getRoutineTotal(), getRoutineIcon(), getRoutineSummaryHTML(),
      getRoutineProductThumbHTML(), getRoutineProductCardHTML(),
      getRoutineProductCards(), getRecommendedSetHTML(),
      getBuildYourOwnDividerHTML(), getRoutineStepGroupsHTML(),
      getRoutineSectionsHTML(), getRoutineEmptyHTML(),
      getRoutineShareButton(), renderRoutinePage(),
      renderRoutinePageWithPlan(), wireRoutineControls().
    - Routine cart actions: getRoutineProductButton(),
      getRoutineProductIndex(), setRoutineMainButtonState(),
      setRoutineProductButtonState(), addRoutineProductToCart(),
      addRoutineProductsQuietly(), addRoutineProductsToCart().
    - Events, Boltic, and Kwikpass phone parsing: dispatchCustomEvent(),
      resetBolticSkinSyncFlag(), jsonSafeCloneForBoltic(),
      buildBolticSkinAnalysisMeta(), normalizeCountryCode(),
      getStorageValueByKey(), readStorageCandidateValues(),
      parseJsonString(), decodeJwtPayload(), isCountryCodeStorageKey(),
      extractCountryCodeFromStorageCandidate(), extractCountryCodeValue(),
      extractPhoneValue(), normalizeKwikpassPhoneNumber(),
      combineCountryCodeWithPhone(), getKwikpassCountryCodeFromStorage(),
      getKwikpassStructuredPhoneNumber(), getKwikpassPhoneNumber(),
      createBolticLoginRecordWithPhone(), syncBolticLoginRecordAfterLogin().
    - Login gate: hasLoggedInCustomerSignal(), getKwikpassTokenValue(),
      hasKwikpassLoginSignal(), isKwikpassFullyLoggedIn(),
      setSkinAnalysisLoginGateScrollLock(), resetSkinAnalysisLoginPopupState(),
      stopSkinAnalysisBodyClassObserver(), startSkinAnalysisBodyClassObserver(),
      ensureSkinAnalysisNetworkUnlockHooks(), stopSkinAnalysisLoginUnlockWatch(),
      isVisibleElement(), isKwikpassLoginTriggerNode(),
      isKwikpassPopupCandidate(), isKwikpassLoginPopupVisible(),
      updateSkinAnalysisLoginPopupState(), tryUnlockSkinAnalysisIfLoggedIn(),
      startSkinAnalysisLoginUnlockWatch(), clickVisibleKwikpassLoginTrigger(),
      openKwikpassLoginForSkinAnalysis(),
      openKwikpassLoginForSkinAnalysisSoon(),
      clearSkinAnalysisLoginGateState(), abandonSkinAnalysisLoginGateToHome(),
      unlockSkinAnalysisAfterLogin(), ensureKwikpassLoginGateListener().
    - Result rendering: showResultAgain(), showResultStage(),
      renderSkinAnalysisResult(), renderResultAnnotations(),
      wireResultControls(), summarizeRecommendationPayload(),
      renderRecommendationPanel().
    - Camera and SDK lifecycle: setCameraMessage(), clearStartupTimeout(),
      clearAnalysisStartRetry(), resetAnalysisFlow(), getViewportHeight(),
      getHeaderOffsetSelectors(), getHeaderOffsetNodes(),
      getVisibleHeaderOffset(), getCameraTopOffset(),
      updateCameraViewportHeight(), refreshCameraViewportHeightIfActive(),
      clearCameraViewportHeightRefreshes(), scheduleCameraViewportHeightRefreshes(),
      stopCameraHeaderResizeObserver(), startCameraHeaderResizeObserver(),
      setCameraMode(), bindViewportResize(), setCgLoading(),
      removeAllDebugOverlays(), isSDKModuleLoaded(), patchSDKFrameLayout(),
      applyCameraPermission(), ensureCameraPermission(), monitorContainer(),
      loadSDKScript(), startSkinAnalysis(), requestSkinAnalysisStart(),
      markSDKReady(), handleSDKLifecycleEvent(), bindPostMessageFallback(),
      bindSDKEvents(), handleSDKUIInteraction(), closeSDKCanvasForStageExit(),
      closeSDKAfterReportReady(), handleSDKBackButton(),
      handleSDKCloseButton(), initSDK().
    - Shopify cart/theme bridge: parseCartEventData(), normalizeVariantRef(),
      fetchCartData(), getCartDataForQuietUpdate(), getCartVariantQuantity(),
      updateCartQuantities(), addCartVariantQuietly(), addToCart(), openCart(),
      refreshCartDrawer(), updateCartBadge(), fireCartEvents().
    - Stage entrypoints: showError(), showLandingStage(), showIntroStage(),
      activateCameraStage(), handleBack(), bindIntroActions(), boot().

    Maintenance notes:
    - This is a self-contained browser script. It should keep working when pasted
      into Shopify without a bundler.
    - Keep helpers as function declarations so call order stays flexible.
    - Treat API/SDK payloads as variable-shaped. Normalizers are deliberately
      defensive because GlamAR, PIM, Shopify, and Kwikpass payloads differ.
    - After comment-only edits, node --check is enough to verify syntax.
      Behavior changes should also be smoke-tested in Shopify with camera,
      login, PIM fetch, and cart add flows.
  */

  var ACCESS_KEY = "c47ab41f-f3e3-482d-a682-c95e0a187933";
  var APP_ID = "81bed486-e2b1-4518-afa0-1311401fadac";
  var ORG_ID = "16372935";

  var MISC_PUBLIC_API_BASE = "https://api.pixelbin.io/service/public/misc/v1.0";
  var ROUTINE_RECOMMENDATION_URL =
    MISC_PUBLIC_API_BASE +
    "/pim?orgId=" +
    encodeURIComponent(ORG_ID) +
    "&glamar_app_id=" +
    encodeURIComponent(APP_ID) +
    "&page=1&limit=50";
  var BOLTIC_PROXY_TABLE_ID = "e25e9e02-740d-4d31-8056-9f463781adfc";
  var BOLTIC_RECORDS_PROXY_URL =
    MISC_PUBLIC_API_BASE +
    "/boltic-database/tables/" +
    BOLTIC_PROXY_TABLE_ID +
    "/records";
  var VALID_API_SKIN_TYPES = ["oily", "dry", "combination", "normal"];
  /*
    Client-facing category switchboard.

    To disable any concern everywhere, set enabled: false.
    To enable a disabled concern, set enabled: true.

    This config drives:
    - SDK/PIM concern normalization
    - diagnosis labels and trigger copy
    - sheet/routine rule eligibility
    - routine product concern matching

    When adding a new concern, add:
    - an entry here with aliases used by SDK/PIM
    - optional default regions in getDefaultRegions()
    - optional sheet rules in ASAYA_SHEET_RECOMMENDATION_RULES
    - optional product signal matching in getRoutineConcernFromSignal()
  */
  var ASAYA_CONCERN_CATEGORY_CONFIG = {
    acne: {
      enabled: true,
      label: "Acne",
      triggers: ["Post-inflammatory inflammation"],
      aliases: ["pimples", "breakout", "breakouts"]
    },
    pores: {
      enabled: true,
      label: "Pores",
      triggers: ["Excess sebum + congestion"],
      aliases: ["texture", "enlarged_pores", "oiliness"]
    },
    whiteheads: {
      enabled: true,
      label: "Whiteheads",
      triggers: ["Clogged pores"],
      aliases: []
    },
    eye_bags: {
      enabled: true,
      label: "Eye Bags",
      triggers: ["Under-eye fluid retention"],
      aliases: ["bags", "baggy_eyes"]
    },
    dark_circles: {
      enabled: false,
      label: "Dark Circles",
      triggers: ["Under-eye discoloration"],
      aliases: [
        "dark_circle",
        "dark_circles",
        "under_eye_darkness",
        "under_eye_pigmentation",
        "dark_under_eyes",
        "periorbital_darkness"
      ]
    },
    post_acne_scars: {
      enabled: true,
      label: "Post Acne Scars",
      triggers: ["Past breakout marks"],
      aliases: ["scars", "scar", "acne_scars"]
    },
    wrinkles: {
      enabled: true,
      label: "Wrinkles",
      triggers: ["UV exposure + lifestyle"],
      aliases: ["fine_lines", "ageing", "aging", "anti_ageing"]
    },
    redness: {
      enabled: true,
      label: "Redness",
      triggers: ["Sensitivity + inflammation"],
      aliases: ["inflammation", "rosacea"]
    },
    hydration: {
      enabled: true,
      label: "Hydration",
      triggers: ["Barrier stress"],
      aliases: ["dullness", "dry_skin"]
    },
    pigmentation: {
      enabled: true,
      label: "Pigmentation",
      triggers: ["UV exposure + melanin response"],
      aliases: [
        "melasma",
        "hyperpigmentation",
        "pih",
        "spots",
        "spot",
        "postinflammatory_hyperpigmentation"
      ]
    }
  };
  var VALID_API_CONCERN_SET = getEnabledConcernCategorySet();
  var ROUTINE_CURRENCY_SYMBOLS = {
    aed: "د.إ",
    aud: "AU$",
    brl: "R$",
    cad: "CA$",
    chf: "₣",
    cny: "CN¥",
    dkk: "kr",
    eur: "€",
    gbp: "£",
    hkd: "HK$",
    ils: "₪",
    inr: "₹",
    iqd: "ع.د",
    jod: "د.أ",
    jpy: "¥",
    krw: "₩",
    mnt: "₮",
    mxn: "MX$",
    myr: "RM",
    ngn: "₦",
    nok: "kr",
    nzd: "NZ$",
    pln: "zł",
    rub: "₽",
    sar: "﷼",
    sek: "kr",
    sgd: "S$",
    thb: "฿",
    try: "₺",
    usd: "$",
    zar: "R",
    egp: "E£",
    idr: "Rp",
    le: "E£"
  };
  var DIAGNOSIS_PROFILE_CONFIG = {
    concerns: getEnabledConcernDiagnosisConfig(),
    skinTypes: {
      oily: {
        label: "Oily",
        triggers: ["Excess sebum"]
      },
      dry: {
        label: "Dry",
        triggers: ["Barrier dryness"]
      },
      combination: {
        label: "Combination",
        triggers: ["T-zone oil imbalance"]
      },
      normal: {
        label: "Normal",
        triggers: ["Routine maintenance"]
      }
    },
    quizBreakouts: {
      Frequently: "Active inflammation - higher chance of post-acne marks",
      Occasionally: "Periodic congestion - occasional flare-up risk",
      Rarely: "Low congestion - minimal breakout activity",
      Never: "Clear pattern - no active breakout tendency"
    },
    defaults: {
      triggers: ["Barrier balance"],
      breakout: "Based on your answer"
    }
  };
  var SDK_SRC = "https://cdn.glamar.io/sdk/wrapper";
  var CONTAINER = "cg-sdk-container";
  var DEFAULT_CAMERA_MESSAGE =
    "Allow camera access when prompted, then align your face inside the frame.";
  var sdkInited = false;
  var sdkReady = false;
  var startRequested = false;
  var analysisStarted = false;
  var startupTimeoutId = null;
  var analysisStartRetryId = null;
  var analysisStartAttempts = 0;
  var sdkInitPromise = null;
  var sdkEventsBound = false;
  var sdkStageExitInProgress = false;
  var sdkClosedAfterResult = false;
  var viewportResizeBound = false;
  var cameraLayoutRefreshIds = [];
  var cameraHeaderResizeObserver = null;
  var CAMERA_HEADER_CLEARANCE = 0;
  var CAMERA_BOTTOM_CLEARANCE = 0;
  var SKIN_LOGIN_POPUP_POLL_INTERVAL_MS = 200;
  var SKIN_LOGIN_POPUP_MISSING_TICKS_TO_ABANDON = 2;
  var latestSkinResult = null;
  var latestSkinScanMetadata = null;
  var latestRecommendationPayload = null;
  var routineVariantIdCache = {};
  var routineVariantLookupPromises = {};
  var routineCartDataCache = null;
  var reportConcerns = [];
  var bolticLoginRecordSynced = false;
  var bolticLoginRecordSyncInFlight = false;
  var pendingSkinAnalysisPayloadForLogin = null;
  var skinAnalysisLoginGateActive = false;
  var kwikpassLoginListenerBound = false;
  var skinAnalysisLoginPollId = null;
  var skinAnalysisLoginBodyObserver = null;
  var skinAnalysisLoginPopupObserved = false;
  var skinAnalysisLoginPopupMissingTicks = 0;
  var skinAnalysisLoginOpenTimerId = null;
  var selectedConcernIdx = 0;
  var quizIndex = 0;
  var quizAnswers = {};
  var quizUserName = "";
  var quizQuestions = [
    {
      id: "name",
      type: "text",
      section: "About You",
      question: "What should we call you?",
      reason: "We'll use your name to personalize your skincare routine.",
      placeholder: "Enter your first name"
    },
    {
      id: "gender",
      type: "single",
      section: "About You",
      question: "Would you like to share your gender?",
      reason: "Sub-Hormones can influence some skin concerns",
      options: ["Female", "Male", "Prefer not to say"]
    },
    {
      id: "hormonalFactors",
      type: "single",
      section: "Hormonal Factors",
      question:
        "Are you currently pregnant or recently postpartum (last 12 months)?",
      reason: "Pregnancy hormones commonly trigger pigmentation.",
      options: ["Yes", "No"]
    },
    {
      id: "skinType",
      type: "single",
      section: "About Your Skin",
      question: "Let’s start with your skin type?",
      reason: "What best describes your skin?",
      options: ["Oily", "Dry", "Combination", "Normal", "Not sure"]
    },
    {
      id: "priority",
      type: "single",
      section: "About Your Skin",
      question: "What would you like to focus on first?",
      reason: "This will help us get your routine right",
      options: [
        "Pigmentation",
        "Acne & breakouts",
        "Hydration & glow",
        "Anti-ageing",
        "Firmness & fine lines"
      ]
    },
    {
      id: "acne",
      type: "single",
      section: "Skin Behavior",
      question: "How often does your skin break out?",
      reason: "Breakouts can lead to post-inflammatory pigmentation (PIH).",
      options: ["Often", "Occasionally", "Rarely", "Never"]
    },
    {
      id: "bodyExtra",
      type: "single",
      section: "Beyond The Face",
      question: "Where else would you like specialist care?",
      reason: "Beyond your face, is there a body area you'd like to treat for pigmentation?",
      options: [
        "Underarms",
        "Knees, neck & elbows",
        "Bikini line & inner thighs",
        "Just my face, for now"
      ]
    }
  ];
  var BODY_EXTRA_PRODUCT_OPTIONS = [
    {
      key: "underarms",
      productTitle: "Even Tone Underarm Mist",
      answers: ["Dark underarms", "Just my face for now"],
      name: "Even Tone Underarm Mist",
      why: "Bestseller-led body-care add-on for visibly even-toned underarms.",
      priceValue: 499,
      url: "https://worldofasaya.com/products/underarm-spray"
    },
    {
      key: "kne",
      productTitle: "Tone Restore Knee-Neck-Elbow Cream",
      answers: ["Dark knees, neck & elbows"],
      name: "Tone Restore Knee-Neck-Elbow Cream",
      why: "Targets stubborn pigmentation on knees, neck and elbows.",
      priceValue: 499,
      url: "https://worldofasaya.com/products/kne-cream"
    },
    {
      key: "bikini-inner-thighs",
      productTitle: "Fade Forward Inner Thigh & Bum Roll-On",
      answers: ["Bikini line & inner thighs"],
      name: "Fade Forward Inner Thigh & Bum Roll-On",
      why: "Targeted body-care pick for friction-prone pigmentation zones.",
      priceValue: 699,
      url: "https://worldofasaya.com/products/fade-forward-inner-thigh-and-bum-aha-roll-on"
    }
  ];
  var ASAYA_DISCONTINUED_PRODUCT_TITLES = [
    "Overnight Revive Crème",
    "Aqua Dew Sunscreen",
    "Radiance Serum",
    "Festive Glow Set",
    "Travel Kit"
  ];
  /*
    Client sheet contract for Asaya product selection. The thresholded concern
    rows decide which recommendation/routine products are shown; aliases only
    bridge sheet shorthand to exact PIM product titles and do not substitute
    unavailable products. When a sheet title is discontinued, keep that title
    in ASAYA_DISCONTINUED_PRODUCT_TITLES and add safe replacements here so
    findPimResourceByTitle() can try replacements before declaring it missing.
  */
  var ASAYA_PIM_PRODUCT_TITLE_ALIASES = {
    "Spot Light SPF": [
      "Spot Light Sunscreen SPF 50 PA++++ for Dark Spots & Hyperpigmentation"
    ],
    "Even tone facewash": ["Even Tone Face Cleanser"],
    "New light Gel": ["New Light Gel Moisturiser"],
    "Salicylic facewash": ["Salicylic Face Cleanser"],
    "Salicylic Cleanser": ["Salicylic Face Cleanser"],
    "2% Salicylic Acid & 5% Niacinamide Serum": ["Salicylic Acid Serum"],
    "5% Niacinamide Serum": [
      "Salicylic Acid Serum",
      "10% Niacinamide & 1% Alpha Arbutin Serum"
    ],
    "5% Multi Peptide & HA Serum": [
      "5% Multi Peptide Complex & Hyaluronic Acid Serum"
    ],
    "Fresh Start Scrub": ["Salicylic Acid Serum"],
    "Intense Moisture Gel": ["New Light Gel Moisturiser"],
    "Moisture Cocoon Creme - Hydrating & Glow Moisturizer with Niacinamide": [
      "Moisture Cocoon Creme"
    ],
    "Glow and Lift Moist.": ["Glow & Lift Crème"],
    "Aqua Dew Sunscreen": ["Sun Drip Mineral Sunscreen", "Hint of Tint"],
    "Overnight Revive Crème": ["Moisture Cocoon Creme"],
    "Overnight Revive Creme": ["Moisture Cocoon Creme"],
    "Radiance Serum": ["10% Vitamin C Serum", "15% Vitamin C Serum"]
  };
  var ASAYA_SHEET_RECOMMENDATION_RULES = [
    {
      concern: "pigmentation",
      threshold: 70,
      recommendationTitles: ["Advanced Spot Targeting Serum"],
      routineProducts: [
        {
          category: "Targeted Treatment",
          title: "Advanced Spot Targeting Serum"
        },
        { category: "Sunscreen", title: "Spot Light SPF" },
        { category: "Cleanser", title: "Even tone facewash" },
        { category: "Moisturiser", title: "New light Gel" }
      ]
    },
    {
      concern: "post_acne_scars",
      threshold: 70,
      recommendationTitles: ["10% Niacinamide & 1% Alpha Arbutin Serum"],
      routineProducts: [
        {
          category: "Targeted Treatment",
          title: "10% Niacinamide & 1% Alpha Arbutin Serum"
        },
        {
          category: "Targeted Treatment",
          title: "Advanced Spot Targeting Gel"
        },
        { category: "Cleanser", title: "Salicylic facewash" }
      ]
    },
    {
      concern: "acne",
      threshold: 70,
      recommendationTitles: ["2% Salicylic Acid & 5% Niacinamide Serum"],
      routineProducts: [
        { category: "Cleanser", title: "Salicylic Cleanser" },
        {
          category: "Targeted Treatment",
          title: "2% Salicylic Acid & 5% Niacinamide Serum"
        },
        { category: "Moisturiser", title: "Oil-Free Barrier+ Crème" }
      ]
    },
    {
      concern: "whiteheads",
      threshold: 70,
      recommendationTitles: ["5% Niacinamide Serum"],
      routineProducts: [
        {
          category: "Targeted Treatment",
          title: "2% Salicylic Acid & 5% Niacinamide Serum"
        },
        { category: "Cleanser", title: "Salicylic Cleanser" },
        { category: "Exfoliator", title: "Fresh Start Scrub" }
      ]
    },
    {
      concern: "pores",
      threshold: 75,
      recommendationTitles: ["2% Salicylic Acid & 5% Niacinamide Serum"],
      routineProducts: [
        {
          category: "Targeted Treatment",
          title: "2% Salicylic Acid & 5% Niacinamide Serum"
        },
        { category: "Cleanser", title: "Salicylic Cleanser" }
      ]
    },
    {
      concern: "hydration",
      threshold: 60,
      recommendationTitles: [
        "Moisture Cocoon Creme - Hydrating & Glow Moisturizer with Niacinamide",
        "5% Multi Peptide & HA Serum"
      ],
      routineProducts: [
        {
          category: "Moisturiser",
          title:
            "Moisture Cocoon Creme - Hydrating & Glow Moisturizer with Niacinamide"
        },
        {
          category: "Targeted Treatment",
          title: "5% Multi Peptide & HA Serum"
        }
      ]
    },
    {
      concern: "wrinkles",
      threshold: 70,
      recommendationTitles: ["5% Multi Peptide & HA Serum"],
      routineProducts: [
        {
          category: "Targeted Treatment",
          title: "5% Multi Peptide & HA Serum"
        },
        { category: "Moisturiser", title: "Glow and Lift Moist." }
      ]
    },
    {
      concern: "eye_bags",
      threshold: 70,
      recommendationTitles: ["5% Multi Peptide & HA Serum"],
      routineProducts: [
        {
          category: "Targeted Treatment",
          title: "5% Multi Peptide & HA Serum"
        }
      ]
    },
    {
      concern: "dark_circles",
      threshold: 70,
      recommendationTitles: ["5% Multi Peptide & HA Serum"],
      routineProducts: [
        {
          category: "Targeted Treatment",
          title: "5% Multi Peptide & HA Serum"
        }
      ]
    }
  ];

  /*
    DOM, escaping, score, and payload helpers.

    These are intentionally small function declarations because almost every
    renderer and event handler below depends on them. Keep them side-effect
    free unless the function name says otherwise.
  */
  function $(id) {
    return document.getElementById(id);
  }
  function getWrapperNode() {
    return $("cg-inline-wrapper");
  }
  function getAppNode() {
    return $("cg-asaya-app");
  }
  function getStageNode(className) {
    var app = getAppNode();
    return app ? app.querySelector("." + className) : null;
  }
  function resetStageScroll(className) {
    var stage = getStageNode(className);
    if (stage) stage.scrollTop = 0;
  }
  function scrollWrapperIntoView() {
    var wrapper = getWrapperNode();
    if (!wrapper || wrapper.classList.contains("cg-camera-mode")) return;
    requestAnimationFrame(function () {
      var pageTop =
        window.pageYOffset || document.documentElement.scrollTop || 0;
      var top = 0;
      if (wrapper.getBoundingClientRect) {
        top = Math.max(
          0,
          Math.round(wrapper.getBoundingClientRect().top + pageTop)
        );
      }
      try {
        window.scrollTo({ top: top, left: 0, behavior: "auto" });
      } catch (error) {
        window.scrollTo(0, top);
      }
    });
  }
  /*
    GlamAR event payloads are not perfectly consistent across SDK versions.
    getEventPayload(), getSkinAnalysisOption(), getSkinAnalysisValue(), and
    getSkinOutput() unwrap the common shapes before the normalized result is
    rendered or recommendation metadata is stored.
  */
  function getEventPayload(eventData) {
    if (!eventData || typeof eventData !== "object") return {};
    if (
      Object.prototype.hasOwnProperty.call(eventData, "option") ||
      Object.prototype.hasOwnProperty.call(eventData, "options") ||
      Object.prototype.hasOwnProperty.call(eventData, "value") ||
      Object.prototype.hasOwnProperty.call(eventData, "result")
    ) {
      return eventData;
    }
    return (
      eventData.payload ||
      eventData.value ||
      eventData.detail ||
      eventData.data ||
      eventData
    );
  }
  function escapeHTML(value) {
    var map = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;"
    };
    return String(value == null ? "" : value).replace(
      /[&<>"']/g,
      function (ch) {
        return map[ch];
      }
    );
  }
  function escapeAttr(value) {
    return escapeHTML(value);
  }
  function getPoweredFooterHTML(extraClass) {
    return (
      '<div class="cg-powered-footer ' +
      (extraClass || "") +
      '">Powered by <strong>GlamAR</strong> AI Skin Technology</div>'
    );
  }
  function clampScore(value) {
    var num = Number(value);
    if (!Number.isFinite(num)) num = 0;
    if (num < 0) return 0;
    if (num > 100) return 100;
    return Math.round(num);
  }
  function normalizeConcernKey(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  }
  function isConcernCategoryEnabled(apiKey) {
    var key = normalizeConcernKey(apiKey);
    var config = ASAYA_CONCERN_CATEGORY_CONFIG[key];
    return !!(config && config.enabled !== false);
  }
  function getEnabledConcernCategorySet() {
    var out = {};
    Object.keys(ASAYA_CONCERN_CATEGORY_CONFIG).forEach(function (apiKey) {
      if (isConcernCategoryEnabled(apiKey)) out[apiKey] = true;
    });
    return out;
  }
  function getEnabledConcernDiagnosisConfig() {
    var out = {};
    Object.keys(ASAYA_CONCERN_CATEGORY_CONFIG).forEach(function (apiKey) {
      var config = ASAYA_CONCERN_CATEGORY_CONFIG[apiKey] || {};
      if (!isConcernCategoryEnabled(apiKey)) return;
      out[apiKey] = {
        label: config.label || toTitle(apiKey),
        triggers: Array.isArray(config.triggers) ? config.triggers.slice() : []
      };
    });
    return out;
  }
  function getConcernCategoryAliasMap() {
    var aliases = {};
    Object.keys(ASAYA_CONCERN_CATEGORY_CONFIG).forEach(function (apiKey) {
      var config = ASAYA_CONCERN_CATEGORY_CONFIG[apiKey] || {};
      var key = normalizeConcernKey(apiKey);
      aliases[key] = apiKey;
      var list = Array.isArray(config.aliases) ? config.aliases : [];
      for (var i = 0; i < list.length; i++) {
        aliases[normalizeConcernKey(list[i])] = apiKey;
      }
    });
    return aliases;
  }
  function toTitle(value) {
    var raw = String(value || "")
      .replace(/_/g, " ")
      .trim();
    if (!raw) return "Skin Concern";
    return raw.replace(/\w\S*/g, function (word) {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    });
  }
  function getSeverityClass(severity, score) {
    var safeScore = clampScore(score);
    if (safeScore >= 90) return "good";
    if (safeScore < 60) return "att";
    return "avg";
  }
  function getSeverityLabel(severity, score) {
    var cls = getSeverityClass(severity, score);
    if (cls === "att") return "Needs Attention";
    if (cls === "good") return "Good";
    return "Average";
  }
  function getHealthLabel(score, health) {
    var label = String(health || "").trim();
    if (label) {
      if (label.toLowerCase() === "good") return "Great Skin";
      if (label.toLowerCase() === "average") return "Looking Good";
      return label;
    }
    var safeScore = clampScore(score);
    if (safeScore >= 80) return "Great Skin";
    if (safeScore >= 60) return "Looking Good";
    return "Needs Care";
  }
  function safeCssSize(value) {
    var raw = String(value || "").trim();
    if (/^-?\d+(\.\d+)?(%|px|rem|em|vw|vh)$/.test(raw)) return raw;
    return "";
  }
  function getDefaultRegions(key) {
    var map = {
      pigmentation: [
        { top: "18%", left: "12%", w: "24%", h: "14%", shape: "blob" },
        { top: "18%", right: "12%", w: "24%", h: "14%", shape: "blob" },
        { top: "10%", left: "35%", w: "30%", h: "10%", shape: "blob" }
      ],
      melasma: [
        { top: "38%", left: "8%", w: "26%", h: "18%", shape: "blob" },
        { top: "38%", right: "8%", w: "26%", h: "18%", shape: "blob" }
      ],
      eye_bags: [
        { top: "34%", left: "16%", w: "22%", h: "7%", shape: "oval" },
        { top: "34%", right: "16%", w: "22%", h: "7%", shape: "oval" }
      ],
      dark_circles: [
        { top: "35%", left: "15%", w: "24%", h: "7%", shape: "oval" },
        { top: "35%", right: "15%", w: "24%", h: "7%", shape: "oval" }
      ],
      acne: [
        { top: "14%", left: "40%", w: "20%", h: "8%", shape: "oval" },
        { top: "48%", left: "42%", w: "16%", h: "10%", shape: "oval" }
      ],
      pores: [{ top: "42%", left: "38%", w: "24%", h: "14%", shape: "oval" }],
      scars: [
        { top: "44%", left: "20%", w: "10%", h: "6%", shape: "oval" },
        { top: "50%", right: "22%", w: "8%", h: "5%", shape: "oval" },
        { top: "60%", left: "40%", w: "10%", h: "5%", shape: "oval" }
      ],
      whiteheads: [
        { top: "44%", left: "44%", w: "12%", h: "7%", shape: "oval" },
        { top: "68%", left: "42%", w: "16%", h: "8%", shape: "oval" }
      ],
      wrinkles: [
        { top: "28%", left: "12%", w: "16%", h: "4%", shape: "oval" },
        { top: "28%", right: "12%", w: "16%", h: "4%", shape: "oval" },
        { top: "8%", left: "30%", w: "40%", h: "5%", shape: "oval" }
      ],
      hydration: [
        { top: "20%", left: "15%", w: "70%", h: "50%", shape: "oval" }
      ]
    };
    return (
      map[key] || [
        { top: "18%", left: "18%", w: "64%", h: "46%", shape: "oval" }
      ]
    );
  }
  function getSkinAnalysisOption(eventData) {
    var payload = getEventPayload(eventData);
    return String(
      payload.option ||
        payload.options ||
        payload.event ||
        payload.type ||
        payload.name ||
        ""
    ).toLowerCase();
  }
  function getSkinAnalysisValue(eventData) {
    var payload = getEventPayload(eventData);
    if (payload && Object.prototype.hasOwnProperty.call(payload, "value"))
      return payload.value;
    if (payload && Object.prototype.hasOwnProperty.call(payload, "result"))
      return payload.result;
    if (
      payload &&
      payload.data &&
      Object.prototype.hasOwnProperty.call(payload.data, "value")
    )
      return payload.data.value;
    return payload;
  }
  function getSkinOutput(value) {
    if (!value || typeof value !== "object") return null;
    if (value.outputs && value.outputs[0]) return value.outputs[0];
    if (value.output) return value.output;
    if (value.skinData) return value;
    if (value.data) return getSkinOutput(value.data);
    if (value.value) return getSkinOutput(value.value);
    return null;
  }
  function normalizeSkinResult(value) {
    var output = getSkinOutput(value);
    var skin = output && output.skinData;
    if (!skin && output && output.total_skin_score != null) skin = output;
    if (!skin) return null;
    var rawConcerns = Array.isArray(skin.concerns) ? skin.concerns : [];
    var concerns = rawConcerns
      .map(function (c) {
        c = c || {};
        var key = mapNormalizedConcernToApiKey(
          c.tech_name || c.key || c.name || c.label
        );
        if (!key) return null;
        var score = clampScore(c.value != null ? c.value : c.score);
        var severity = getSeverityLabel(c.severity, score);
        return {
          key: key,
          name: toTitle(c.name || c.label || key),
          value: score,
          severity: severity,
          severityClass: getSeverityClass(severity, score),
          description:
            c.description ||
            c.analyses ||
            "This skin dimension was included in your AI analysis.",
          image: c.image || c.image_url || c.masked_url || "",
          regions:
            Array.isArray(c.regions) && c.regions.length
              ? c.regions
              : getDefaultRegions(key),
          raw: c
        };
      })
      .filter(function (concern) {
        return !!concern;
      });
    concerns.sort(function (a, b) {
      return a.value - b.value;
    });
    return {
      score: clampScore(skin.total_skin_score || skin.totalScore),
      skinAge: skin.skin_age || skin.skinAge || "--",
      eyeAge: skin.eye_age || skin.eyeAge || "",
      skinType: skin.skin_type || skin.skinType || "--",
      skinTone: skin.skin_tone || skin.skinTone || "",
      skinShade: skin.skin_shade || skin.skinShade || "",
      health: skin.skin_health || skin.health || "",
      summary:
        skin.summary ||
        "Your scan is complete. Review the key skin dimensions below.",
      faceImage:
        skin.inputImage ||
        skin.input_image ||
        skin.facial_hair_url ||
        skin.combine_masked_url ||
        "",
      combinedMask: skin.combine_masked_url || "",
      zones: skin.zones || {},
      concerns: concerns,
      raw: skin,
      source: value
    };
  }
  function getConcernImage(concern, result) {
    if (concern && concern.image) return concern.image;
    if (result && (result.combinedMask || result.faceImage))
      return result.combinedMask || result.faceImage;
    return "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=900&h=900&fit=crop&crop=face&auto=format&q=80";
  }
  function getConcernDetailHTML(concern) {
    if (!concern) return "";
    var raw = concern.raw || {};
    var description =
      concern.description ||
      raw.description ||
      "This skin dimension was included in your AI analysis.";
    var analyses = raw.analyses || raw.analysis || "";
    var html = "<p>" + escapeHTML(description) + "</p>";
    if (
      analyses &&
      String(analyses).trim() &&
      String(analyses).trim() !== String(description).trim()
    ) {
      html += '<p class="cg-result-desc-note">' + escapeHTML(analyses) + "</p>";
    }
    return html;
  }
  function getQuizAnswer(id) {
    return quizAnswers && Object.prototype.hasOwnProperty.call(quizAnswers, id)
      ? quizAnswers[id]
      : "";
  }
  function shouldShowQuizQuestion(question) {
    if (!question) return false;
    if (question.id === "hormonalFactors") {
      return getQuizAnswer("gender") === "Female";
    }
    return true;
  }
  function getVisibleQuizQuestions() {
    var visible = [];
    for (var i = 0; i < quizQuestions.length; i++) {
      if (shouldShowQuizQuestion(quizQuestions[i])) {
        visible.push(quizQuestions[i]);
      }
    }
    return visible;
  }
  function getVisibleQuizPosition(question, visibleQuestions) {
    var list = visibleQuestions || getVisibleQuizQuestions();
    for (var i = 0; i < list.length; i++) {
      if (list[i] && question && list[i].id === question.id) return i;
    }
    return 0;
  }
  function getNextVisibleQuizIndex(fromIndex) {
    for (var i = fromIndex + 1; i < quizQuestions.length; i++) {
      if (shouldShowQuizQuestion(quizQuestions[i])) return i;
    }
    return -1;
  }
  function getPreviousVisibleQuizIndex(fromIndex) {
    for (var i = fromIndex - 1; i >= 0; i--) {
      if (shouldShowQuizQuestion(quizQuestions[i])) return i;
    }
    return -1;
  }
  function getLastVisibleQuizIndex() {
    for (var i = quizQuestions.length - 1; i >= 0; i--) {
      if (shouldShowQuizQuestion(quizQuestions[i])) return i;
    }
    return 0;
  }
  function ensureVisibleQuizIndex() {
    if (shouldShowQuizQuestion(quizQuestions[quizIndex])) return;
    var nextIndex = getNextVisibleQuizIndex(quizIndex);
    quizIndex = nextIndex !== -1 ? nextIndex : getLastVisibleQuizIndex();
  }
  function pushUnique(list, seen, value) {
    var key = String(value || "").trim();
    if (!key || seen[key]) return false;
    list.push(key);
    seen[key] = true;
    return true;
  }
  function getConcernConfig(apiKey) {
    return DIAGNOSIS_PROFILE_CONFIG.concerns[apiKey] || {};
  }
  function getConcernDisplayName(apiKey, fallback) {
    return fallback || getConcernConfig(apiKey).label || toTitle(apiKey);
  }
  function getSkinTypeDisplayName(apiKey, fallback) {
    var config = DIAGNOSIS_PROFILE_CONFIG.skinTypes[apiKey] || {};
    return fallback || config.label || toTitle(apiKey);
  }
  function getTopScanConcernItems(limit) {
    var source =
      reportConcerns && reportConcerns.length
        ? reportConcerns
        : latestSkinResult && latestSkinResult.concerns
          ? latestSkinResult.concerns
          : [];
    var out = [];
    var seen = {};
    for (var i = 0; i < source.length && out.length < limit; i++) {
      var c = source[i] || {};
      var rawKey =
        c.key || (c.raw && (c.raw.tech_name || c.raw.name)) || c.name || "";
      var apiKey = mapNormalizedConcernToApiKey(rawKey);
      if (!apiKey || seen[apiKey]) continue;
      seen[apiKey] = true;
      out.push({
        apiKey: apiKey,
        name: getConcernDisplayName(apiKey, c.name),
        score: c.value
      });
    }
    return out;
  }
  function getDiagnosisRuleLabels(group, keys, fieldName, limit) {
    var out = [];
    var seen = {};
    for (var i = 0; i < keys.length; i++) {
      var config = group[keys[i]] || {};
      var labels = config[fieldName] || [];
      for (var j = 0; j < labels.length; j++) {
        pushUnique(out, seen, labels[j]);
        if (limit && out.length >= limit) return out;
      }
    }
    return out;
  }
  function getQuizBreakoutDisplay() {
    var answer = getQuizAnswer("acne");
    return (
      DIAGNOSIS_PROFILE_CONFIG.quizBreakouts[answer] ||
      answer ||
      DIAGNOSIS_PROFILE_CONFIG.defaults.breakout
    );
  }
  /*
    Diagnosis profile builder.

    This is the bridge between the raw scan and the human-readable diagnosis
    screens. It merges the lowest scan concerns, selected quiz priority,
    pregnancy/postpartum signal, and breakout answer into ordered concern and
    skin-type lists used by diagnosis copy and routine filtering.
  */
  function getDiagnosisProfile() {
    var concernItems = [];
    var concernSeen = {};
    var topScanConcerns = getTopScanConcernItems(3);
    function pushConcern(apiKey, displayName, score) {
      if (!apiKey || !VALID_API_CONCERN_SET[apiKey] || concernSeen[apiKey]) {
        return;
      }
      concernSeen[apiKey] = true;
      concernItems.push({
        apiKey: apiKey,
        name: getConcernDisplayName(apiKey, displayName),
        score: score
      });
    }
    for (var i = 0; i < topScanConcerns.length; i++) {
      pushConcern(
        topScanConcerns[i].apiKey,
        topScanConcerns[i].name,
        topScanConcerns[i].score
      );
    }
    var priorityConcern = mapQuizPriorityToApiConcern();
    if (priorityConcern) {
      pushConcern(priorityConcern, getConcernDisplayName(priorityConcern, ""));
    }
    var acneAns = getQuizAnswer("acne");
    if (acneAns === "Frequently" || acneAns === "Occasionally") {
      pushConcern("acne", "Acne");
    }
    if (getQuizAnswer("hormonalFactors") === "Yes") {
      pushConcern("pigmentation", "Pigmentation");
    }
    if (!concernItems.length) {
      pushConcern("hydration", "Hydration");
    }
    var skinTypeItems = [];
    var skinTypeSeen = {};
    var quizRaw = getQuizAnswer("skinType");
    var fromQuiz = mapQuizSkinTypeToApi(quizRaw);
    if (fromQuiz && !skinTypeSeen[fromQuiz]) {
      skinTypeSeen[fromQuiz] = true;
      skinTypeItems.push({
        apiKey: fromQuiz,
        name: getSkinTypeDisplayName(fromQuiz, quizRaw),
        source: "Selected"
      });
    }
    if (latestSkinResult && latestSkinResult.skinType) {
      var scan = normalizeApiSkinToken(latestSkinResult.skinType);
      if (
        scan &&
        VALID_API_SKIN_TYPES.indexOf(scan) !== -1 &&
        !skinTypeSeen[scan]
      ) {
        skinTypeSeen[scan] = true;
        skinTypeItems.push({
          apiKey: scan,
          name: getSkinTypeDisplayName(scan, latestSkinResult.skinType),
          source: "Scan"
        });
      }
    }
    if (!skinTypeItems.length) {
      skinTypeItems.push({
        apiKey: "normal",
        name: "Normal",
        source: "Default"
      });
    }
    var concernApiKeys = concernItems.map(function (item) {
      return item.apiKey;
    });
    var skinTypeApiKeys = skinTypeItems.map(function (item) {
      return item.apiKey;
    });
    var triggers = getDiagnosisRuleLabels(
      DIAGNOSIS_PROFILE_CONFIG.concerns,
      concernApiKeys,
      "triggers",
      2
    ).concat(
      getDiagnosisRuleLabels(
        DIAGNOSIS_PROFILE_CONFIG.skinTypes,
        skinTypeApiKeys,
        "triggers",
        1
      )
    );
    var uniqueTriggers = [];
    var triggerSeen = {};
    for (var t = 0; t < triggers.length; t++) {
      pushUnique(uniqueTriggers, triggerSeen, triggers[t]);
    }
    if (!uniqueTriggers.length) {
      uniqueTriggers = DIAGNOSIS_PROFILE_CONFIG.defaults.triggers.slice();
    }
    return {
      concernItems: concernItems,
      skinTypeItems: skinTypeItems,
      concernApiKeys: concernApiKeys,
      skinTypeApiKeys: skinTypeApiKeys,
      concernDisplay: concernItems
        .map(function (item) {
          return item.name;
        })
        .join(", "),
      skinTypeDisplay: skinTypeItems
        .map(function (item) {
          return item.source + ": " + item.name;
        })
        .join(", "),
      triggerDisplay: uniqueTriggers.slice(0, 3).join(" + "),
      breakoutDisplay: getQuizBreakoutDisplay()
    };
  }
  function getPrimaryConcernName() {
    return getDiagnosisProfile().concernDisplay || "Overall skin health";
  }
  function getDiagnosisSkinType() {
    return getDiagnosisProfile().skinTypeDisplay || "Not sure";
  }
  function getRoutineSkinTypeCopy() {
    var profile = getDiagnosisProfile();
    var names = profile.skinTypeItems.map(function (item) {
      return item.name;
    });
    return names.length ? names.join(" + ") : "your";
  }
  function getDiagnosisBreakoutText() {
    return (
      getDiagnosisProfile().breakoutDisplay ||
      DIAGNOSIS_PROFILE_CONFIG.defaults.breakout
    );
  }
  function getDiagnosisTriggerText() {
    return getDiagnosisProfile().triggerDisplay || "Barrier balance";
  }
  function hasDiagnosisKey(keys, key) {
    return keys.indexOf(key) !== -1;
  }
  function getDiagnosisTipsHTML(profile) {
    var concerns = (profile && profile.concernApiKeys) || [];
    var skinTypes = (profile && profile.skinTypeApiKeys) || [];
    var tips = [];
    if (hasDiagnosisKey(concerns, "pigmentation")) {
      tips.push({
        title: "SPF is non-negotiable.",
        body: "UV exposure can deepen pigmentation. Apply SPF 50 as your last AM step, rain or shine."
      });
    } else if (hasDiagnosisKey(concerns, "wrinkles")) {
      tips.push({
        title: "Protect collagen daily.",
        body: "Consistent sunscreen and antioxidant support help slow visible photo-ageing."
      });
    } else if (
      hasDiagnosisKey(concerns, "acne") ||
      hasDiagnosisKey(concerns, "whiteheads") ||
      hasDiagnosisKey(concerns, "pores")
    ) {
      tips.push({
        title: "Keep pores clear without stripping.",
        body: "Use a gentle cleanse and avoid harsh scrubs that can trigger more congestion."
      });
    } else {
      tips.push({
        title: "Keep your barrier calm.",
        body: "Avoid harsh scrubs and aggressive actives when skin feels irritated."
      });
    }
    if (hasDiagnosisKey(skinTypes, "combination")) {
      tips.push({
        title: "Treat zones differently.",
        body: "Keep T-zone oil balanced while maintaining moisture across drier areas."
      });
    } else if (hasDiagnosisKey(skinTypes, "oily")) {
      tips.push({
        title: "Control shine gently.",
        body: "Choose lightweight hydration so oil control does not become barrier stress."
      });
    } else if (hasDiagnosisKey(skinTypes, "dry")) {
      tips.push({
        title: "Prioritize barrier repair.",
        body: "Layer humectants and barrier-supporting moisturizers to reduce tightness."
      });
    } else {
      tips.push({
        title: "Consistency compounds.",
        body: "A steady AM and PM routine helps preserve your scan strengths."
      });
    }
    tips.push({
      title: "Be patient with progress.",
      body: "Visible changes usually need 8-12 weeks of consistent routine adherence."
    });
    return tips
      .slice(0, 3)
      .map(function (tip) {
        return (
          '<div class="cg-diagnosis-tip"><strong>' +
          escapeHTML(tip.title) +
          "</strong> " +
          escapeHTML(tip.body) +
          "</div>"
        );
      })
      .join("");
  }
  function normalizeApiSkinToken(value) {
    var parts = String(value || "")
      .toLowerCase()
      .split(/[^a-z]+/)
      .filter(Boolean);
    for (var i = 0; i < parts.length; i++) {
      if (VALID_API_SKIN_TYPES.indexOf(parts[i]) !== -1) {
        return parts[i];
      }
    }
    return "";
  }
  function mapQuizSkinTypeToApi(value) {
    var v = String(value || "")
      .trim()
      .toLowerCase();
    if (v === "oily") return "oily";
    if (v === "dry") return "dry";
    if (v === "combination") return "combination";
    if (v === "normal") return "normal";
    return "";
  }
  function collectApiSkinTypes() {
    return getDiagnosisProfile().skinTypeApiKeys.slice();
  }
  function mapNormalizedConcernToApiKey(key) {
    var k = normalizeConcernKey(key);
    if (VALID_API_CONCERN_SET[k]) return k;
    var mapped = getConcernCategoryAliasMap()[k];
    return mapped && VALID_API_CONCERN_SET[mapped] ? mapped : "";
  }
  function mapQuizPriorityToApiConcern() {
    var p = getQuizAnswer("priority");
    if (p === "Fade pigmentation")
      return mapNormalizedConcernToApiKey("pigmentation");
    if (p === "Reduce acne & breakouts")
      return mapNormalizedConcernToApiKey("acne");
    if (p === "Hydration & glow")
      return mapNormalizedConcernToApiKey("hydration");
    if (p === "Anti-ageing") return mapNormalizedConcernToApiKey("wrinkles");
    return "";
  }
  function collectApiConcerns() {
    return getDiagnosisProfile().concernApiKeys.slice();
  }
  function collectRoutineFilterSkinTypes() {
    return collectApiSkinTypes();
  }
  function collectRoutineFilterConcerns() {
    var out = [];
    var seen = {};
    var topScanConcerns = getTopScanConcernItems(3);
    for (var i = 0; i < topScanConcerns.length; i++) {
      pushUnique(out, seen, topScanConcerns[i].apiKey);
    }
    var quizConcern = mapQuizPriorityToApiConcern();
    if (quizConcern) pushUnique(out, seen, quizConcern);
    if (getQuizAnswer("hormonalFactors") === "Yes") {
      var hormonalConcern = mapNormalizedConcernToApiKey("pigmentation");
      if (hormonalConcern) pushUnique(out, seen, hormonalConcern);
    }
    if (!out.length) {
      var diagnosisConcerns = collectApiConcerns();
      for (var d = 0; d < diagnosisConcerns.length; d++) {
        pushUnique(out, seen, diagnosisConcerns[d]);
      }
    }
    return out;
  }
  function getRoutineFilterContext() {
    return {
      skinTypes: collectRoutineFilterSkinTypes(),
      concerns: collectRoutineFilterConcerns()
    };
  }
  /*
    Shopify product and variant helpers.

    PIM records often contain product URLs or SKU-like strings, while Shopify
    cart endpoints require a concrete ProductVariant ID. The resolver below
    extracts explicit IDs when present, otherwise it fetches the product handle
    JSON and caches the first available variant.
  */
  function extractShopifyVariantIdFromRoutineProduct(p) {
    if (!p) return null;
    if (p.shopifyVariantId) return String(p.shopifyVariantId);
    var raw =
      p.variant_id ||
      p.variantId ||
      p.product_variant_id ||
      p.shopify_variant_id;
    if (raw != null && String(raw).trim()) return String(raw).trim();
    var url = p.url || p.product_url || "";
    var match = String(url).match(/[?&]variant=(\d+)/i);
    if (match) return match[1];
    var sku = String(p.sku || "").trim();
    if (sku) {
      var parts = sku.split("_").filter(Boolean);
      for (var i = 0; i < parts.length; i++) {
        if (/^\d{6,20}$/.test(parts[i])) return parts[i];
      }
    }
    return null;
  }
  function getExplicitRoutineVariantId(product) {
    var p =
      (product && product._apiProduct ? product._apiProduct : product) || null;
    if (!p) return "";
    if (p.shopifyVariantId) return String(p.shopifyVariantId);
    var raw =
      p.variant_id ||
      p.variantId ||
      p.product_variant_id ||
      p.shopify_variant_id;
    if (raw != null && String(raw).trim()) return String(raw).trim();
    var url = p.url || p.product_url || "";
    var match = String(url).match(/[?&]variant=(\d+)/i);
    return match ? match[1] : "";
  }
  function getRoutineProductHandle(product) {
    var rawUrl =
      (product && (product.url || product.product_url)) ||
      (product &&
        product._apiProduct &&
        (product._apiProduct.url || product._apiProduct.product_url)) ||
      "";
    if (!rawUrl) return "";
    try {
      var url = new URL(rawUrl, window.location.origin);
      var match = url.pathname.match(/\/products\/([^/?#]+)/);
      return match ? match[1] : "";
    } catch (error) {
      var raw = String(rawUrl);
      var fallback = raw.match(/\/products\/([^/?#]+)/);
      return fallback ? fallback[1] : "";
    }
  }
  function getRoutineFallbackSkuId(product) {
    return (
      (product && product.skuId) ||
      (product &&
        product._apiProduct &&
        extractShopifyVariantIdFromRoutineProduct(product._apiProduct)) ||
      extractShopifyVariantIdFromRoutineProduct(product) ||
      (product && product.sku) ||
      ""
    );
  }
  function setRoutineProductCartVariantId(product, variantId) {
    var id = String(variantId || "").trim();
    if (product && id) product._cartVariantId = id;
    return id;
  }
  // Shopify cart endpoints need variant ids; the routine SKU prefix can be a product id.
  function resolveRoutineCartVariantId(product) {
    if (product && product._cartVariantId) {
      return Promise.resolve(String(product._cartVariantId));
    }
    var directVariantId = getExplicitRoutineVariantId(product);
    if (directVariantId) {
      return Promise.resolve(
        setRoutineProductCartVariantId(product, directVariantId)
      );
    }
    var handle = getRoutineProductHandle(product);
    if (!handle) {
      return Promise.resolve(
        setRoutineProductCartVariantId(
          product,
          getRoutineFallbackSkuId(product)
        )
      );
    }
    if (routineVariantIdCache[handle]) {
      return Promise.resolve(
        setRoutineProductCartVariantId(product, routineVariantIdCache[handle])
      );
    }
    if (!routineVariantLookupPromises[handle]) {
      routineVariantLookupPromises[handle] = fetch(
        "/products/" + encodeURIComponent(handle) + ".js",
        {
          credentials: "same-origin"
        }
      )
        .then(function (response) {
          if (!response.ok) throw new Error("product-lookup-failed");
          return response.json();
        })
        .then(function (productData) {
          var variants = Array.isArray(productData.variants)
            ? productData.variants
            : [];
          var chosen = null;
          for (var i = 0; i < variants.length; i++) {
            if (variants[i] && variants[i].available) {
              chosen = variants[i];
              break;
            }
          }
          if (!chosen && variants.length) chosen = variants[0];
          var variantId = chosen && chosen.id ? String(chosen.id) : "";
          if (variantId) routineVariantIdCache[handle] = variantId;
          return variantId || getRoutineFallbackSkuId(product);
        })
        .catch(function () {
          return getRoutineFallbackSkuId(product);
        })
        .then(function (variantId) {
          delete routineVariantLookupPromises[handle];
          return variantId;
        });
    }
    return routineVariantLookupPromises[handle].then(function (variantId) {
      if (routineVariantIdCache[handle]) {
        return setRoutineProductCartVariantId(
          product,
          routineVariantIdCache[handle]
        );
      }
      return String(variantId || "");
    });
  }
  function preloadRoutineCartContext(products) {
    var list = Array.isArray(products) ? products : [];
    for (var i = 0; i < list.length; i++) {
      resolveRoutineCartVariantId(list[i]).catch(function () {});
    }
    if (!routineCartDataCache) {
      fetchCartData().catch(function () {});
    }
  }
  function formatRoutinePriceDisplay(product) {
    return formatRoutineMoney(
      getRoutinePriceAmount(product),
      product && product.currency
    );
  }
  function getRoutinePriceAmount(product) {
    if (!product) return 0;
    var priceRaw =
      product.selling_price != null && String(product.selling_price).trim()
        ? product.selling_price
        : product.price != null && String(product.price).trim()
          ? product.price
          : product.mrp;
    var digits = String(priceRaw == null ? "0" : priceRaw).replace(
      /[^0-9.]/g,
      ""
    );
    return Number(digits) || 0;
  }
  function formatRoutineMoney(amount, currency) {
    var rawCurrency = String(currency || "INR").trim();
    var symbol = getRoutineCurrencySymbol(rawCurrency);
    var prefix = symbol || rawCurrency.toUpperCase() + " ";
    return prefix + formatRoutineAmount(amount);
  }
  function getRoutineCurrencySymbol(currency) {
    var code = String(currency || "")
      .trim()
      .toLowerCase();
    if (code === "rs" || code === "rs.") return ROUTINE_CURRENCY_SYMBOLS.inr;
    return ROUTINE_CURRENCY_SYMBOLS[code] || "";
  }
  function formatRoutineAmount(amount) {
    var rounded = Math.round((Number(amount) || 0) * 100) / 100;
    var isWhole = Math.abs(rounded - Math.round(rounded)) < 0.001;
    var text = isWhole ? String(Math.round(rounded)) : rounded.toFixed(2);
    return text.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }
  function normalizeRoutineTimePeriod(value) {
    var raw = String(value || "")
      .trim()
      .toLowerCase();
    if (raw === "am" || raw === "morning") return "am";
    if (raw === "pm" || raw === "evening" || raw === "night") return "pm";
    if (raw === "both" || raw === "all" || raw === "am_pm") return "both";
    return "";
  }
  function getRoutineStepTimePeriod(step) {
    var direct = normalizeRoutineTimePeriod(
      (step && (step.timePeriod || step.am_pm || step.period)) || ""
    );
    if (direct) return direct;
    var products = step && Array.isArray(step.products) ? step.products : [];
    for (var i = 0; i < products.length; i++) {
      var fromProduct = normalizeRoutineTimePeriod(
        products[i] && (products[i].am_pm || products[i].timePeriod)
      );
      if (fromProduct) return fromProduct;
    }
    return "routine";
  }
  function getRoutineSectionLabel(sectionKey) {
    if (sectionKey === "am") return "AM Routine";
    if (sectionKey === "pm") return "PM Routine";
    if (sectionKey === "both") return "AM + PM Routine";
    return "Routine";
  }
  function getRoutineSectionOrder(sectionKey) {
    if (sectionKey === "am") return 0;
    if (sectionKey === "pm") return 1;
    if (sectionKey === "both") return 2;
    return 3;
  }
  /*
    PIM normalization helpers.

    PIM product attributes arrive as attribute_data rows. These helpers keep all
    parsing in one place so rule selection can use normalized skin types,
    concerns, category, currency, image, URL, and AM/PM values.
  */
  function getPimAttribute(resource, code) {
    var attrs =
      resource && Array.isArray(resource.attribute_data)
        ? resource.attribute_data
        : [];
    for (var i = 0; i < attrs.length; i++) {
      if (attrs[i] && attrs[i].code === code) return attrs[i].value;
    }
    return "";
  }
  function getPimAttributeList(resource, code) {
    var value = getPimAttribute(resource, code);
    var out = [];
    function pushValue(item) {
      if (item == null) return;
      if (Array.isArray(item)) {
        for (var i = 0; i < item.length; i++) pushValue(item[i]);
        return;
      }
      String(item)
        .split(",")
        .forEach(function (part) {
          var text = part.trim();
          if (text) out.push(text);
        });
    }
    pushValue(value);
    return out;
  }
  function getPimAttributeFirst(resource, code) {
    var value = getPimAttribute(resource, code);
    if (Array.isArray(value)) return value.length ? value[0] : "";
    return value == null ? "" : value;
  }
  function normalizePimList(values, mapper) {
    var out = [];
    var seen = {};
    for (var i = 0; i < values.length; i++) {
      var mapped = mapper(values[i]);
      if (mapped) pushUnique(out, seen, mapped);
    }
    return out;
  }
  function normalizePimSkinType(value) {
    return normalizeApiSkinToken(value);
  }
  function normalizePimConcern(value) {
    return mapNormalizedConcernToApiKey(value);
  }
  function getPimProductSkinTypes(resource) {
    return normalizePimList(
      getPimAttributeList(resource, "skin_type"),
      normalizePimSkinType
    );
  }
  function getPimProductConcerns(resource) {
    return normalizePimList(
      getPimAttributeList(resource, "concern"),
      normalizePimConcern
    );
  }
  function getPimProductCategory(resource) {
    return String(getPimAttributeFirst(resource, "category") || "Step").trim();
  }
  function getPimProductCurrency(resource) {
    return String(getPimAttributeFirst(resource, "currency") || "INR").trim();
  }
  function getPimProductUrl(resource) {
    return String(getPimAttributeFirst(resource, "product_url") || "").trim();
  }
  function getPimProductImage(resource) {
    var images =
      resource && Array.isArray(resource.images) ? resource.images : [];
    for (var i = 0; i < images.length; i++) {
      if (images[i] && images[i].src) return String(images[i].src);
    }
    return "";
  }
  function getPimProductTimePeriod(resource) {
    var rawPeriods = getPimAttributeList(resource, "am_pm");
    var periods = normalizePimList(rawPeriods, normalizeRoutineTimePeriod);
    if (periods.indexOf("both") !== -1) return "both";
    if (periods.indexOf("am") !== -1 && periods.indexOf("pm") !== -1) {
      return "both";
    }
    if (periods.indexOf("am") !== -1) return "am";
    if (periods.indexOf("pm") !== -1) return "pm";
    return "routine";
  }
  function normalizePimProductTitle(value) {
    var raw = String(value || "")
      .trim()
      .toLowerCase();
    raw = raw
      .replace(/\u00c3\u00a8/g, "\u00e8")
      .replace(/\u00c3\u00a9/g, "\u00e9")
      .replace(/\u00c2/g, "");
    if (raw.normalize) {
      raw = raw.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    }
    return raw
      .replace(/&/g, "and")
      .replace(/['’]/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }
  function createPimProductLookup(resources) {
    var lookup = {
      byTitle: {},
      discontinued: {},
      missing: [],
      missingSeen: {}
    };
    for (var d = 0; d < ASAYA_DISCONTINUED_PRODUCT_TITLES.length; d++) {
      lookup.discontinued[
        normalizePimProductTitle(ASAYA_DISCONTINUED_PRODUCT_TITLES[d])
      ] = true;
    }
    var list = Array.isArray(resources) ? resources : [];
    for (var i = 0; i < list.length; i++) {
      var resource = list[i];
      if (!resource || !resource.title) continue;
      lookup.byTitle[normalizePimProductTitle(resource.title)] = resource;
    }
    return lookup;
  }
  function recordMissingPimProduct(lookup, title, reason) {
    if (!lookup || !title) return;
    var key = normalizePimProductTitle(title) + "|" + String(reason || "");
    if (lookup.missingSeen[key]) return;
    lookup.missingSeen[key] = true;
    lookup.missing.push({
      title: title,
      reason: reason || "missing_from_pim"
    });
  }
  function getPimProductTitleCandidates(title) {
    var candidates = [];
    var seen = {};
    var key = normalizePimProductTitle(title);
    pushUnique(candidates, seen, title);
    Object.keys(ASAYA_PIM_PRODUCT_TITLE_ALIASES).forEach(function (alias) {
      if (normalizePimProductTitle(alias) !== key) return;
      var mapped = ASAYA_PIM_PRODUCT_TITLE_ALIASES[alias] || [];
      for (var i = 0; i < mapped.length; i++) {
        pushUnique(candidates, seen, mapped[i]);
      }
    });
    return candidates;
  }
  function findPimResourceByTitle(lookup, title) {
    if (!lookup || !title) return null;
    var candidates = getPimProductTitleCandidates(title);
    var foundDiscontinued = false;
    for (var i = 0; i < candidates.length; i++) {
      var key = normalizePimProductTitle(candidates[i]);
      if (lookup.discontinued[key]) {
        foundDiscontinued = true;
        continue;
      }
      if (lookup.byTitle[key]) return lookup.byTitle[key];
    }
    if (lookup.discontinued[normalizePimProductTitle(title)]) {
      recordMissingPimProduct(lookup, title, "discontinued");
      return null;
    }
    recordMissingPimProduct(
      lookup,
      title,
      foundDiscontinued ? "discontinued" : "missing_from_pim"
    );
    return null;
  }
  function findFirstPimResourceByTitles(lookup, titles) {
    var list = Array.isArray(titles) ? titles : [];
    for (var i = 0; i < list.length; i++) {
      var resource = findPimResourceByTitle(lookup, list[i]);
      if (resource) return resource;
    }
    return null;
  }
  /*
    Asaya profile eligibility gates.

    These functions stop a technically matching product from being shown when
    the customer profile makes it inappropriate. Current gates cover dry-skin
    exclusions and pregnancy/postpartum avoidance for salicylic, kojic, retinoid,
    and retinol signals in the title or description.
  */
  function getAsayaProfileSkinTypesForGate(context) {
    var out = [];
    var seen = {};
    var skinTypes = (context && context.skinTypes) || [];
    for (var i = 0; i < skinTypes.length; i++) {
      pushUnique(out, seen, skinTypes[i]);
    }
    var quizSkinType = mapQuizSkinTypeToApi(getQuizAnswer("skinType"));
    if (quizSkinType) pushUnique(out, seen, quizSkinType);
    if (latestSkinResult && latestSkinResult.skinType) {
      var scanSkinType = normalizeApiSkinToken(latestSkinResult.skinType);
      if (scanSkinType) pushUnique(out, seen, scanSkinType);
    }
    return out;
  }
  function hasAsayaProfileSkinType(context, skinType) {
    return getAsayaProfileSkinTypesForGate(context).indexOf(skinType) !== -1;
  }
  function isPimResourceExcludedForAsayaProfile(resource, context) {
    if (!resource) return true;
    var title = normalizePimProductTitle(resource.title);
    var description = normalizePimProductTitle(resource.description);
    var signal = title + " " + description;
    if (hasAsayaProfileSkinType(context, "dry")) {
      if (
        /salicylic face cleanser|spot light sunscreen|sheer milk/.test(
          signal
        ) ||
        /oil-free barrier|even evermore|glow and lift|overnight revive/.test(
          signal
        )
      ) {
        return true;
      }
    }
    if (getQuizAnswer("hormonalFactors") === "Yes") {
      if (/salicylic acid|kojic acid|retinoid|retinol/.test(signal)) {
        return true;
      }
    }
    return false;
  }
  function findFirstEligiblePimResourceByTitles(lookup, titles, context) {
    var list = Array.isArray(titles) ? titles : [];
    for (var i = 0; i < list.length; i++) {
      var resource = findPimResourceByTitle(lookup, list[i]);
      if (!resource) continue;
      if (!isPimResourceExcludedForAsayaProfile(resource, context)) {
        return resource;
      }
      recordMissingPimProduct(lookup, list[i], "profile_excluded");
    }
    return null;
  }
  function getAsayaSheetRuleByConcern(key) {
    var target = mapNormalizedConcernToApiKey(key);
    if (!target) return null;
    for (var i = 0; i < ASAYA_SHEET_RECOMMENDATION_RULES.length; i++) {
      if (ASAYA_SHEET_RECOMMENDATION_RULES[i].concern === target) {
        return ASAYA_SHEET_RECOMMENDATION_RULES[i];
      }
    }
    return null;
  }
  function getAsayaSheetConcernItems() {
    var source =
      reportConcerns && reportConcerns.length
        ? reportConcerns
        : latestSkinResult && latestSkinResult.concerns
          ? latestSkinResult.concerns
          : [];
    var out = [];
    var seen = {};
    for (var i = 0; i < source.length; i++) {
      var item = source[i] || {};
      var rawKey =
        item.key ||
        (item.raw && (item.raw.tech_name || item.raw.name)) ||
        item.name ||
        "";
      var apiKey = mapNormalizedConcernToApiKey(rawKey);
      if (!apiKey || seen[apiKey]) continue;
      seen[apiKey] = true;
      out.push({
        apiKey: apiKey,
        name: getConcernDisplayName(apiKey, item.name),
        score: item.value
      });
    }
    return out;
  }
  /*
    Sheet rule selection.

    Scan concerns are already sorted from lowest score to highest score, so the
    first concern below its sheet threshold becomes the routine's primary rule.
    If a concern is detected without a numeric score, the rule is still eligible
    as a weaker "detected" source.
  */
  function getAsayaSheetRuleSelection() {
    var sheetItems = getAsayaSheetConcernItems();
    for (var i = 0; i < sheetItems.length; i++) {
      var item = sheetItems[i];
      var rule = getAsayaSheetRuleByConcern(item.apiKey);
      var score = Number(item.score);
      if (rule && Number.isFinite(score) && score < rule.threshold) {
        return {
          rule: rule,
          concern: item.apiKey,
          score: score,
          source: "threshold"
        };
      }
    }
    for (var d = 0; d < sheetItems.length; d++) {
      var detectedItem = sheetItems[d];
      var detectedRule = getAsayaSheetRuleByConcern(detectedItem.apiKey);
      if (detectedRule && !Number.isFinite(Number(detectedItem.score))) {
        return {
          rule: detectedRule,
          concern: detectedRule.concern,
          score: null,
          source: "detected"
        };
      }
    }
    return {
      rule: null,
      concern: "",
      score: null,
      source: "no_threshold_match"
    };
  }
  /*
    Builds the complete context used by the Asaya routine rules. This is the
    central place to add or remove decision signals when client rules change.
  */
  function getAsayaRuleContext(filterContext) {
    var context = filterContext || getRoutineFilterContext();
    var priorityConcern = mapQuizPriorityToApiConcern();
    var acneAnswer = getQuizAnswer("acne");
    var acneConcernEnabled = !!mapNormalizedConcernToApiKey("acne");
    var hormonalPigmentation = getQuizAnswer("hormonalFactors") === "Yes";
    var sheetRuleSelection = getAsayaSheetRuleSelection();
    var concernSeen = {};
    var concerns = [];
    function pushConcern(key) {
      var apiKey = mapNormalizedConcernToApiKey(key);
      if (!apiKey || concernSeen[apiKey]) return;
      concernSeen[apiKey] = true;
      concerns.push(apiKey);
    }
    if (sheetRuleSelection.concern) pushConcern(sheetRuleSelection.concern);
    if (priorityConcern) pushConcern(priorityConcern);
    var sourceConcerns = (context && context.concerns) || [];
    for (var i = 0; i < sourceConcerns.length; i++) {
      pushConcern(sourceConcerns[i]);
    }
    if (hormonalPigmentation) pushConcern("pigmentation");
    if (acneAnswer === "Frequently" || acneAnswer === "Occasionally") {
      pushConcern("acne");
    }
    return {
      concerns: concerns,
      skinTypes: ((context && context.skinTypes) || []).slice(),
      priorityConcern: priorityConcern,
      hormonalPigmentation: hormonalPigmentation,
      acneProne:
        acneConcernEnabled &&
        (acneAnswer === "Frequently" || acneAnswer === "Occasionally"),
      sheetRule: sheetRuleSelection.rule,
      sheetRuleConcern: sheetRuleSelection.concern,
      sheetRuleScore: sheetRuleSelection.score,
      sheetRuleSource: sheetRuleSelection.source
    };
  }
  /*
    Recommended set selection.

    Set choices are intentionally separate from the individual AM/PM products.
    They combine severe scan scores, quiz priority, body-care answer, and active
    pigmentation-family concerns to pick one set title to resolve from PIM.
  */
  function getAsayaRecommendedSetTitles(context) {
    var priority = (context && context.priorityConcern) || "";
    var bodyAnswer = getQuizAnswer("bodyExtra");
    var pigmentationScore = getAsayaConcernScore("pigmentation");
    var scarsScore = getAsayaConcernScore("post_acne_scars");
    var acneScore = getAsayaConcernScore("acne");
    var hydrationScore = getAsayaConcernScore("hydration");
    var hasPigmentationFamily =
      isAsayaActiveConcern("pigmentation") ||
      isAsayaActiveConcern("post_acne_scars");
    if (
      hasPigmentationFamily &&
      bodyAnswer &&
      bodyAnswer !== "Just my face for now"
    ) {
      return ["Head to Toe Anti Pigmentation Set"];
    }
    if (
      Number.isFinite(pigmentationScore) &&
      pigmentationScore < 70 &&
      Number.isFinite(scarsScore) &&
      scarsScore < 70
    ) {
      return ["3-Step Spot Diminishing Set"];
    }
    if (
      priority === "pigmentation" &&
      Number.isFinite(pigmentationScore) &&
      pigmentationScore < 60
    ) {
      return ["Advanced Anti-Pigmentation Kit"];
    }
    if (priority === "acne" && Number.isFinite(acneScore) && acneScore < 60) {
      return ["Anti-Acne Set"];
    }
    if (
      priority === "hydration" &&
      Number.isFinite(hydrationScore) &&
      hydrationScore < 60
    ) {
      return ["Even Tone Restore Set"];
    }
    if (hasSingleSevereAsayaConcern()) {
      return ["Rapid Dark Spot Correction Set"];
    }
    if (!hasPigmentationFamily) {
      return ["Even Tone Restore Set"];
    }
    return ["Anti Pigmentation Set"];
  }
  function getAsayaConcernScore(apiKey) {
    var items = getAsayaSheetConcernItems();
    for (var i = 0; i < items.length; i++) {
      if (items[i] && items[i].apiKey === apiKey) {
        var score = Number(items[i].score);
        return Number.isFinite(score) ? score : null;
      }
    }
    return null;
  }
  function isAsayaActiveConcern(apiKey) {
    var rule = getAsayaSheetRuleByConcern(apiKey);
    var score = getAsayaConcernScore(apiKey);
    return !!(rule && Number.isFinite(score) && score < rule.threshold);
  }
  function hasSingleSevereAsayaConcern() {
    var severeCount = 0;
    var items = getAsayaSheetConcernItems();
    for (var i = 0; i < items.length; i++) {
      var score = Number(items[i] && items[i].score);
      if (Number.isFinite(score) && score < 50) severeCount += 1;
    }
    return severeCount === 1;
  }
  function hasAnyRoutineOverlap(left, right) {
    if (!left.length || !right.length) return true;
    for (var i = 0; i < left.length; i++) {
      if (right.indexOf(left[i]) !== -1) return true;
    }
    return false;
  }
  function getPimProductMatchScore(resource, filterContext) {
    var context = filterContext || getRoutineFilterContext();
    var productConcerns = getPimProductConcerns(resource);
    var productSkinTypes = getPimProductSkinTypes(resource);
    var score = 0;
    for (var c = 0; c < productConcerns.length; c++) {
      var concernIndex = context.concerns.indexOf(productConcerns[c]);
      if (concernIndex !== -1) score += 120 - concernIndex * 12;
    }
    for (var s = 0; s < productSkinTypes.length; s++) {
      if (context.skinTypes.indexOf(productSkinTypes[s]) !== -1) score += 24;
    }
    return score;
  }
  function filterPimResourcesForRoutine(resources, filterContext) {
    var context = filterContext || getRoutineFilterContext();
    var list = Array.isArray(resources) ? resources : [];
    var indexed = [];
    for (var i = 0; i < list.length; i++) {
      if (list[i] && typeof list[i] === "object") {
        indexed.push({ resource: list[i], index: i });
      }
    }
    function matches(entry, mode) {
      var resource = entry.resource;
      var skinMatch = hasAnyRoutineOverlap(
        getPimProductSkinTypes(resource),
        context.skinTypes
      );
      var concernMatch = hasAnyRoutineOverlap(
        getPimProductConcerns(resource),
        context.concerns
      );
      if (mode === "both") return skinMatch && concernMatch;
      if (mode === "concern") return concernMatch;
      if (mode === "skin") return skinMatch;
      return true;
    }
    function sortMatches(items) {
      return items
        .slice()
        .sort(function (a, b) {
          var scoreDiff =
            getPimProductMatchScore(b.resource, context) -
            getPimProductMatchScore(a.resource, context);
          return scoreDiff || a.index - b.index;
        })
        .map(function (entry) {
          return entry.resource;
        });
    }
    var exact = indexed.filter(function (entry) {
      return matches(entry, "both");
    });
    if (exact.length) return sortMatches(exact);
    var concernOnly = indexed.filter(function (entry) {
      return matches(entry, "concern");
    });
    if (concernOnly.length) return sortMatches(concernOnly);
    var skinOnly = indexed.filter(function (entry) {
      return matches(entry, "skin");
    });
    if (skinOnly.length) return sortMatches(skinOnly);
    return sortMatches(indexed);
  }
  /*
    Routine plan shaping helpers.

    getRoutineCategoryOrder() controls the visual order of steps inside each
    AM/PM section. createRoutineProductFromPimResource() is the canonical mapper
    from a PIM resource to the product card/cart payload used by the UI.
  */
  function getRoutineCategoryOrder(category) {
    var key = String(category || "").toLowerCase();
    if (/clean/.test(key)) return 0;
    if (/exfol|peel|scrub/.test(key)) return 1;
    if (/serum|treat|correct|retinoid|complex/.test(key)) return 2;
    if (/eye/.test(key)) return 3;
    if (/cream|moistur/.test(key)) return 4;
    if (/protect|sunscreen|spf/.test(key)) return 5;
    if (/kit|set/.test(key)) return 6;
    if (/body/.test(key)) return 7;
    return 8;
  }
  function getSheetRuleWhyCopy(selection) {
    if (!selection || !selection.concern) return "";
    var label = getConcernDisplayName(selection.concern, "");
    var score = Number(selection.score);
    if (Number.isFinite(score)) {
      return (
        "Picked because " +
        label +
        " scored " +
        Math.round(score) +
        "/100 on your scan."
      );
    }
    return (
      "Picked because " + label.toLowerCase() + " was detected in your scan."
    );
  }
  /*
    Converts a PIM resource into the product shape consumed by all routine
    renderers and cart handlers. Keep this shape stable if Shopify markup or
    cart behavior is customized by the client.
  */
  function createRoutineProductFromPimResource(
    resource,
    sectionKey,
    stepCategory,
    index,
    selection
  ) {
    var currency = getPimProductCurrency(resource);
    var brandName =
      resource && resource.brand && resource.brand.name
        ? String(resource.brand.name)
        : "";
    var apiProduct = {
      title: resource && resource.title,
      name: resource && resource.title,
      brand: brandName,
      description: resource && resource.description,
      mrp: resource && resource.mrp,
      selling_price: resource && resource.selling_price,
      currency: currency,
      sku: resource && resource.sku,
      url: getPimProductUrl(resource),
      product_url: getPimProductUrl(resource),
      image: getPimProductImage(resource),
      img: getPimProductImage(resource),
      am_pm: sectionKey,
      timePeriod: sectionKey,
      category: stepCategory,
      concerns: getPimProductConcerns(resource),
      skin_type: getPimProductSkinTypes(resource)
    };
    var why =
      getSheetRuleWhyCopy(selection) ||
      getRoutineWhyCopy({ category: stepCategory }, apiProduct);
    return {
      step: String(stepCategory),
      stepOrder: 1,
      timePeriod: sectionKey,
      name: String((resource && resource.title) || "Recommended product"),
      brand: brandName,
      why: String(why),
      price: formatRoutinePriceDisplay(apiProduct),
      priceValue: getRoutinePriceAmount(apiProduct),
      currency: String(currency || "INR").toUpperCase(),
      image: getPimProductImage(resource),
      sku: String((resource && resource.sku) || "").trim(),
      skuId: extractShopifyVariantIdFromRoutineProduct(apiProduct) || "",
      url: getPimProductUrl(resource),
      _apiProduct: apiProduct,
      _pimResource: resource,
      _sheetRuleConcern: selection && selection.concern,
      _sheetRuleScore: selection && selection.score,
      _routineIndex: index
    };
  }
  function getAsayaPlanSection(plan, sectionKey) {
    var key = sectionKey || "routine";
    if (!plan._sectionsByKey[key]) {
      plan._sectionsByKey[key] = {
        key: key,
        label: getRoutineSectionLabel(key),
        steps: []
      };
      plan.sections.push(plan._sectionsByKey[key]);
    }
    return plan._sectionsByKey[key];
  }
  function getAsayaSectionStepByCategory(section, category) {
    var steps = (section && section.steps) || [];
    var target = normalizePimProductTitle(category);
    for (var i = 0; i < steps.length; i++) {
      if (normalizePimProductTitle(steps[i].category) === target) {
        return steps[i];
      }
    }
    return null;
  }
  /*
    Fills one fixed routine slot from a candidate title list. The title list is
    already ordered by preference, so the first eligible PIM match wins. The
    used-key allows the same product to appear in AM and PM when intended, but
    prevents duplicates inside the same section/category slot.
  */
  function addAsayaRuleProduct(plan, lookup, rule) {
    var resource = findFirstEligiblePimResourceByTitles(
      lookup,
      rule.titles,
      plan._ruleContext
    );
    if (!resource) return null;
    var titleKey = normalizePimProductTitle(resource.title);
    var sectionKey = rule.sectionKey || "am";
    var usedKey =
      sectionKey +
      "|" +
      normalizePimProductTitle(rule.category) +
      "|" +
      titleKey;
    if (plan._usedProductTitles[usedKey]) return null;
    plan._usedProductTitles[usedKey] = true;
    var product = createRoutineProductFromPimResource(
      resource,
      sectionKey,
      rule.category,
      plan.products.length,
      plan._sheetRuleSelection
    );
    var section = getAsayaPlanSection(plan, sectionKey);
    var existingStep = getAsayaSectionStepByCategory(section, rule.category);
    if (existingStep) {
      existingStep.products.push(product);
    } else {
      section.steps.push({
        category: rule.category,
        stepOrder: rule.stepOrder || section.steps.length + 1,
        products: [product]
      });
    }
    plan.products.push(product);
    return product;
  }
  /*
    Picks the single recommended set card shown above "or build your own".
    It uses the same eligibility gate as normal products so unsafe/profile-
    excluded sets are not rendered.
  */
  function setAsayaRecommendedSet(plan, lookup, context) {
    var resource = findFirstEligiblePimResourceByTitles(
      lookup,
      getAsayaRecommendedSetTitles(context),
      context
    );
    if (!resource) return;
    var product = createRoutineProductFromPimResource(
      resource,
      "recommended-set",
      "Recommended Set",
      -1,
      plan._sheetRuleSelection
    );
    product.step = "Recommended Set";
    product.stepOrder = 1;
    product.timePeriod = "recommended-set";
    product._setKey = normalizePimProductTitle(resource.title);
    product._routineIndex = -1;
    plan.recommendedSet = product;
  }
  function getAsayaRuleProductTitlesByCategory(context, category) {
    var rule = context && context.sheetRule;
    var products =
      rule && Array.isArray(rule.routineProducts) ? rule.routineProducts : [];
    var out = [];
    var seen = {};
    var target = normalizePimProductTitle(category);
    for (var i = 0; i < products.length; i++) {
      var productCategory = products[i] && products[i].category;
      if (normalizePimProductTitle(productCategory) === target) {
        pushUnique(out, seen, products[i].title);
      }
    }
    return out;
  }
  /*
    Scaffold fallback titles are the client-approved product priorities for
    each fixed routine slot. Sheet titles are concatenated before these fallbacks
    in getAsayaRoutineRules(), so an active concern can override the default.
  */
  function getAsayaScaffoldTitlesForCategory(category, context) {
    var dry = hasAsayaProfileSkinType(context, "dry");
    var concerns = (context && context.concerns) || [];
    var acneProne =
      (context && context.acneProne) || concerns.indexOf("acne") !== -1;
    if (category === "cleanser") {
      return dry
        ? ["Even tone facewash", "D Tan Face Cleanser", "Salicylic facewash"]
        : ["Even tone facewash", "Salicylic facewash", "D Tan Face Cleanser"];
    }
    if (category === "moisturiser") {
      if (dry) {
        return [
          "New light Gel",
          "Moisture Cocoon Creme - Hydrating & Glow Moisturizer with Niacinamide",
          "Glow and Lift Moist."
        ];
      }
      if (acneProne) {
        return [
          "Oil-Free Barrier+ Crème",
          "New light Gel",
          "Glow and Lift Moist."
        ];
      }
      return [
        "New light Gel",
        "Moisture Cocoon Creme - Hydrating & Glow Moisturizer with Niacinamide",
        "Glow and Lift Moist."
      ];
    }
    if (category === "sunscreen") {
      return dry
        ? [
            "Hint of Tint",
            "Sun Drip Mineral Sunscreen",
            "Sheerscreen Mist 100ml",
            "Spot Light SPF"
          ]
        : [
            "Spot Light SPF",
            "Hint of Tint",
            "Sheerscreen Mist 100ml",
            "Sun Drip Mineral Sunscreen"
          ];
    }
    if (category === "repair") {
      return [
        "Overnight Revive Crème",
        "Moisture Cocoon Creme - Hydrating & Glow Moisturizer with Niacinamide",
        "New light Gel"
      ];
    }
    return [];
  }
  /*
    Targeted treatment priority logic.

    AM uses the active sheet treatment first. PM may prefer vitamin C or peptide
    support depending on pigmentation severity, then falls back to the sheet
    treatment list.
  */
  function getAsayaTreatmentTitles(context, period) {
    var targetTitles = getAsayaRuleProductTitlesByCategory(
      context,
      "Targeted Treatment"
    );
    if (!targetTitles.length) {
      targetTitles = [
        "Advanced Spot Targeting Serum",
        "10% Niacinamide & 1% Alpha Arbutin Serum",
        "2% Kojic Acid & 1% Alpha Arbutin Serum",
        "5% Multi Peptide & HA Serum"
      ];
    }
    if (period === "pm") {
      var pigmentationScore = getAsayaConcernScore("pigmentation");
      if (
        Number.isFinite(pigmentationScore) &&
        pigmentationScore >= 60 &&
        pigmentationScore < 75
      ) {
        return [
          "15% Vitamin C Serum",
          "10% Vitamin C Serum",
          "5% Multi Peptide & HA Serum"
        ].concat(targetTitles);
      }
      if (
        Number.isFinite(
          getAsayaConcernScore((context && context.sheetRuleConcern) || "")
        ) &&
        getAsayaConcernScore(context.sheetRuleConcern) < 50
      ) {
        return targetTitles.concat(["5% Multi Peptide & HA Serum"]);
      }
      return ["5% Multi Peptide & HA Serum"].concat(targetTitles);
    }
    return targetTitles;
  }
  /*
    The fixed Asaya routine scaffold. This is the main routine contract:
    every returned object is one slot, with a section, display category, step
    order, and ordered title candidates used by addAsayaRuleProduct().
  */
  function getAsayaRoutineRules(context) {
    var cleanserTitles = getAsayaRuleProductTitlesByCategory(
      context,
      "Cleanser"
    ).concat(getAsayaScaffoldTitlesForCategory("cleanser", context));
    var moisturiserTitles = getAsayaRuleProductTitlesByCategory(
      context,
      "Moisturiser"
    ).concat(getAsayaScaffoldTitlesForCategory("moisturiser", context));
    var sunscreenTitles = getAsayaRuleProductTitlesByCategory(
      context,
      "Sunscreen"
    ).concat(getAsayaScaffoldTitlesForCategory("sunscreen", context));
    return [
      {
        sectionKey: "am",
        stepOrder: 1,
        category: "Cleanser",
        titles: cleanserTitles
      },
      {
        sectionKey: "am",
        stepOrder: 2,
        category: "Targeted Treatment",
        titles: getAsayaTreatmentTitles(context, "am")
      },
      {
        sectionKey: "am",
        stepOrder: 3,
        category: "Moisturiser",
        titles: moisturiserTitles
      },
      {
        sectionKey: "am",
        stepOrder: 4,
        category: "Sunscreen",
        titles: sunscreenTitles
      },
      {
        sectionKey: "pm",
        stepOrder: 1,
        category: "Cleanser",
        titles: cleanserTitles
      },
      {
        sectionKey: "pm",
        stepOrder: 2,
        category: "Targeted Treatment",
        titles: getAsayaTreatmentTitles(context, "pm")
      },
      {
        sectionKey: "pm",
        stepOrder: 3,
        category: "Repair",
        titles: getAsayaScaffoldTitlesForCategory("repair", context)
      }
    ];
  }
  /*
    Final clean-up before rendering. It sorts sections/steps, rewrites visible
    step numbers after missing products are skipped, and removes private builder
    fields so renderers do not accidentally rely on transient state.
  */
  function finalizeAsayaRoutinePlan(plan) {
    plan.sections.sort(function (a, b) {
      return getRoutineSectionOrder(a.key) - getRoutineSectionOrder(b.key);
    });
    for (var s = 0; s < plan.sections.length; s++) {
      var steps = plan.sections[s].steps || [];
      steps.sort(function (a, b) {
        var orderDiff =
          getRoutineCategoryOrder(a.category) -
          getRoutineCategoryOrder(b.category);
        return (
          orderDiff || String(a.category).localeCompare(String(b.category))
        );
      });
      for (var st = 0; st < steps.length; st++) {
        steps[st].stepOrder = st + 1;
        for (var p = 0; p < steps[st].products.length; p++) {
          steps[st].products[p].stepOrder = st + 1;
        }
      }
    }
    delete plan._sectionsByKey;
    delete plan._usedProductTitles;
    delete plan._ruleContext;
    return plan;
  }
  /*
    Main PIM-to-routine mapper.

    This is called after fetchRoutineRecommendation(). It builds a title lookup,
    derives the Asaya rule context, attaches a recommended set, fills each fixed
    AM/PM slot, and returns a renderer-ready plan.
  */
  function mapPimResourcesToRoutinePlan(resources, filterContext) {
    var lookup = createPimProductLookup(resources);
    var context = getAsayaRuleContext(filterContext);
    var plan = {
      sections: [],
      products: [],
      recommendedSet: null,
      missingProducts: lookup.missing,
      _pimResources: Array.isArray(resources) ? resources : [],
      _sectionsByKey: {},
      _usedProductTitles: {},
      _ruleContext: context,
      _sheetRuleSelection: {
        concern: context.sheetRuleConcern,
        score: context.sheetRuleScore,
        source: context.sheetRuleSource
      }
    };
    setAsayaRecommendedSet(plan, lookup, context);
    var rules = getAsayaRoutineRules(context);
    for (var i = 0; i < rules.length; i++) {
      addAsayaRuleProduct(plan, lookup, rules[i]);
    }
    return finalizeAsayaRoutinePlan(plan);
  }
  /*
    Reason-copy helpers.

    These functions prefer explicit API/PIM reasons when available, then derive
    clear "Picked because ..." copy from the scan concern, quiz priority,
    hormonal flag, or skin type. This copy appears under each routine product.
  */
  function normalizeRoutineWhySentence(value) {
    if (value && typeof value === "object") {
      value =
        value.text ||
        value.message ||
        value.reason ||
        value.description ||
        value.value ||
        "";
    }
    var text = String(value || "")
      .replace(/\s+/g, " ")
      .trim();
    if (!text) return "";
    var firstSentence = text.match(/^(.+?[.!?])(\s|$)/);
    if (firstSentence && firstSentence[1]) return firstSentence[1].trim();
    return /[.!?]$/.test(text) ? text : text + ".";
  }
  function getApiRoutineReason(step, product) {
    var candidates = [
      product && product.recommendation_reason,
      product && product.recommendationReason,
      product && product.match_reason,
      product && product.matchReason,
      product && product.selection_reason,
      product && product.selectionReason,
      product && product.picked_reason,
      product && product.pickedReason,
      product && product.reason,
      product && product.why,
      product && product.rationale,
      step && step.recommendation_reason,
      step && step.recommendationReason,
      step && step.match_reason,
      step && step.matchReason,
      step && step.selection_reason,
      step && step.selectionReason,
      step && step.picked_reason,
      step && step.pickedReason,
      step && step.reason,
      step && step.why,
      step && step.rationale
    ];
    for (var i = 0; i < candidates.length; i++) {
      var reason = normalizeRoutineWhySentence(candidates[i]);
      if (reason) return reason;
    }
    return "";
  }
  function stringifyRoutineSignal(value) {
    if (value == null) return "";
    if (Array.isArray(value)) return value.join(" ");
    if (typeof value === "object") {
      return Object.keys(value)
        .map(function (key) {
          return value[key];
        })
        .join(" ");
    }
    return String(value);
  }
  function getRoutineTextSignal(step, product) {
    return [
      product && product.title,
      product && product.name,
      product && product.category,
      product && product.type,
      product && product.concern,
      product && product.concerns,
      product && product.benefit,
      product && product.benefits,
      product && product.description,
      step && step.category,
      step && step.description,
      step && step.type
    ]
      .map(stringifyRoutineSignal)
      .join(" ")
      .toLowerCase();
  }
  function getRoutineConcernFromSignal(step, product) {
    var signal = getRoutineTextSignal(step, product);
    if (/dark circle|under.?eye dark|under.?eye pigment/.test(signal)) {
      return mapNormalizedConcernToApiKey("dark_circles");
    }
    if (/eye bag|puffy eye|under.?eye puff/.test(signal)) return "eye_bags";
    if (
      /pigment|bright|even tone|tone restore|spot|melasma|hyperpig/.test(signal)
    ) {
      return "pigmentation";
    }
    if (/acne|breakout|pimple|blemish|whitehead/.test(signal)) return "acne";
    if (/pore|congestion|sebum|oil/.test(signal)) return "pores";
    if (/hydrat|moistur|glow|barrier|dry/.test(signal)) return "hydration";
    if (/wrinkle|age|aging|ageing|lift|firm|fine line/.test(signal)) {
      return "wrinkles";
    }
    if (/redness|sooth|calm|sensitive/.test(signal)) return "redness";
    return "";
  }
  function getDiagnosisConcernItem(apiKey) {
    var target = String(apiKey || "");
    if (!target) return null;
    var profile = getDiagnosisProfile();
    var items = profile.concernItems || [];
    for (var i = 0; i < items.length; i++) {
      if (items[i] && items[i].apiKey === target) return items[i];
    }
    var source = latestSkinResult && latestSkinResult.concerns;
    if (!Array.isArray(source)) return null;
    for (var s = 0; s < source.length; s++) {
      var concern = source[s] || {};
      var rawKey =
        concern.key ||
        (concern.raw && (concern.raw.tech_name || concern.raw.name)) ||
        concern.name ||
        "";
      if (mapNormalizedConcernToApiKey(rawKey) === target) {
        return {
          apiKey: target,
          name: getConcernDisplayName(target, concern.name),
          score: concern.value
        };
      }
    }
    return null;
  }
  function getPrimaryDiagnosisConcernItem() {
    var profile = getDiagnosisProfile();
    var items = profile.concernItems || [];
    return items.length ? items[0] : null;
  }
  function formatConcernScanWhy(item) {
    if (!item) return "";
    var score = Number(item.score);
    if (!Number.isFinite(score)) return "";
    return (
      "Picked because " +
      getConcernDisplayName(item.apiKey, item.name) +
      " scored " +
      Math.round(score) +
      "/100 on your scan."
    );
  }
  function getQuizPriorityWhy(apiKey) {
    var priority = getQuizAnswer("priority");
    if (!priority || mapQuizPriorityToApiConcern() !== apiKey) return "";
    return (
      "Picked because you selected " +
      priority.toLowerCase() +
      " as your top skincare goal."
    );
  }
  function getHormonalPigmentationWhy(apiKey) {
    if (apiKey !== "pigmentation") return "";
    if (getQuizAnswer("hormonalFactors") !== "Yes") return "";
    return (
      "Picked because you said pregnancy or postpartum hormones may be " +
      "a pigmentation trigger."
    );
  }
  function getRoutineWhyFromScanOrQuiz(step, product) {
    var concernKey = getRoutineConcernFromSignal(step, product);
    if (!concernKey) concernKey = mapQuizPriorityToApiConcern();
    if (concernKey) {
      var scanWhy = formatConcernScanWhy(getDiagnosisConcernItem(concernKey));
      if (scanWhy) return scanWhy;
      var priorityWhy = getQuizPriorityWhy(concernKey);
      if (priorityWhy) return priorityWhy;
      var hormonalWhy = getHormonalPigmentationWhy(concernKey);
      if (hormonalWhy) return hormonalWhy;
    }
    var primaryWhy = formatConcernScanWhy(getPrimaryDiagnosisConcernItem());
    if (primaryWhy) return primaryWhy;
    var priority = getQuizAnswer("priority");
    if (priority) {
      return (
        "Picked because you selected " +
        priority.toLowerCase() +
        " as your top skincare goal."
      );
    }
    var skinType = getRoutineSkinTypeCopy();
    if (skinType && skinType !== "your") {
      return "Picked for your " + skinType.toLowerCase() + " skin profile.";
    }
    return "Picked from your scan and quiz responses.";
  }
  function getRoutineWhyCopy(step, product) {
    return (
      getApiRoutineReason(step, product) ||
      getRoutineWhyFromScanOrQuiz(step, product)
    );
  }
  function mapRoutineApiStepsToRoutine(steps) {
    var plan = {
      sections: [],
      products: []
    };
    if (!Array.isArray(steps)) return plan;
    var sectionsByKey = {};
    function getSection(key) {
      var sectionKey = key || "routine";
      if (!sectionsByKey[sectionKey]) {
        sectionsByKey[sectionKey] = {
          key: sectionKey,
          label: getRoutineSectionLabel(sectionKey),
          steps: []
        };
        plan.sections.push(sectionsByKey[sectionKey]);
      }
      return sectionsByKey[sectionKey];
    }
    var sorted = steps.slice().sort(function (a, b) {
      var ta = getRoutineStepTimePeriod(a);
      var tb = getRoutineStepTimePeriod(b);
      if (ta !== tb) {
        return getRoutineSectionOrder(ta) - getRoutineSectionOrder(tb);
      }
      var oa = a && Number(a.stepOrder);
      var ob = b && Number(b.stepOrder);
      if (Number.isFinite(oa) && Number.isFinite(ob) && oa !== ob) {
        return oa - ob;
      }
      return 0;
    });
    for (var s = 0; s < sorted.length; s++) {
      var step = sorted[s];
      var list = step && Array.isArray(step.products) ? step.products : [];
      if (!list.length) continue;
      var section = getSection(getRoutineStepTimePeriod(step));
      var stepProducts = [];
      var stepCategory =
        step.category ||
        step.description ||
        (list[0] && (list[0].category || list[0].type)) ||
        "Step";
      for (var pIdx = 0; pIdx < list.length; pIdx++) {
        var p = list[pIdx];
        if (!p || typeof p !== "object") continue;
        var title = p.title || p.name || "Recommended product";
        var why = getRoutineWhyCopy(step, p);
        var product = {
          step: String(stepCategory),
          stepOrder: Number(step.stepOrder) || section.steps.length + 1,
          timePeriod: section.key,
          name: String(title),
          brand: String(p.brand || "").trim(),
          why: String(why),
          price: formatRoutinePriceDisplay(p),
          priceValue: getRoutinePriceAmount(p),
          currency: String(p.currency || "INR").toUpperCase(),
          image: p.img || p.image || p.image_url || p.product_image || "",
          sku: String(p.sku || "").trim(),
          skuId: extractShopifyVariantIdFromRoutineProduct(p) || "",
          url: p.url || p.product_url || "",
          _apiProduct: p,
          _routineIndex: plan.products.length
        };
        stepProducts.push(product);
        plan.products.push(product);
      }
      if (!stepProducts.length) continue;
      section.steps.push({
        category: String(stepCategory),
        stepOrder: Number(step.stepOrder) || section.steps.length + 1,
        products: stepProducts
      });
    }
    plan.sections = plan.sections.filter(function (section) {
      return section.steps && section.steps.length;
    });
    return plan;
  }
  /*
    Body-care add-on helpers.

    The bodyExtra quiz answer is converted into a single "Beyond the Face"
    product after the core face routine is built. PIM data is preferred; the
    static config is only a fallback so the section can still render.
  */
  function getSelectedBodyExtraConfig() {
    var answer = getQuizAnswer("bodyExtra");
    if (!answer) return null;
    for (var i = 0; i < BODY_EXTRA_PRODUCT_OPTIONS.length; i++) {
      var config = BODY_EXTRA_PRODUCT_OPTIONS[i];
      var answers = config.answers || [];
      for (var a = 0; a < answers.length; a++) {
        if (answers[a] === answer) return config;
      }
    }
    return null;
  }
  function getBodyExtraWhyCopy(config) {
    var answer = getQuizAnswer("bodyExtra");
    if (answer === "Just my face for now") {
      return "Added as Asaya's bestseller-led body-care upsell.";
    }
    if (answer) {
      return "You said " + answer.toLowerCase() + " are your top body concern.";
    }
    return (
      normalizeRoutineWhySentence(config && config.why) ||
      "Picked from your body-care answer."
    );
  }
  function createBodyExtraRoutineProduct(config, index, resource) {
    if (resource) {
      var productFromPim = createRoutineProductFromPimResource(
        resource,
        "body-extra",
        "Body Extra",
        index
      );
      productFromPim.step = "Body Extra";
      productFromPim.timePeriod = "body-extra";
      productFromPim.why = getBodyExtraWhyCopy(config);
      productFromPim._bodyExtraKey = config.key;
      productFromPim._routineIndex = index;
      return productFromPim;
    }
    return {
      step: "Body Extra",
      stepOrder: 1,
      timePeriod: "body-extra",
      name: config.name,
      brand: "Asaya",
      why: getBodyExtraWhyCopy(config),
      price: formatRoutineMoney(config.priceValue, "INR"),
      priceValue: config.priceValue,
      currency: "INR",
      image: "",
      sku: "",
      skuId: "",
      url: config.url,
      _apiProduct: {
        title: config.name,
        name: config.name,
        brand: "Asaya",
        price: config.priceValue,
        currency: "INR",
        url: config.url,
        product_url: config.url
      },
      _bodyExtraKey: config.key,
      _routineIndex: index
    };
  }
  function getRecommendedSetWhyCopy() {
    var pigmentationWhy = formatConcernScanWhy(
      getDiagnosisConcernItem("pigmentation")
    );
    if (pigmentationWhy) return pigmentationWhy;
    if (getQuizAnswer("priority") === "Fade pigmentation") {
      return "Picked because you selected fade pigmentation as your top skincare goal.";
    }
    var hormonalWhy = getHormonalPigmentationWhy("pigmentation");
    if (hormonalWhy) return hormonalWhy;
    return "Picked to support visible tone and pigmentation care.";
  }
  function getDummyRecommendedSet() {
    return {
      name: "Anti Pigmentation Set",
      brand: "Recommended Set",
      why: getRecommendedSetWhyCopy(),
      price: formatRoutineMoney(4499, "INR"),
      priceValue: 4499,
      currency: "INR",
      image: "",
      _setKey: "anti-pigmentation-set"
    };
  }
  function appendBodyExtraToRoutinePlan(plan) {
    var config = getSelectedBodyExtraConfig();
    if (!config) return plan || { sections: [], products: [] };
    var existingProducts = (plan && plan.products) || [];
    for (var i = 0; i < existingProducts.length; i++) {
      if (existingProducts[i] && existingProducts[i]._bodyExtraKey) {
        return plan;
      }
    }
    var nextPlan = {
      sections: ((plan && plan.sections) || []).slice(),
      products: existingProducts.slice(),
      recommendedSet: plan && plan.recommendedSet,
      missingProducts: ((plan && plan.missingProducts) || []).slice(),
      _pimResources: (plan && plan._pimResources) || []
    };
    var lookup = createPimProductLookup(nextPlan._pimResources);
    var resource = findPimResourceByTitle(
      lookup,
      config.productTitle || config.name
    );
    if (lookup.missing && lookup.missing.length) {
      Array.prototype.push.apply(nextPlan.missingProducts, lookup.missing);
    }
    var product = createBodyExtraRoutineProduct(
      config,
      nextPlan.products.length,
      resource
    );
    nextPlan.products.push(product);
    nextPlan.sections.push({
      key: "body-extra",
      label: "Beyond the Face",
      steps: [
        {
          category: "",
          badgeLabel: "Body Care",
          stepOrder: 1,
          products: [product]
        }
      ]
    });
    return nextPlan;
  }
  /*
    PIM fetch boundary.

    This request intentionally fetches the full configured PIM page, then the
    local rule engine chooses the products. Success and error events are emitted
    for QA/debugging without blocking the user flow.
  */
  function fetchRoutineRecommendation() {
    var filterContext = getRoutineFilterContext();
    var request = {
      url: ROUTINE_RECOMMENDATION_URL,
      skin_type: filterContext.skinTypes,
      concerns: filterContext.concerns
    };
    return fetch(ROUTINE_RECOMMENDATION_URL, {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      },
      credentials: "omit"
    })
      .then(function (response) {
        return response.text().then(function (text) {
          var data = {};
          if (text) {
            try {
              data = JSON.parse(text);
            } catch (parseError) {
              data = {};
            }
          }
          if (!response.ok) {
            var msg =
              (data && (data.message || data.error)) ||
              text ||
              "PIM routine request failed";
            throw new Error(
              typeof msg === "string" ? msg : "PIM routine request failed"
            );
          }
          return data;
        });
      })
      .then(function (data) {
        var resources =
          data && Array.isArray(data.resources) ? data.resources : [];
        var plan = mapPimResourcesToRoutinePlan(resources, filterContext);
        dispatchCustomEvent("skin:routine-api-success", {
          request: request,
          resources: resources,
          filteredProducts: plan.products,
          recommendedSet: plan.recommendedSet,
          missingProducts: plan.missingProducts || [],
          discontinuedProducts: ASAYA_DISCONTINUED_PRODUCT_TITLES.slice(),
          page_stat: data && data.page_stat
        });
        return {
          plan: plan,
          routineType: "pim"
        };
      })
      .catch(function (error) {
        dispatchCustomEvent("skin:routine-api-error", {
          request: request,
          message: error && error.message ? error.message : String(error)
        });
        throw error;
      });
  }
  /*
    Routine rendering helpers.

    These return HTML strings only. They do not bind events. Event binding
    happens later in wireRoutineControls() so a full innerHTML replacement can
    safely rebuild the page.
  */
  function getRoutineTotal(products) {
    var total = 0;
    var currency = "";
    for (var i = 0; i < products.length; i++) {
      if (!currency && products[i].currency) currency = products[i].currency;
      total += Number(products[i].priceValue) || 0;
    }
    return formatRoutineMoney(total, currency || "INR");
  }
  function getRoutineIcon(name) {
    var icons = {
      cleanse:
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3c2.6 3 5 6.1 5 9.2A5 5 0 0 1 7 12.2C7 9.1 9.4 6 12 3Z"></path><path d="M9.5 13.5c.8 1.3 2.3 2 3.9 1.6"></path></svg>',
      serum:
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10 3h4"></path><path d="M11 3v4l-4.2 7.4A4.4 4.4 0 0 0 10.6 21h2.8a4.4 4.4 0 0 0 3.8-6.6L13 7V3"></path><path d="M8.4 15h7.2"></path></svg>',
      glow: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l1.4 5.1L18 10l-4.6 1.9L12 17l-1.4-5.1L6 10l4.6-1.9L12 3Z"></path><path d="M19 14l.6 2.1L22 17l-2.4.9L19 20l-.6-2.1L16 17l2.4-.9L19 14Z"></path></svg>',
      shield:
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l7 3v5.4c0 4.4-2.8 7.9-7 9.6-4.2-1.7-7-5.2-7-9.6V6l7-3Z"></path><path d="M9 12l2 2 4-4"></path></svg>',
      bag: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6.5 8h11l-.8 12h-9.4L6.5 8Z"></path><path d="M9 8a3 3 0 0 1 6 0"></path></svg>',
      products:
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 4h8v16H8z"></path><path d="M10 8h4"></path><path d="M10 12h4"></path></svg>',
      total:
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6h12"></path><path d="M8 10h8"></path><path d="M10 14h4"></path><path d="M12 18h.01"></path></svg>',
      link: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.1 0l2-2a5 5 0 0 0-7.1-7.1l-1.1 1.1"></path><path d="M14 11a5 5 0 0 0-7.1 0l-2 2A5 5 0 0 0 12 20.1l1.1-1.1"></path></svg>',
      copy: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 8h11v11H8z"></path><path d="M5 16H4V4h12v1"></path></svg>',
      message:
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5h14v10H8l-3 3V5Z"></path><path d="M8 9h8"></path><path d="M8 12h5"></path></svg>',
      reset:
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7v5h5"></path><path d="M5.6 12A7 7 0 1 0 8 6.7L4 10"></path></svg>'
    };
    return icons[name] || "";
  }
  function getRoutineSummaryHTML(products, total) {
  return (
    "" +
    '<section class="cg-routine-cart-summary" aria-label="Routine bag summary">' +
      '<div class="cg-routine-cart-heading">' +
        '<span class="cg-routine-cart-icon">' +
          getRoutineIcon("bag") +
        "</span>" +
        "<span>Routine Bag</span>" +
      "</div>" +

      '<div class="cg-routine-cart-grid">' +

        '<div class="cg-routine-cart-stat">' +
          '<span class="cg-routine-cart-stat-icon">' +
            getRoutineIcon("products") +
          "</span>" +
          '<span class="cg-routine-cart-label">Products</span>' +
          '<strong id="cg-routine-selected-count">0</strong>' +
        "</div>" +

        '<div class="cg-routine-cart-stat">' +
          '<span class="cg-routine-cart-stat-icon">' +
            getRoutineIcon("total") +
          "</span>" +
          '<span class="cg-routine-cart-label">Routine Total</span>' +
          '<strong id="cg-routine-selected-total">₹0</strong>' +
        "</div>" +

      "</div>" +
    "</section>"
  );
}
  function getRoutineProductThumbHTML(product) {
    if (product && product.image) {
      return (
        '<div class="cg-routine-product-thumb">' +
        '<img src="' +
        escapeAttr(product.image) +
        '" alt="' +
        escapeAttr(product.name) +
        '" loading="lazy" decoding="async" referrerPolicy="no-referrer">' +
        "</div>"
      );
    }
    var initial = String((product && product.name) || "P")
      .trim()
      .charAt(0);
    return (
      '<div class="cg-routine-product-thumb cg-routine-product-thumb--empty">' +
      "<span>" +
      escapeHTML(initial || "P") +
      "</span></div>"
    );
  }
  function getRoutineProductCardHTML(product, buttonAttrs, buttonLabel) {
    return (
      '<article class="cg-routine-card">' +
      getRoutineProductThumbHTML(product) +
      '<div class="cg-routine-product-copy">' +
      (product.brand
        ? '<div class="cg-routine-product-brand">' +
          escapeHTML(product.brand) +
          "</div>"
        : "") +
      '<h3 class="cg-routine-product-name">' +
      escapeHTML(product.name) +
      "</h3>" +
      '<p class="cg-routine-product-why">' +
      escapeHTML(product.why) +
      "</p>" +
      '<div class="cg-routine-product-meta">' +
      '<span class="cg-routine-price">' +
      escapeHTML(product.price) +
      "</span>" +
      '<button class="cg-routine-shop" type="button" ' +
      buttonAttrs +
      ">" +
      escapeHTML(buttonLabel || "Add to Cart") +
      "</button>" +
      "</div>" +
      "</div>" +
      "</article>"
    );
  }
  function getRoutineProductCards(products) {
    var cards = [];
    for (var i = 0; i < products.length; i++) {
      var product = products[i];
      cards.push(
        getRoutineProductCardHTML(
          product,
          'data-routine-product="' + product._routineIndex + '"',
          "Add to Cart"
        )
      );
    }
    return cards.join("");
  }
  function getRecommendedSetHTML(product) {
    if (!product) return "";
    return (
      '<section class="cg-routine-period cg-routine-recommended-set" aria-label="Recommended set">' +
      '<div class="cg-routine-period-header">' +
      '<h3 class="cg-routine-period-title">Recommended Set</h3>' +
      "</div>" +
      '<div class="cg-routine-product-list">' +
      getRoutineProductCardHTML(
  product,
  'data-routine-product="' +
    product._index +
    '" data-recommended-set="' +
    escapeAttr(product._setKey) +
    '"',
  "Add to Cart"
) +
      "</div>" +
      "</section>"
    );
  }
  function getBuildYourOwnDividerHTML() {
    return (
      '<div class="cg-routine-build-divider" role="separator" aria-label="Or build your own">' +
      "<span></span><strong>or build your own</strong><span></span>" +
      "</div>"
    );
  }
  function getRoutineStepGroupsHTML(steps) {
    var html = [];
    for (var i = 0; i < steps.length; i++) {
      var step = steps[i];
      var productCount = step.products ? step.products.length : 0;
      if (!productCount) continue;
      var badgeLabel = step.badgeLabel || "Step " + step.stepOrder;
      html.push(
        '<section class="cg-routine-step-group">' +
          '<div class="cg-routine-step-heading">' +
          '<div class="cg-routine-step-heading-main">' +
          '<span class="cg-routine-step-badge">' +
          escapeHTML(badgeLabel) +
          "</span>" +
          (step.category
            ? '<h3 class="cg-routine-step-title">' +
              escapeHTML(step.category) +
              "</h3>"
            : "") +
          "</div>" +
          '<span class="cg-routine-step-count">' +
          productCount +
          " product" +
          (productCount === 1 ? "" : "s") +
          "</span>" +
          "</div>" +
          '<div class="cg-routine-product-list">' +
          getRoutineProductCards(step.products) +
          "</div>" +
          "</section>"
      );
    }
    return html.join("");
  }
  function getRoutineSectionsHTML(plan) {
    var sections = (plan && plan.sections) || [];
    var html = [];
    for (var i = 0; i < sections.length; i++) {
      var section = sections[i];
      if (!section.steps || !section.steps.length) continue;
      html.push(
        '<section class="cg-routine-period" data-routine-period="' +
          escapeAttr(section.key) +
          '">' +
          '<div class="cg-routine-period-header">' +
          '<h3 class="cg-routine-period-title">' +
          escapeHTML(section.label) +
          "</h3>" +
          "</div>" +
          getRoutineStepGroupsHTML(section.steps) +
          "</section>"
      );
    }
    return html.join("");
  }
  function getRoutineEmptyHTML() {
    return (
      '<section class="cg-routine-empty" role="status">' +
      "<h3>No routine found for now.</h3>" +
      "</section>"
    );
  }
  function getRoutineShareButton(channel, label, icon) {
    return (
      '<button type="button" data-routine-share="' +
      escapeAttr(channel) +
      '">' +
      getRoutineIcon(icon) +
      "<span>" +
      escapeHTML(label) +
      "</span></button>"
    );
  }
  function getQuizProgressHTML(visibleQuestions, activeIndex) {
    var parts = [];
    var list = visibleQuestions || getVisibleQuizQuestions();
    var current = Number(activeIndex) || 0;
    for (var i = 0; i < list.length; i++) {
      var cls = i < current ? " done" : i === current ? " active" : "";
      parts.push('<span class="cg-quiz-bar' + cls + '"></span>');
    }
    return parts.join("");
  }
  function showResultAgain() {
    if (latestSkinResult && latestSkinResult.source) {
      renderSkinAnalysisResult(latestSkinResult.source);
    }
  }
  /*
    Quiz flow.

    renderQuizStep() renders the current visible question. Visibility is dynamic:
    the pregnancy/postpartum question is skipped unless gender is Female.
    wireQuizControls() stores answers and advances automatically for single
    choice questions.
  */
  function renderQuizStep() {
    var container = $("cg-report-container");
    if (!container) return;
    ensureVisibleQuizIndex();
    var visibleQuestions = getVisibleQuizQuestions();
    var question = quizQuestions[quizIndex] || quizQuestions[0];
    var total = visibleQuestions.length;
    var visibleIndex = getVisibleQuizPosition(question, visibleQuestions);
    var answer = getQuizAnswer(question.id);
    var optionsHtml = "";
    if (question.type === "single") {
      for (var i = 0; i < question.options.length; i++) {
        var option = question.options[i];
        optionsHtml +=
          '<button class="cg-quiz-option' +
          (answer === option ? " is-selected" : "") +
          '" type="button" data-value="' +
          escapeAttr(option) +
          '">' +
          escapeHTML(option) +
          "</button>";
      }
    }
    var fieldHtml =
      question.type === "text"
        ? '<input class="cg-quiz-input" id="cg-quiz-input" type="text" value="' +
          escapeAttr(answer || quizUserName) +
          '" placeholder="' +
          escapeAttr(question.placeholder || "") +
          '" autocomplete="given-name">'
        : '<div class="cg-quiz-options" id="cg-quiz-options">' +
          optionsHtml +
          "</div>";
    container.innerHTML =
      '<div class="cg-quiz-page">' +
      '<div class="cg-quiz-top">' +
      '<button class="cg-quiz-back" id="cg-quiz-back" type="button" aria-label="Go back">' +
      '<span aria-hidden="true">&larr;</span><span>Back</span>' +
      "</button>" +
      '<div class="cg-quiz-step">' +
      (visibleIndex + 1) +
      " OF " +
      total +
      "</div>" +
      "</div>" +
      '<div class="cg-quiz-shell">' +
      '<div class="cg-quiz-progress" style="--cg-quiz-count:' +
      total +
      '">' +
      getQuizProgressHTML(visibleQuestions, visibleIndex) +
      "</div>" +
      '<div class="cg-quiz-section">' +
      escapeHTML(question.section) +
      "</div>" +
      '<h2 class="cg-quiz-question">' +
      escapeHTML(question.question) +
      "</h2>" +
      '<div class="cg-quiz-reason">' +
      escapeHTML(question.reason) +
      "</div>" +
      fieldHtml +
      (question.type === "text"
        ? '<button class="cg-quiz-continue" id="cg-quiz-continue" type="button">Continue</button>'
        : "") +
      "</div>" +
      getPoweredFooterHTML("") +
      "</div>";
    resetStageScroll("cg-result-stage");
    wireQuizControls();
    scrollWrapperIntoView();
  }
  function showQuizStage() {
    var app = getAppNode();
    if (!app) return;
    setCameraMode(false);
    resetStageScroll("cg-result-stage");
    app.classList.remove("cg-show-intro");
    app.classList.remove("cg-camera-active");
    app.classList.add("cg-result-active");
    quizIndex = 0;
    quizAnswers = {};
    renderQuizStep();
    scrollWrapperIntoView();
  }
  function submitQuizText() {
    var question = quizQuestions[quizIndex];
    var input = $("cg-quiz-input");
    var value = input ? String(input.value || "").trim() : "";
    if (!value) {
      if (input) input.focus();
      return;
    }
    quizAnswers[question.id] = value.split(/\s+/)[0];
    quizUserName = quizAnswers[question.id];
    goToNextQuizStep();
  }
  function goToNextQuizStep() {
    var nextIndex = getNextVisibleQuizIndex(quizIndex);
    if (nextIndex !== -1) {
      quizIndex = nextIndex;
      renderQuizStep();
    } else {
      renderPersonalizedDiagnosis();
    }
  }
  function goToPreviousQuizStep() {
    var previousIndex = getPreviousVisibleQuizIndex(quizIndex);
    if (previousIndex !== -1) {
      quizIndex = previousIndex;
      renderQuizStep();
    } else {
      showResultAgain();
    }
  }
  function wireQuizControls() {
    var back = $("cg-quiz-back");
    if (back) back.onclick = goToPreviousQuizStep;
    var input = $("cg-quiz-input");
    if (input) {
      input.focus();
      input.onkeydown = function (event) {
        if (event.key === "Enter") submitQuizText();
      };
    }
    var next = $("cg-quiz-continue");
    if (next) next.onclick = submitQuizText;
    var options = $("cg-quiz-options");
    if (options) {
      options.onclick = function (event) {
        var target = event.target;
        while (
          target &&
          target !== options &&
          !target.classList.contains("cg-quiz-option")
        ) {
          target = target.parentNode;
        }
        if (!target || target === options) return;
        var question = quizQuestions[quizIndex];
        quizAnswers[question.id] =
          target.getAttribute("data-value") || target.textContent;
        if (question.id === "gender" && quizAnswers[question.id] !== "Female") {
          delete quizAnswers.hormonalFactors;
        }
        Array.prototype.forEach.call(
          options.querySelectorAll(".cg-quiz-option"),
          function (option) {
            option.classList.toggle("is-selected", option === target);
          }
        );
        setTimeout(goToNextQuizStep, 180);
      };
    }
  }
  /*
    Diagnosis screen renderer.

    This screen is the bridge between quiz answers and the routine request. It
    does not fetch products; it only shows the combined scan/quiz profile and
    wires the "See My Routine" CTA to renderRoutinePage().
  */
  function renderPersonalizedDiagnosis() {
    var container = $("cg-report-container");
    if (!container) return;
    var profile = getDiagnosisProfile();
    var primaryConcern = profile.concernDisplay || getPrimaryConcernName();
    var skinType = profile.skinTypeDisplay || getDiagnosisSkinType();
    var breakouts = profile.breakoutDisplay || getDiagnosisBreakoutText();
    var trigger = profile.triggerDisplay || getDiagnosisTriggerText();
    var firstName = quizUserName || getQuizAnswer("name") || "";
    var lead = firstName
      ? "For " +
        escapeHTML(firstName) +
        ", this plan focuses on " +
        escapeHTML(primaryConcern.toLowerCase()) +
        "."
      : "This plan focuses on " +
        escapeHTML(primaryConcern.toLowerCase()) +
        ".";
    container.innerHTML =
      '<div class="cg-diagnosis-page">' +
      '<div class="cg-diagnosis-hero">' +
      '<div class="cg-diagnosis-nav">' +
      '<button class="cg-diagnosis-back" id="cg-diagnosis-back" type="button" aria-label="Back to questions">' +
      '<span aria-hidden="true">&larr;</span><span>Back</span>' +
      "</button>" +
      "</div>" +
      '<h2 class="cg-diagnosis-title">Your Personalized<br>Diagnosis</h2>' +
      '<div class="cg-diagnosis-sub">Based on your scan + questionnaire</div>' +
      "</div>" +
      '<div class="cg-diagnosis-shell">' +
      '<section class="cg-diagnosis-card">' +
      '<div class="cg-diagnosis-row"><span>Primary Concerns</span><strong>' +
      escapeHTML(primaryConcern) +
      "</strong></div>" +
      '<div class="cg-diagnosis-row"><span>Trigger</span><strong>' +
      escapeHTML(trigger) +
      "</strong></div>" +
      '<div class="cg-diagnosis-row"><span>Skin Type</span><strong>' +
      escapeHTML(skinType) +
      "</strong></div>" +
      '<div class="cg-diagnosis-row"><span>Breakouts</span><strong>' +
      escapeHTML(breakouts) +
      "</strong></div>" +
      '<div class="cg-diagnosis-row"><span>Expected Results</span><strong>8-12 weeks</strong></div>' +
      "</section>" +
      '<section class="cg-diagnosis-tips">' +
      "<h3>Tips For Your Skin</h3>" +
      getDiagnosisTipsHTML(profile) +
      "</section>" +
      '<button class="cg-diagnosis-cta" id="cg-diagnosis-routine" type="button">See My Routine</button>' +
      '<div class="cg-diagnosis-note">' +
      lead +
      "</div>" +
      "</div>" +
      getPoweredFooterHTML("") +
      "</div>";
    var back = $("cg-diagnosis-back");
    if (back)
      back.onclick = function () {
        quizIndex = getLastVisibleQuizIndex();
        renderQuizStep();
      };
    var routine = $("cg-diagnosis-routine");
    if (routine)
      routine.onclick = function () {
        dispatchCustomEvent("skin:routine-requested", {
          result: latestSkinResult,
          answers: quizAnswers,
          recommendation: latestRecommendationPayload
        });
        renderRoutinePage();
      };
    resetStageScroll("cg-result-stage");
    scrollWrapperIntoView();
  }
  /*
    Final routine screen renderer.

    The plan has already been built from PIM before this method runs. This
    method appends the body-care add-on, renders the recommended set plus fixed
    routine sections, binds cart/share/start-over actions, and preloads variant
    IDs/cart state for faster add-to-cart clicks.
  */
  function renderRoutinePageWithPlan(plan) {
    var container = $("cg-report-container");
    if (!container) return;
    plan = appendBodyExtraToRoutinePlan(plan);
    var products = (plan && plan.products) || [];
    var total = getRoutineTotal(products);
    var skinType = getRoutineSkinTypeCopy();
    var primaryConcern = getPrimaryConcernName();
    var firstName = quizUserName || getQuizAnswer("name") || "";
    var title = firstName
      ? firstName + "'s Skincare Routine"
      : "Your Asaya Routine";
    var target = String(primaryConcern || "overall skin health").toLowerCase();
    var skin = String(skinType || "your").toLowerCase();
    var hasRoutine = products.length > 0;
    var recommendedSetHtml = getRecommendedSetHTML(plan.recommendedSet);
    var routineListHtml =
      recommendedSetHtml +
      (recommendedSetHtml ? getBuildYourOwnDividerHTML() : "") +
      (hasRoutine ? getRoutineSectionsHTML(plan) : getRoutineEmptyHTML());
    container.innerHTML =
      '<div class="cg-routine-page">' +
      '<div class="cg-result-nav cg-routine-nav" aria-label="Routine navigation">' +
      '<button class="cg-result-nav-btn cg-result-nav-back" id="cg-routine-back" type="button" aria-label="Back to diagnosis">' +
      "<span>&larr; Back</span>" +
      "</button>" +
      "</div>" +
      '<header class="cg-routine-header">' +
      '<h2 class="cg-routine-title">' +
      escapeHTML(title) +
      "</h2>" +
      '<p class="cg-routine-sub">Personalized for ' +
escapeHTML(skin) +
" skin targeting " +
escapeHTML(target) +
".</p>" +

(getQuizAnswer("hormonalFactors") === "Yes"
  ? '<p class="cg-routine-sub cg-routine-pregnancy-note">Our products are dermatologist-tested. If you’re pregnant or breastfeeding, we recommend checking with your dermatologist before introducing new products.</p>'
  : "") +
      "</header>" +
      '<main class="cg-routine-shell">' +
      '<div class="cg-routine-list">' +
      routineListHtml +
      "</div>" +
      (hasRoutine
        ? '<div class="cg-routine-summary">' +
          getRoutineSummaryHTML(products, total) +
          '<button class="cg-routine-main-cta" id="cg-routine-shop-all" type="button">Shop My Routine</button>' +
          "</div>" +
          '<section class="cg-routine-share" aria-label="Share your results">' +
          "<h3>Share Your Results</h3>" +
          "<p>Show your skin story</p>" +
          '<div class="cg-routine-share-actions">' +
          getRoutineShareButton("link", "Link", "link") +
          getRoutineShareButton("copy", "Copy", "copy") +
          getRoutineShareButton("message", "SMS", "message") +
          "</div>" +
          "</section>"
        : "") +
      '<div class="cg-routine-reset-row">' +
      '<button class="cg-routine-start-over" id="cg-routine-start-over" type="button">' +
      getRoutineIcon("reset") +
      "<span>Start Over</span></button>" +
      "</div>" +
      "</main>" +
      getPoweredFooterHTML("") +
      "</div>";
    wireRoutineControls(products, total, plan.recommendedSet);
    if (hasRoutine) preloadRoutineCartContext(products);
    resetStageScroll("cg-result-stage");
    scrollWrapperIntoView();
  }
  /*
    Routine loading state and async fetch wrapper.

    This is intentionally separate from renderRoutinePageWithPlan() so the user
    sees immediate feedback while PIM data loads. Fetch failures render an empty
    routine instead of leaving a stuck loading state.
  */
  function renderRoutinePage() {
    var container = $("cg-report-container");
    if (!container) return;
    var skinType = getRoutineSkinTypeCopy();
    var primaryConcern = getPrimaryConcernName();
    var firstName = quizUserName || getQuizAnswer("name") || "";
    var title = firstName
      ? firstName + "'s Asaya Routine"
      : "Your Asaya Routine";
    var target = String(primaryConcern || "overall skin health").toLowerCase();
    var skin = String(skinType || "your").toLowerCase();
    container.innerHTML =
      '<div class="cg-routine-page">' +
      '<div class="cg-result-nav cg-routine-nav" aria-label="Routine navigation">' +
      '<button class="cg-result-nav-btn cg-result-nav-back" id="cg-routine-back" type="button" aria-label="Back to diagnosis">' +
      "<span>&larr; Back</span>" +
      "</button>" +
      "</div>" +
      '<header class="cg-routine-header">' +
      '<h2 class="cg-routine-title">' +
      escapeHTML(title) +
      "</h2>" +
      '<p class="cg-routine-sub">Personalized for ' +
      escapeHTML(skin) +
      " skin targeting " +
      escapeHTML(target) +
      ".</p>" +
      "</header>" +
      '<main class="cg-routine-shell">' +
      '<p class="cg-routine-loading-hint" id="cg-routine-loading">Fetching your personalized routine…</p>' +
      "</main>" +
      getPoweredFooterHTML("") +
      "</div>";
    var back = $("cg-routine-back");
    if (back) {
      back.onclick = function () {
        renderPersonalizedDiagnosis();
      };
    }
    resetStageScroll("cg-result-stage");
    scrollWrapperIntoView();
   fetchRoutineRecommendation()
.then(async function (payload) {

    var products = payload.plan.products || [];

    await Promise.all(products.map(async function(product){

        var handle = getRoutineProductHandle(product);

        if(!handle) return;

        try{

            var response = await fetch("/products/" + handle + ".js");
            var shopifyProduct = await response.json();

            if(shopifyProduct.variants && shopifyProduct.variants.length){

                var variant = shopifyProduct.variants[0];

                product.priceValue = Number(variant.price) / 100;
                product.price = formatRoutineMoney(
                    Number(variant.price) / 100,
                    "INR"
                );

            }

        }catch(e){
            console.log(e);
        }

    }));
    
/* RECOMMENDED SET LIVE SHOPIFY PRICE */

var recommendedSet = payload.plan.recommendedSet;

if (recommendedSet) {

  var recommendedHandle =
    getRoutineProductHandle(recommendedSet);

  if (recommendedHandle) {

    try {

      var recommendedResponse =
        await fetch(
          "/products/" +
          recommendedHandle +
          ".js"
        );

      var recommendedShopifyProduct =
        await recommendedResponse.json();

      if (
        recommendedShopifyProduct.variants &&
        recommendedShopifyProduct.variants.length
      ) {

        var recommendedVariant =
          recommendedShopifyProduct.variants.find(
            function(variant) {
              return variant.available;
            }
          ) ||
          recommendedShopifyProduct.variants[0];

        if (recommendedVariant) {

          recommendedSet.priceValue =
            Number(recommendedVariant.price) / 100;

          recommendedSet.price =
            formatRoutineMoney(
              Number(recommendedVariant.price) / 100,
              "INR"
            );

          recommendedSet.skuId =
            String(recommendedVariant.id);

          setRoutineProductCartVariantId(
            recommendedSet,
            recommendedVariant.id
          );

        }

      }

    } catch (e) {

      console.log(
        "Recommended set price error:",
        e
      );

    }

  }

}
    renderRoutinePageWithPlan(payload.plan);

})
.catch(function () {
    renderRoutinePageWithPlan({ sections: [], products: [] });
});
  }
  /*
    Routine control binding.

    Because renderRoutinePageWithPlan() replaces container.innerHTML, handlers
    must be rebound every time the routine page is rendered.
  */

    function getSelectedRoutineProducts(products, recommendedSetProduct) {
  var selected = [];

  var buttons = document.querySelectorAll(
    ".cg-routine-shop.is-added[data-routine-product]"
  );

  for (var i = 0; i < buttons.length; i++) {
    var button = buttons[i];
    var product = null;

    // Recommended Set
    if (button.hasAttribute("data-recommended-set")) {
      product = recommendedSetProduct;
    } else {
      var index = Number(button.getAttribute("data-routine-product"));

      if (
        Number.isFinite(index) &&
        products &&
        products[index]
      ) {
        product = products[index];
      }
    }

    if (product && selected.indexOf(product) === -1) {
      selected.push(product);
    }
  }

  return selected;
}


function updateRoutineSelectedSummary(products, recommendedSetProduct) {
  var selectedProducts = getSelectedRoutineProducts(
    products,
    recommendedSetProduct
  );

  var countElement = document.getElementById(
    "cg-routine-selected-count"
  );

  var totalElement = document.getElementById(
    "cg-routine-selected-total"
  );

  var checkoutButton = document.getElementById(
    "cg-routine-shop-all"
  );

  var totalValue = 0;

  for (var i = 0; i < selectedProducts.length; i++) {
    totalValue += Number(selectedProducts[i].priceValue) || 0;
  }

  if (countElement) {
    countElement.textContent = selectedProducts.length;
  }

  if (totalElement) {
    totalElement.textContent = formatRoutineMoney(
      totalValue,
      "INR"
    );
  }

  if (checkoutButton) {
    checkoutButton.disabled = selectedProducts.length === 0;

    if (selectedProducts.length === 0) {
      checkoutButton.classList.add("is-disabled");
    } else {
      checkoutButton.classList.remove("is-disabled");
    }
  }

  return selectedProducts;
}


function checkoutSelectedRoutineProducts(
  products,
  recommendedSetProduct,
  button
) {

  var selectedProducts =
    getSelectedRoutineProducts(
      products,
      recommendedSetProduct
    );

  if (!selectedProducts.length) {
    return;
  }

  button.disabled = true;

  /*
    Get latest Shopify cart
  */
  fetch("/cart.js")
    .then(function(response) {
      return response.json();
    })

    .then(function(cartData) {

      routineCartDataCache = cartData;

      /*
        Refresh Pipeline cart with latest data
      */
      document.dispatchEvent(
        new CustomEvent("theme:cart:change", {
          bubbles: true,
          detail: {
            cart: cartData
          }
        })
      );

      /*
        Give cart UI one frame to update
      */
      requestAnimationFrame(function() {

        button.disabled = false;

        updateRoutineSelectedSummary(
          products,
          recommendedSetProduct
        );

        openCart();

      });

    })

    .catch(function(error) {

      console.error(
        "Unable to refresh cart:",
        error
      );

      button.disabled = false;

      openCart();

    });
}
  function wireRoutineControls(
  products,
  total,
  recommendedSetProduct
) {

  var back = $("cg-routine-back");

  if (back) {
    back.onclick = function () {
      renderPersonalizedDiagnosis();
    };
  }


  var startOver = $("cg-routine-start-over");

  if (startOver) {
    startOver.onclick = function () {

      dispatchCustomEvent(
        "skin:routine-start-over",
        {
          result: latestSkinResult,
          answers: quizAnswers
        }
      );

      showLandingStage();
    };
  }


  /*
  =====================================
  SHOP MY ROUTINE
  =====================================
  */

  var shopAll = $("cg-routine-shop-all");

  if (shopAll) {

    shopAll.onclick = function () {

      checkoutSelectedRoutineProducts(
        products,
        recommendedSetProduct,
        shopAll
      );

    };
  }


  /*
  =====================================
  NORMAL ROUTINE PRODUCTS
  =====================================
  */

  var productButtons =
    document.querySelectorAll(
      "[data-routine-product]:not([data-recommended-set])"
    );

  for (var i = 0; i < productButtons.length; i++) {

    productButtons[i].onclick = function () {

      var button = this;

      var idx = Number(
        button.getAttribute(
          "data-routine-product"
        )
      );

      var product = products[idx];

      if (!product) return;


      dispatchCustomEvent(
        "skin:routine-product-clicked",
        {
          product: product,
          index: idx,
          result: latestSkinResult,
          answers: quizAnswers
        }
      );


      /*
      REMOVE
      */

      if (
        button.classList.contains("is-added")
      ) {

        removeRoutineProductFromCart(
          product,
          button
        );

        var removeWatcher =
          setInterval(function () {

            if (!button.disabled) {

              clearInterval(removeWatcher);

              button.classList.remove(
                "is-added",
                "is-adding",
                "is-error"
              );

              button.textContent =
                "Add to Cart";

              updateRoutineSelectedSummary(
                products,
                recommendedSetProduct
              );

            }

          }, 100);

        return;
      }


      /*
      ADD
      */

      addRoutineProductToCart(
        product,
        button
      ).then(function (success) {

        if (success) {

          updateRoutineSelectedSummary(
            products,
            recommendedSetProduct
          );

        }

      });

    };
  }


  /*
  =====================================
  RECOMMENDED SET
  =====================================
  */

  var setButtons =
    document.querySelectorAll(
      "[data-recommended-set]"
    );

  for (var r = 0; r < setButtons.length; r++) {

    setButtons[r].onclick = function () {

      var button = this;

      if (!recommendedSetProduct) return;


      if (
        button.classList.contains("is-added")
      ) {

        removeRoutineProductFromCart(
          recommendedSetProduct,
          button
        );

        var removeWatcher =
          setInterval(function () {

            if (!button.disabled) {

              clearInterval(removeWatcher);

              button.classList.remove(
                "is-added",
                "is-adding",
                "is-error"
              );

              button.textContent =
                "Add to Cart";

              updateRoutineSelectedSummary(
                products,
                recommendedSetProduct
              );

            }

          }, 100);

        return;
      }


      addRoutineProductToCart(
        recommendedSetProduct,
        button
      ).then(function (success) {

        if (success) {

          updateRoutineSelectedSummary(
            products,
            recommendedSetProduct
          );

        }

      });

    };
  }


  /*
  =====================================
  SHARE
  =====================================
  */

  var shareButtons =
    document.querySelectorAll(
      "[data-routine-share]"
    );

  for (var s = 0; s < shareButtons.length; s++) {

    shareButtons[s].onclick = function () {

      dispatchCustomEvent(
        "skin:routine-share",
        {
          channel:
            this.getAttribute(
              "data-routine-share"
            ),

          result: latestSkinResult,
          answers: quizAnswers
        }
      );

    };
  }


  /*
  =====================================
  SYNC ROUTINE BUTTONS WITH REAL CART
  =====================================

  IMPORTANT:
  If product is removed from your custom
  cart drawer, this checks /cart.js and
  changes "Remove" -> "Add to Cart".
  */

  function syncRoutineWithRealCart() {

    fetch("/cart.js", {
      credentials: "same-origin",
      cache: "no-store"
    })

    .then(function (response) {
      return response.json();
    })

    .then(function (cart) {

      routineCartDataCache = cart;

      var cartVariantIds = {};

      for (
        var c = 0;
        c < cart.items.length;
        c++
      ) {

        var cartItem = cart.items[c];

        cartVariantIds[
          String(
            cartItem.variant_id ||
            cartItem.id
          )
        ] = true;

      }


      /*
      NORMAL PRODUCTS
      */

      var buttons =
        document.querySelectorAll(
          "[data-routine-product]:not([data-recommended-set])"
        );

      for (
        var b = 0;
        b < buttons.length;
        b++
      ) {

        (function (button) {

          var idx = Number(
            button.getAttribute(
              "data-routine-product"
            )
          );

          var product = products[idx];

          if (!product) return;


          resolveRoutineCartVariantId(product)
            .then(function (variantId) {

              var inCart =
                !!cartVariantIds[
                  String(variantId)
                ];


              if (inCart) {

                if (
                  !button.classList.contains(
                    "is-added"
                  )
                ) {

                  button.classList.remove(
                    "is-adding",
                    "is-error"
                  );

                  button.classList.add(
                    "is-added"
                  );

                  button.disabled = false;

                  button.textContent =
                    "Remove";

                }

              } else {

                /*
                THIS IS THE IMPORTANT PART

                Product removed from custom cart,
                so remove is-added from
                cg-routine-shop.
                */

                if (
                  button.classList.contains(
                    "is-added"
                  )
                ) {

                  button.classList.remove(
                    "is-added",
                    "is-adding",
                    "is-error"
                  );

                  button.disabled = false;

                  button.textContent =
                    "Add to Cart";

                }

              }

            })
            .catch(function () {});

        })(buttons[b]);

      }


      /*
      RECOMMENDED SET
      */

      var recommendedButton =
        document.querySelector(
          "[data-recommended-set]"
        );

      if (
        recommendedButton &&
        recommendedSetProduct
      ) {

        resolveRoutineCartVariantId(
          recommendedSetProduct
        )

        .then(function (variantId) {

          var inCart =
            !!cartVariantIds[
              String(variantId)
            ];


          if (inCart) {

            recommendedButton.classList.add(
              "is-added"
            );

            recommendedButton.classList.remove(
              "is-adding",
              "is-error"
            );

            recommendedButton.disabled =
              false;

            recommendedButton.textContent =
              "Remove";

          } else {

            recommendedButton.classList.remove(
              "is-added",
              "is-adding",
              "is-error"
            );

            recommendedButton.disabled =
              false;

            recommendedButton.textContent =
              "Add to Cart";

          }

        })

        .catch(function () {});

      }


      /*
      Update selected count + price +
      Shop My Routine button
      */

      setTimeout(function () {

        updateRoutineSelectedSummary(
          products,
          recommendedSetProduct
        );

      }, 50);

    })

    .catch(function (error) {

      console.error(
        "Routine cart sync error:",
        error
      );

    });

  }


  /*
  Initial sync
  */

  syncRoutineWithRealCart();


  /*
  Remove old watcher before creating
  another one when routine re-renders.
  */

  if (
    window.__cgRoutineCartSyncInterval
  ) {

    clearInterval(
      window.__cgRoutineCartSyncInterval
    );

  }


  /*
  Check actual Shopify cart every 600ms.

  So custom cart remove works even if
  your cart script fires NO custom event.
  */

  window.__cgRoutineCartSyncInterval =
    setInterval(function () {

      if (
        !document.querySelector(
          ".cg-routine-page"
        )
      ) {

        clearInterval(
          window.__cgRoutineCartSyncInterval
        );

        window.__cgRoutineCartSyncInterval =
          null;

        return;
      }


      syncRoutineWithRealCart();

    }, 600);


  /*
  INITIAL SUMMARY
  */

  updateRoutineSelectedSummary(
    products,
    recommendedSetProduct
  );

}
  function getRoutineProductButton(index) {
    return document.querySelector(
      '[data-routine-product="' + String(index) + '"]'
    );
  }
  function getRoutineProductIndex(product, fallbackIndex) {
    var idx =
      product && product._routineIndex != null
        ? Number(product._routineIndex)
        : Number(fallbackIndex);
    return Number.isFinite(idx) ? idx : Number(fallbackIndex) || 0;
  }
  function setRoutineMainButtonState(button, state) {
    if (!button) return;
    button.classList.remove("is-adding", "is-added", "is-error");
    if (state === "adding") {
      button.classList.add("is-adding");
      button.disabled = true;
      button.textContent = "Adding Routine...";
    } else if (state === "added") {
      button.classList.add("is-added");
      button.disabled = true;
      button.textContent = "Added";
    } else if (state === "error") {
      button.classList.add("is-error");
      button.disabled = false;
      button.textContent = "Try Again";
    } else {
      button.disabled = false;
      button.textContent = "Shop My Routine";
    }
  }
  function setRoutineProductButtonState(button, state) {
    if (!button) return;
    button.classList.remove("is-adding", "is-added", "is-error");
    if (state === "adding") {
      button.classList.add("is-adding");
      button.disabled = true;
      button.textContent = "Adding...";
    } else if (state === "added") {
      button.classList.add("is-added");
      button.disabled = false;
      button.textContent = "Remove";
    } else if (state === "error") {
      button.classList.add("is-error");
      button.disabled = false;
      button.textContent = "Try Again";
    } else {
      button.disabled = false;
      button.textContent = "Add to Cart";
    }
  }
  /*
    Adds a single routine product.

    The product may only have a URL or SKU when rendered, so the variant is
    resolved at click time. Button state and debug events are updated for every
    success/failure path.
  */
  function addRoutineProductToCart(product, button) {
    if (!product) return Promise.resolve(false);
    setRoutineProductButtonState(button, "adding");
    return resolveRoutineCartVariantId(product)
      .then(function (skuId) {
        if (!skuId) {
          setRoutineProductButtonState(button, "error");
          dispatchCustomEvent("skin:routine-product-add-error", {
            product: product,
            message: "missing-sku"
          });
          return false;
        }
        return addCartVariantQuietly(skuId, 1).then(function (success) {
          if (success) {
            setRoutineProductButtonState(button, "added");
            dispatchCustomEvent("skin:routine-product-added", {
              product: product,
              skuId: skuId
            });
            return true;
          }
          setRoutineProductButtonState(button, "error");
          dispatchCustomEvent("skin:routine-product-add-error", {
            product: product,
            skuId: skuId,
            message: "add-to-cart-failed"
          });
          return false;
        });
      })
      .catch(function () {
        setRoutineProductButtonState(button, "error");
        dispatchCustomEvent("skin:routine-product-add-error", {
          product: product,
          message: "variant-resolve-failed"
        });
        return false;
      });
  }
  function removeRoutineProductFromCart(product, button) {

  // Only affects REMOVE flow
  button.disabled = true;
  button.dataset.originalText = button.textContent;
  button.textContent = "Removing...";

  resolveRoutineCartVariantId(product)
    .then(function (variantId) {

      return fetch("/cart.js")
        .then(function (res) {
          return res.json();
        })
        .then(function (cart) {

          var item = cart.items.find(function (item) {
            return Number(item.variant_id) === Number(variantId);
          });

          if (!item) {
            throw new Error("Item not found in cart");
          }

          return fetch("/cart/change.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              id: item.key,
              quantity: 0
            })
          });

        });

    })
    .then(function () {

      button.disabled = false;
      button.classList.remove("is-added");
      button.textContent = "Add to Cart";

      if (typeof refreshCartDrawer === "function") {
        refreshCartDrawer();
      }

      if (typeof fetchCartData === "function") {
        fetchCartData();
      }

    })
    .catch(function (err) {

      console.error(err);

      button.disabled = false;
      button.textContent = button.dataset.originalText || "Remove";

    });

}
  /*
    Quiet batch add helper.

    Shopify /cart/update.js accepts target quantities, not deltas. This method
    reads the current cart once, groups duplicate variant IDs, adds the desired
    counts, and posts a single update payload.
  */
  function addRoutineProductsQuietly(products) {
    var list = Array.isArray(products) ? products : [];
    var resolvedVariantIds = [];
    return Promise.all(
      list.map(function (product) {
        return resolveRoutineCartVariantId(product).catch(function () {
          return "";
        });
      })
    )
      .then(function (variantIds) {
        resolvedVariantIds = variantIds;
        return getCartDataForQuietUpdate().then(function (cartData) {
          var counts = {};
          var updates = {};
          var keys = [];
          for (var i = 0; i < variantIds.length; i++) {
            var id = String(variantIds[i] || "").trim();
            if (!id) continue;
            if (!counts[id]) {
              counts[id] = 0;
              keys.push(id);
            }
            counts[id] += 1;
          }
          if (!keys.length) {
            return {
              success: false,
              results: variantIds.map(function () {
                return false;
              }),
              variantIds: variantIds,
              cartData: cartData
            };
          }
          for (var k = 0; k < keys.length; k++) {
            updates[keys[k]] =
              getCartVariantQuantity(cartData, keys[k]) + counts[keys[k]];
          }
          return updateCartQuantities(updates).then(function (nextCartData) {
            var results = variantIds.map(function (variantId) {
              return !!String(variantId || "").trim();
            });
            updateCartBadge(nextCartData.item_count, nextCartData);
            return {
              success: results.every(function (success) {
                return !!success;
              }),
              results: results,
              variantIds: variantIds,
              cartData: nextCartData
            };
          });
        });
      })
      .catch(function () {
        return {
          success: false,
          results: list.map(function () {
            return false;
          }),
          variantIds: resolvedVariantIds
        };
      });
  }
  /*
    "Shop My Routine" handler.

    Products already added individually are skipped, pending product buttons are
    marked as adding, and the cart is opened only when the full batch succeeds.
  */
  function addRoutineProductsToCart(products, total, button) {
    var list = Array.isArray(products) ? products : [];
    if (!list.length) {
      setRoutineMainButtonState(button, "error");
      return Promise.resolve(false);
    }
    setRoutineMainButtonState(button, "adding");
    dispatchCustomEvent("skin:routine-shop-all", {
      products: list,
      total: total,
      result: latestSkinResult,
      answers: quizAnswers
    });
    var results = [];
    var pendingProducts = [];
    var pendingIndexes = [];
    for (var i = 0; i < list.length; i++) {
      var product = list[i];
      var buttonIndex = getRoutineProductIndex(product, i);
      var productButton = getRoutineProductButton(buttonIndex);
      if (productButton && productButton.classList.contains("is-added")) {
        results[i] = true;
        continue;
      }
      setRoutineProductButtonState(productButton, "adding");
      pendingProducts.push(product);
      pendingIndexes.push(i);
    }
    if (!pendingProducts.length) {
      setRoutineMainButtonState(button, "added");
      dispatchCustomEvent("skin:routine-shop-all-added", {
        products: list,
        total: total,
        result: latestSkinResult,
        answers: quizAnswers,
        results: results
      });
      openCart();
      return Promise.resolve(true);
    }
    return addRoutineProductsQuietly(pendingProducts).then(function (payload) {
      var pendingResults = (payload && payload.results) || [];
      var variantIds = (payload && payload.variantIds) || [];
      for (var p = 0; p < pendingProducts.length; p++) {
        var originalIndex = pendingIndexes[p];
        var pendingProduct = pendingProducts[p];
        var pendingButtonIndex = getRoutineProductIndex(
          pendingProduct,
          originalIndex
        );
        var pendingButton = getRoutineProductButton(pendingButtonIndex);
        var success = !!pendingResults[p];
        var variantId = variantIds[p] || "";
        results[originalIndex] = success;
        setRoutineProductButtonState(
          pendingButton,
          success ? "added" : "error"
        );
        dispatchCustomEvent(
          success
            ? "skin:routine-product-added"
            : "skin:routine-product-add-error",
          {
            product: pendingProduct,
            skuId: variantId,
            message: success ? "" : "add-to-cart-failed"
          }
        );
      }
      var allAdded =
        results.length === list.length &&
        results.every(function (success) {
          return !!success;
        });
      setRoutineMainButtonState(button, allAdded ? "added" : "error");
      dispatchCustomEvent(
        allAdded
          ? "skin:routine-shop-all-added"
          : "skin:routine-shop-all-error",
        {
          products: list,
          total: total,
          result: latestSkinResult,
          answers: quizAnswers,
          results: results
        }
      );
      if (allAdded) {
        openCart();
      }
      return allAdded;
    });
  }
  /*
    Shared event bridge for QA, analytics, or client-side observers. Every
    dispatch is wrapped so unsupported CustomEvent environments do not break the
    user journey.
  */
  function dispatchCustomEvent(name, detail) {
    try {
      window.dispatchEvent(new CustomEvent(name, { detail: detail }));
    } catch (error) {}
  }
  /*
    Boltic sync and Kwikpass identity extraction.

    These helpers normalize phone/country values from cookies, localStorage,
    sessionStorage, JWTs, and Shopify/Kwikpass globals. The Boltic write is kept
    separate from result rendering so it can be enabled/disabled without
    changing the scan UI.
  */
  function resetBolticSkinSyncFlag() {
    bolticLoginRecordSynced = false;
    bolticLoginRecordSyncInFlight = false;
  }
  function jsonSafeCloneForBoltic(value) {
    if (value == null) return value;
    if (typeof value !== "object") return value;
    try {
      return JSON.parse(JSON.stringify(value));
    } catch (error) {
      return { _note: "non-serializable-sdk-payload", message: String(error) };
    }
  }
  function buildBolticSkinAnalysisMeta(rawSdkPayload) {
    var meta = {
      skinAnalysisResult: jsonSafeCloneForBoltic(rawSdkPayload),
      syncedAt: new Date().toISOString(),
      glamAppId: APP_ID
    };
    if (latestSkinScanMetadata) {
      meta["scan-metadata"] = jsonSafeCloneForBoltic(latestSkinScanMetadata);
    }
    return meta;
  }
  function normalizeCountryCode(value) {
    var code = String(value || "").trim();
    if (!code) return "";
    code = code.replace(/[^\d+]/g, "");
    if (!code) return "";
    code = code.replace(/\+/g, "");
    if (code.indexOf("00") === 0) code = code.slice(2);
    if (!code || code.length > 4) return "";
    return code;
  }
  function getStorageValueByKey(key) {
    try {
      if (typeof getValueFromCookiesOrLocalStorage === "function") {
        var helperValue = getValueFromCookiesOrLocalStorage(key);
        if (helperValue != null && String(helperValue).trim()) {
          return helperValue;
        }
      }
    } catch (e1) {}
    try {
      if (window.localStorage) {
        var localValue = window.localStorage.getItem(key);
        if (localValue != null && String(localValue).trim()) {
          return localValue;
        }
      }
    } catch (e2) {}
    try {
      if (window.sessionStorage) {
        var sessionValue = window.sessionStorage.getItem(key);
        if (sessionValue != null && String(sessionValue).trim()) {
          return sessionValue;
        }
      }
    } catch (e3) {}
    try {
      var cookies = String(document.cookie || "").split(";");
      for (var i = 0; i < cookies.length; i++) {
        var part = cookies[i].split("=");
        var cookieKey = decodeURIComponent(String(part.shift() || "").trim());
        if (cookieKey === key) {
          return decodeURIComponent(part.join("=").trim());
        }
      }
    } catch (e4) {}
    return "";
  }
  function readStorageCandidateValues(callback) {
    function shouldInspectKey(key) {
      return /(kwik|gokwik|kp|notify|phone|mobile|country|dial|customer|login|token)/i.test(
        String(key || "")
      );
    }
    function readWebStorage(storage) {
      if (!storage) return;
      try {
        for (var i = 0; i < storage.length; i++) {
          var key = storage.key(i);
          if (!shouldInspectKey(key)) continue;
          callback(key, storage.getItem(key));
        }
      } catch (error) {}
    }
    readWebStorage(window.localStorage);
    readWebStorage(window.sessionStorage);
    try {
      var cookies = String(document.cookie || "").split(";");
      for (var c = 0; c < cookies.length; c++) {
        var part = cookies[c].split("=");
        var cookieKey = decodeURIComponent(String(part.shift() || "").trim());
        if (!shouldInspectKey(cookieKey)) continue;
        callback(cookieKey, decodeURIComponent(part.join("=").trim()));
      }
    } catch (error2) {}
  }
  function parseJsonString(value) {
    var raw = String(value || "").trim();
    if (!raw) return null;
    if (
      !(
        (raw.charAt(0) === "{" && raw.charAt(raw.length - 1) === "}") ||
        (raw.charAt(0) === "[" && raw.charAt(raw.length - 1) === "]")
      )
    ) {
      return null;
    }
    try {
      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }
  function decodeJwtPayload(token) {
    var parts = String(token || "").split(".");
    if (parts.length < 2 || typeof atob !== "function") return null;
    try {
      var base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
      while (base64.length % 4) base64 += "=";
      var decoded = atob(base64);
      try {
        decoded = decodeURIComponent(
          decoded.replace(/(.)/g, function (match, ch) {
            return "%" + ("00" + ch.charCodeAt(0).toString(16)).slice(-2);
          })
        );
      } catch (unicodeError) {}
      return JSON.parse(decoded);
    } catch (error) {
      return null;
    }
  }
  function isCountryCodeStorageKey(key) {
    return /(country|dial|phone[_-]?code|calling[_-]?code)/i.test(
      String(key || "")
    );
  }
  function extractCountryCodeFromStorageCandidate(key, rawValue) {
    if (isCountryCodeStorageKey(key)) {
      return extractCountryCodeValue(rawValue, 0);
    }
    var parsed = parseJsonString(rawValue);
    if (parsed) {
      var parsedCountry = extractCountryCodeValue(parsed, 0);
      if (parsedCountry) return parsedCountry;
    }
    var tokenPayload = decodeJwtPayload(rawValue);
    return extractCountryCodeValue(tokenPayload, 0);
  }
  function extractCountryCodeValue(value, depth) {
    if (value == null || depth > 5) return "";
    if (typeof value === "number") return normalizeCountryCode(value);
    if (typeof value === "string") {
      var raw = value.trim();
      var parsed = parseJsonString(raw);
      if (parsed) {
        var parsedCountry = extractCountryCodeValue(parsed, depth + 1);
        if (parsedCountry) return parsedCountry;
      }
      return normalizeCountryCode(raw);
    }
    if (Array.isArray(value)) {
      for (var i = 0; i < value.length; i++) {
        var itemCountry = extractCountryCodeValue(value[i], depth + 1);
        if (itemCountry) return itemCountry;
      }
      return "";
    }
    if (typeof value === "object") {
      var keys = [
        "country_code",
        "countryCode",
        "dial_code",
        "dialCode",
        "phone_code",
        "phoneCode",
        "calling_code",
        "callingCode",
        "notify_country_code",
        "notifyCountryCode"
      ];
      for (var k = 0; k < keys.length; k++) {
        if (Object.prototype.hasOwnProperty.call(value, keys[k])) {
          var keyCountry = extractCountryCodeValue(value[keys[k]], depth + 1);
          if (keyCountry) return keyCountry;
        }
      }
      var nestedKeys = ["value", "data", "user", "customer", "profile"];
      for (var n = 0; n < nestedKeys.length; n++) {
        if (Object.prototype.hasOwnProperty.call(value, nestedKeys[n])) {
          var nestedCountry = extractCountryCodeValue(
            value[nestedKeys[n]],
            depth + 1
          );
          if (nestedCountry) return nestedCountry;
        }
      }
    }
    return "";
  }
  function extractPhoneValue(value, depth) {
    if (value == null || depth > 5) return "";
    if (typeof value === "number") return String(value);
    if (typeof value === "string") {
      var raw = value.trim();
      if (!raw) return "";
      if (
        (raw.charAt(0) === '"' && raw.charAt(raw.length - 1) === '"') ||
        (raw.charAt(0) === "'" && raw.charAt(raw.length - 1) === "'")
      ) {
        raw = raw.slice(1, -1).trim();
      }
      if (
        (raw.charAt(0) === "{" && raw.charAt(raw.length - 1) === "}") ||
        (raw.charAt(0) === "[" && raw.charAt(raw.length - 1) === "]")
      ) {
        try {
          var parsed = JSON.parse(raw);
          var parsedPhone = extractPhoneValue(parsed, depth + 1);
          if (parsedPhone) return parsedPhone;
        } catch (parseError) {}
      }
      return raw;
    }
    if (Array.isArray(value)) {
      for (var i = 0; i < value.length; i++) {
        var itemPhone = extractPhoneValue(value[i], depth + 1);
        if (itemPhone) return itemPhone;
      }
      return "";
    }
    if (typeof value === "object") {
      var country = normalizeCountryCode(
        value.country_code ||
          value.countryCode ||
          value.dial_code ||
          value.dialCode ||
          value.phone_code ||
          value.phoneCode ||
          value.calling_code ||
          value.callingCode ||
          value.notify_country_code ||
          value.notifyCountryCode ||
          ""
      );
      var localValue =
        value.national_number ||
        value.nationalNumber ||
        value.local_number ||
        value.localNumber ||
        value.phone_number ||
        value.phoneNumber ||
        value.phone ||
        value.mobile_number ||
        value.mobileNumber ||
        value.mobile ||
        value.number ||
        "";
      var localPhone = extractPhoneValue(localValue, depth + 1);
      if (country && localPhone) {
        if (String(localPhone).trim().charAt(0) === "+") return localPhone;
        return country + " " + localPhone;
      }
      var keys = [
        "phone_number",
        "phoneNumber",
        "phone",
        "mobile_number",
        "mobileNumber",
        "mobile",
        "number",
        "value",
        "data",
        "user",
        "customer",
        "profile",
        "notify_phone_number"
      ];
      for (var k = 0; k < keys.length; k++) {
        if (Object.prototype.hasOwnProperty.call(value, keys[k])) {
          var keyPhone = extractPhoneValue(value[keys[k]], depth + 1);
          if (keyPhone) return keyPhone;
        }
      }
    }
    return "";
  }
  function normalizeKwikpassPhoneNumber(value) {
    var phone = extractPhoneValue(value, 0);
    if (!phone) return "";
    phone = String(phone).trim();
    var digits = phone.replace(/\D/g, "");
    if (!digits) return "";
    return digits;
  }
  function combineCountryCodeWithPhone(countryCode, phoneValue) {
    var phone = normalizeKwikpassPhoneNumber(phoneValue);
    var country = normalizeCountryCode(countryCode);
    if (!phone) return "";
    if (!country) return phone;
    if (phone.indexOf(country) === 0) return phone;
    return country + phone.replace(/^0+/, "");
  }
  function getKwikpassCountryCodeFromStorage() {
    var exactKeys = [
      "notify_country_code",
      "notifyCountryCode",
      "notify_dial_code",
      "notifyDialCode",
      "country_code",
      "countryCode",
      "dial_code",
      "dialCode",
      "phone_code",
      "phoneCode",
      "calling_code",
      "callingCode",
      "kp_country_code",
      "gokwik_country_code"
    ];
    for (var i = 0; i < exactKeys.length; i++) {
      var exactCountry = extractCountryCodeValue(
        getStorageValueByKey(exactKeys[i]),
        0
      );
      if (exactCountry) return exactCountry;
    }
    var found = "";
    readStorageCandidateValues(function (key, rawValue) {
      if (found) return;
      found = extractCountryCodeFromStorageCandidate(key, rawValue);
    });
    if (found) return found;
    var tokenPayload = decodeJwtPayload(getKwikpassTokenValue());
    return extractCountryCodeValue(tokenPayload, 0);
  }
  function getKwikpassStructuredPhoneNumber() {
    var found = "";
    var tokenPayload = decodeJwtPayload(getKwikpassTokenValue());
    if (tokenPayload) {
      found = normalizeKwikpassPhoneNumber(tokenPayload);
      if (found) return found;
    }
    readStorageCandidateValues(function (key, rawValue) {
      if (found) return;
      var parsed = parseJsonString(rawValue);
      if (parsed) {
        found = normalizeKwikpassPhoneNumber(parsed);
        if (found) return;
      }
      var tokenPayload = decodeJwtPayload(rawValue);
      if (tokenPayload) {
        found = normalizeKwikpassPhoneNumber(tokenPayload);
      }
    });
    return found;
  }
  function getKwikpassPhoneNumber() {
    try {
      var storedPhone = getStorageValueByKey("notify_phone_number");
      var countryCode = getKwikpassCountryCodeFromStorage();
      if (countryCode && storedPhone) {
        var combinedPhone = combineCountryCodeWithPhone(
          countryCode,
          storedPhone
        );
        if (combinedPhone) return combinedPhone;
      }
      var structuredPhone = getKwikpassStructuredPhoneNumber();
      if (structuredPhone) {
        return countryCode
          ? combineCountryCodeWithPhone(countryCode, structuredPhone)
          : structuredPhone;
      }
      return normalizeKwikpassPhoneNumber(storedPhone);
    } catch (error) {
      return "";
    }
  }
  function createBolticLoginRecordWithPhone(phoneNumber, rawSdkPayload) {
    var phone = normalizeKwikpassPhoneNumber(phoneNumber);
    if (!phone || bolticLoginRecordSynced || bolticLoginRecordSyncInFlight) {
      return;
    }
    bolticLoginRecordSyncInFlight = true;
    var meta = buildBolticSkinAnalysisMeta(rawSdkPayload);
    fetch(BOLTIC_RECORDS_PROXY_URL, {
      method: "POST",
      headers: {
        accept: "application/json, text/plain, */*",
        "content-type": "application/json"
      },
      body: JSON.stringify({
        email: "",
        phone_number: phone,
        user_name: "",
        meta: meta
      }),
      credentials: "omit",
      mode: "cors"
    })
      .then(function (response) {
        return response.text().then(function (text) {
          var body = {};
          if (text) {
            try {
              body = JSON.parse(text);
            } catch (parseError) {
              body = { raw: text };
            }
          }
          if (!response.ok) {
            dispatchCustomEvent("skin:boltic-login-record-error", {
              status: response.status,
              body: body
            });
            return;
          }
          bolticLoginRecordSynced = true;
          dispatchCustomEvent("skin:boltic-login-record-success", {
            phone_number: phone,
            meta: meta,
            record: body
          });
        });
      })
      .catch(function (error) {
        dispatchCustomEvent("skin:boltic-login-record-error", {
          message: error && error.message ? error.message : String(error)
        });
      })
      .then(function () {
        bolticLoginRecordSyncInFlight = false;
      });
  }
  function syncBolticLoginRecordAfterLogin(
    rawSdkPayload,
    preferredPhoneNumber
  ) {
    var attempts = 0;
    var metadataWaitAttempts = 0;
    var preferredPhone = normalizeKwikpassPhoneNumber(preferredPhoneNumber);
    function trySync() {
      if (bolticLoginRecordSynced || bolticLoginRecordSyncInFlight) {
        return;
      }
      var phone = preferredPhone || getKwikpassPhoneNumber();
      if (phone) {
        if (!latestSkinScanMetadata && metadataWaitAttempts < 10) {
          metadataWaitAttempts += 1;
          window.setTimeout(trySync, 300);
          return;
        }

        createBolticLoginRecordWithPhone(phone, rawSdkPayload);
        return;
      }
      attempts += 1;
      if (attempts < 14) {
        window.setTimeout(trySync, 350);
        return;
      }
      dispatchCustomEvent("skin:boltic-login-record-error", {
        message: "missing-phone-number"
      });
    }
    trySync();
  }
  /*
    Login-state detection.

    Kwikpass is the primary signal, but Shopify customer globals/classes are
    also checked because themes and checkout/login apps expose different hooks.
  */
  function hasLoggedInCustomerSignal() {
    try {
      if (typeof getCustomerId === "function") {
        var cid = getCustomerId();
        if (
          cid != null &&
          String(cid).trim().length > 0 &&
          String(cid).trim() !== "0"
        ) {
          return true;
        }
      }
    } catch (e1) {}
    try {
      var shopCust = window.Shopify && window.Shopify.customer;
      if (shopCust && shopCust.id) {
        return true;
      }
    } catch (e2) {}
    try {
      var metaRoot = window.meta;
      if (metaRoot && metaRoot.customer) {
        if (metaRoot.customer.id || metaRoot.customer.email) {
          return true;
        }
      }
    } catch (e3) {}
    try {
      var saMeta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      if (saMeta && saMeta.page && saMeta.page.customerId) {
        return true;
      }
    } catch (e4) {}
    try {
      if (
        document.body &&
        document.body.classList.contains("customer-logged-in")
      ) {
        return true;
      }
      if (
        document.documentElement &&
        document.documentElement.classList.contains("customer-logged-in")
      ) {
        return true;
      }
    } catch (e5) {}
    try {
      var loggedMeta = document.querySelector(
        'meta[name="logged-in"][content="true"],meta[name="customer-logged-in"][content="true"]'
      );
      if (loggedMeta) {
        return true;
      }
    } catch (e6) {}
    return false;
  }
  function getKwikpassTokenValue() {
    try {
      var merchantInfo = window.merchantInfo;
      if (
        !merchantInfo ||
        merchantInfo.environment == null ||
        typeof XGokwikCoreToken !== "function" ||
        typeof getValueFromCookiesOrLocalStorage !== "function"
      ) {
        return "";
      }
      var token = getValueFromCookiesOrLocalStorage(
        XGokwikCoreToken(merchantInfo.environment)
      );
      return token ? String(token).trim() : "";
    } catch (error) {
      return "";
    }
  }
  function hasKwikpassLoginSignal() {
    var token = getKwikpassTokenValue();
    return !!token;
  }
  function isKwikpassFullyLoggedIn() {
    try {
      var merchantInfo = window.merchantInfo;
      if (!merchantInfo || merchantInfo.environment == null) {
        return true;
      }
      if (
        typeof XGokwikCoreToken !== "function" ||
        typeof getValueFromCookiesOrLocalStorage !== "function"
      ) {
        return true;
      }
      var token = getValueFromCookiesOrLocalStorage(
        XGokwikCoreToken(merchantInfo.environment)
      );
      if (!token) {
        return hasLoggedInCustomerSignal();
      }
      return hasKwikpassLoginSignal();
    } catch (error) {
      return true;
    }
  }
  /*
    Skin-analysis login gate.

    When a result arrives for an anonymous shopper, the result page is rendered
    but locked. The gate opens Kwikpass, watches DOM/network/storage signals,
    unlocks the result after login, or returns home if the popup is abandoned.
  */
  function setSkinAnalysisLoginGateScrollLock(on) {
    var cn = "cg-skin-login-gate-active";
    if (on) {
      document.documentElement.classList.add(cn);
      document.body.classList.add(cn);
    } else {
      document.documentElement.classList.remove(cn);
      document.body.classList.remove(cn);
    }
  }
  function resetSkinAnalysisLoginPopupState() {
    skinAnalysisLoginPopupObserved = false;
    skinAnalysisLoginPopupMissingTicks = 0;
    if (skinAnalysisLoginOpenTimerId != null) {
      window.clearTimeout(skinAnalysisLoginOpenTimerId);
      skinAnalysisLoginOpenTimerId = null;
    }
  }
  function stopSkinAnalysisBodyClassObserver() {
    if (skinAnalysisLoginBodyObserver) {
      skinAnalysisLoginBodyObserver.disconnect();
      skinAnalysisLoginBodyObserver = null;
    }
  }
  function startSkinAnalysisBodyClassObserver() {
    stopSkinAnalysisBodyClassObserver();
    if (!window.MutationObserver || !document.body) {
      return;
    }
    var mo = new MutationObserver(function () {
      tryUnlockSkinAnalysisIfLoggedIn();
    });
    mo.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"]
    });
    mo.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"]
    });
    skinAnalysisLoginBodyObserver = mo;
  }
  /*
    Network unlock hooks.

    Some Kwikpass flows update login state through async cart/customer requests
    without changing body classes. These hooks observe successful relevant
    fetch/XHR calls and re-check login shortly after them.
  */
  function ensureSkinAnalysisNetworkUnlockHooks() {
    if (window.__cgSkinNetworkUnlockHooked) {
      return;
    }
    window.__cgSkinNetworkUnlockHooked = true;
    var nf = window.fetch;
    if (typeof nf === "function") {
      window.fetch = function () {
        var args = arguments;
        return nf.apply(window, args).then(function (response) {
          try {
            if (
              skinAnalysisLoginGateActive &&
              response &&
              response.ok &&
              typeof args[0] === "string"
            ) {
              var u = args[0];
              if (
                u.indexOf("/cart") !== -1 ||
                u.indexOf("cart.js") !== -1 ||
                u.indexOf("update.js") !== -1 ||
                u.indexOf("customer") !== -1
              ) {
                window.setTimeout(function () {
                  tryUnlockSkinAnalysisIfLoggedIn();
                }, 350);
                window.setTimeout(function () {
                  tryUnlockSkinAnalysisIfLoggedIn();
                }, 900);
              }
            }
          } catch (eFetch) {}
          return response;
        });
      };
    }
    if (typeof XMLHttpRequest !== "undefined" && XMLHttpRequest.prototype) {
      var xopen = XMLHttpRequest.prototype.open;
      var xsend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.open = function (method, url) {
        try {
          this.__cgSkinUrl = typeof url === "string" ? url : "";
        } catch (eO) {
          this.__cgSkinUrl = "";
        }
        return xopen.apply(this, arguments);
      };
      XMLHttpRequest.prototype.send = function () {
        var xhr = this;
        xhr.addEventListener(
          "load",
          function () {
            try {
              if (
                !skinAnalysisLoginGateActive ||
                xhr.status < 200 ||
                xhr.status >= 300
              ) {
                return;
              }
              var u = xhr.__cgSkinUrl || "";
              if (
                u.indexOf("/cart") !== -1 ||
                u.indexOf("cart.js") !== -1 ||
                u.indexOf("update.js") !== -1 ||
                u.indexOf("customer") !== -1
              ) {
                window.setTimeout(function () {
                  tryUnlockSkinAnalysisIfLoggedIn();
                }, 350);
                window.setTimeout(function () {
                  tryUnlockSkinAnalysisIfLoggedIn();
                }, 900);
              }
            } catch (eX) {}
          },
          false
        );
        return xsend.apply(this, arguments);
      };
    }
  }
  function stopSkinAnalysisLoginUnlockWatch() {
    if (skinAnalysisLoginPollId != null) {
      window.clearInterval(skinAnalysisLoginPollId);
      skinAnalysisLoginPollId = null;
    }
    stopSkinAnalysisBodyClassObserver();
  }
  function isVisibleElement(node) {
    if (!node || node.nodeType !== 1) return false;
    var style = window.getComputedStyle ? window.getComputedStyle(node) : null;
    if (
      style &&
      (style.display === "none" ||
        style.visibility === "hidden" ||
        style.pointerEvents === "none")
    ) {
      return false;
    }
    if (style) {
      var opacity = parseFloat(style.opacity);
      if (Number.isFinite(opacity) && opacity < 0.05) return false;
    }
    var rect = node.getBoundingClientRect && node.getBoundingClientRect();
    if (!rect) return false;
    return rect.width > 8 && rect.height > 8;
  }
  function isKwikpassLoginTriggerNode(node) {
    if (!node || node.nodeType !== 1) return false;
    var id = String(node.id || "");
    return (
      id === "kp-login-button-header-logo-mobile" ||
      id === "kp-login-button-header-logo" ||
      id === "kp-login-button-header" ||
      id === "gokwik-login-button"
    );
  }
  function isKwikpassPopupCandidate(node) {
    if (!node || node.nodeType !== 1 || isKwikpassLoginTriggerNode(node)) {
      return false;
    }
    var tag = String(node.tagName || "").toUpperCase();
    if (tag === "BUTTON" || tag === "A") return false;
    var text = (
      String(node.id || "") +
      " " +
      String(node.className || "") +
      " " +
      String(node.getAttribute("src") || "") +
      " " +
      String(node.getAttribute("name") || "") +
      " " +
      String(node.getAttribute("aria-label") || "")
    ).toLowerCase();
    if (!/(kwik|gokwik|kp)/.test(text)) return false;
    if (tag === "IFRAME") return true;
    return /(modal|popup|iframe|overlay|login|sso|auth|drawer|container|wrapper)/.test(
      text
    );
  }
  function isKwikpassLoginPopupVisible() {
    var nodes = document.querySelectorAll("iframe,[id],[class]");
    for (var i = 0; i < nodes.length; i++) {
      if (isKwikpassPopupCandidate(nodes[i]) && isVisibleElement(nodes[i])) {
        return true;
      }
    }
    return false;
  }
  function updateSkinAnalysisLoginPopupState() {
    if (!skinAnalysisLoginGateActive) return;
    if (isKwikpassFullyLoggedIn()) {
      unlockSkinAnalysisAfterLogin();
      return;
    }
    var popupVisible = isKwikpassLoginPopupVisible();
    if (popupVisible) {
      skinAnalysisLoginPopupObserved = true;
      skinAnalysisLoginPopupMissingTicks = 0;
      return;
    }
    if (!skinAnalysisLoginPopupObserved) return;
    skinAnalysisLoginPopupMissingTicks += 1;
    if (
      skinAnalysisLoginPopupMissingTicks >=
      SKIN_LOGIN_POPUP_MISSING_TICKS_TO_ABANDON
    ) {
      abandonSkinAnalysisLoginGateToHome();
    }
  }
  function tryUnlockSkinAnalysisIfLoggedIn() {
    if (!skinAnalysisLoginGateActive) {
      return;
    }
    if (isKwikpassFullyLoggedIn()) {
      unlockSkinAnalysisAfterLogin();
      return;
    }
    updateSkinAnalysisLoginPopupState();
  }
  function startSkinAnalysisLoginUnlockWatch() {
    stopSkinAnalysisLoginUnlockWatch();
    ensureSkinAnalysisNetworkUnlockHooks();
    startSkinAnalysisBodyClassObserver();
    skinAnalysisLoginPollId = window.setInterval(function () {
      tryUnlockSkinAnalysisIfLoggedIn();
    }, SKIN_LOGIN_POPUP_POLL_INTERVAL_MS);
  }
  function clickVisibleKwikpassLoginTrigger() {
    var ids = [
      "kp-login-button-header-logo-mobile",
      "kp-login-button-header-logo",
      "kp-login-button-header",
      "gokwik-login-button"
    ];
    for (var pass = 0; pass < 2; pass++) {
      for (var i = 0; i < ids.length; i++) {
        var el = document.getElementById(ids[i]);
        if (!el || typeof el.click !== "function") {
          continue;
        }
        if (pass === 0) {
          var st = window.getComputedStyle(el);
          if (
            st.display === "none" ||
            st.visibility === "hidden" ||
            st.pointerEvents === "none"
          ) {
            continue;
          }
          var op = parseFloat(st.opacity);
          if (Number.isFinite(op) && op < 0.05) {
            continue;
          }
          var rect = el.getBoundingClientRect();
          if (rect.width < 2 || rect.height < 2) {
            continue;
          }
        } else {
          var st2 = window.getComputedStyle(el);
          if (st2.display === "none") {
            continue;
          }
        }
        el.click();
        return true;
      }
    }
    return false;
  }
  function openKwikpassLoginForSkinAnalysis() {
    skinAnalysisLoginPopupMissingTicks = 0;
    if (clickVisibleKwikpassLoginTrigger()) {
      return true;
    }
    try {
      if (typeof handleKpAndShopifyLogin === "function") {
        handleKpAndShopifyLogin(
          (window.location.pathname || "/") + (window.location.search || "")
        );
        return true;
      }
    } catch (e1) {}
    try {
      if (typeof openIframe === "function") {
        openIframe("kp_sso_token");
        return true;
      }
    } catch (e2) {}
    return false;
  }
  function openKwikpassLoginForSkinAnalysisSoon() {
    if (skinAnalysisLoginOpenTimerId != null) {
      window.clearTimeout(skinAnalysisLoginOpenTimerId);
    }
    skinAnalysisLoginOpenTimerId = window.setTimeout(function () {
      skinAnalysisLoginOpenTimerId = null;
      if (!skinAnalysisLoginGateActive || isKwikpassFullyLoggedIn()) {
        return;
      }
      if (!openKwikpassLoginForSkinAnalysis()) {
        abandonSkinAnalysisLoginGateToHome();
        return;
      }
      window.setTimeout(function () {
        updateSkinAnalysisLoginPopupState();
      }, 250);
    }, 120);
  }
  function clearSkinAnalysisLoginGateState() {
    skinAnalysisLoginGateActive = false;
    pendingSkinAnalysisPayloadForLogin = null;
    stopSkinAnalysisLoginUnlockWatch();
    resetSkinAnalysisLoginPopupState();
    setSkinAnalysisLoginGateScrollLock(false);
    var gated = document.querySelector(".cg-result-page--login-gated");
    if (gated) {
      gated.classList.remove("cg-result-page--login-gated");
    }
  }
  function abandonSkinAnalysisLoginGateToHome() {
    closeSDKCanvasForStageExit("skin analysis login abandoned");
    resetAnalysisFlow();
    setCgLoading(false);
    showLandingStage();
  }
  /*
    Unlocks the already-rendered result page after Kwikpass login succeeds.
    This does not re-run the SDK result; it restores the stored payload, refreshes
    annotations/recommendations, and publishes the latest result globally.
  */
  function unlockSkinAnalysisAfterLogin() {
    if (!skinAnalysisLoginGateActive) {
      return;
    }
    var payload = pendingSkinAnalysisPayloadForLogin;
    var phone = getKwikpassPhoneNumber();
    stopSkinAnalysisLoginUnlockWatch();
    resetSkinAnalysisLoginPopupState();
    setSkinAnalysisLoginGateScrollLock(false);
    var page = document.querySelector(".cg-result-page--login-gated");
    if (page) {
      page.classList.remove("cg-result-page--login-gated");
    }
    skinAnalysisLoginGateActive = false;
    pendingSkinAnalysisPayloadForLogin = null;
    renderResultAnnotations(selectedConcernIdx || 0);
    renderRecommendationPanel(latestRecommendationPayload);
    dispatchCustomEvent("skin:result", latestSkinResult);
    window.__CG_LATEST_SKIN_ANALYSIS = {
      result: latestSkinResult,
      recommendation: latestRecommendationPayload,
      scanMetadata: latestSkinScanMetadata
    };
    //keeping it disabled for now
    // syncBolticLoginRecordAfterLogin(payload, phone);
  }
  function ensureKwikpassLoginGateListener() {
    if (kwikpassLoginListenerBound) {
      return;
    }
    kwikpassLoginListenerBound = true;
    window.addEventListener("update-dom", function () {
      window.setTimeout(function () {
        tryUnlockSkinAnalysisIfLoggedIn();
      }, 200);
    });
    window.addEventListener(
      "focus",
      function () {
        window.setTimeout(function () {
          tryUnlockSkinAnalysisIfLoggedIn();
          updateSkinAnalysisLoginPopupState();
        }, 120);
      },
      true
    );
    document.addEventListener("visibilitychange", function () {
      if (document.visibilityState !== "visible") {
        return;
      }
      window.setTimeout(function () {
        tryUnlockSkinAnalysisIfLoggedIn();
        updateSkinAnalysisLoginPopupState();
      }, 120);
    });
  }
  function showResultStage() {
    var app = getAppNode();
    if (!app) return;
    setCameraMode(false);
    resetStageScroll("cg-result-stage");
    app.classList.remove("cg-show-intro");
    app.classList.remove("cg-camera-active");
    app.classList.add("cg-result-active");
    clearStartupTimeout();
    clearAnalysisStartRetry();
    setCgLoading(false);
    scrollWrapperIntoView();
  }
  /*
    Result page renderer.

    This is called from SDK result events or postMessage fallback payloads. It
    normalizes the SDK data, builds concern cards/images, closes the SDK canvas,
    and decides whether to show the login gate or publish the result directly.
  */
  function renderSkinAnalysisResult(value) {
    var result = normalizeSkinResult(value);
    if (!result) return;
    latestSkinResult = result;
    reportConcerns = result.concerns.length
      ? result.concerns
      : [
          {
            key: "overall",
            name: "Overall Skin",
            value: result.score,
            severity: getSeverityLabel(result.health, result.score),
            severityClass: getSeverityClass(result.health, result.score),
            description: result.summary,
            image: result.faceImage,
            regions: getDefaultRegions("overall")
          }
        ];
    selectedConcernIdx = 0;
    var needsLoginGate = !isKwikpassFullyLoggedIn();
    var healthLabel = getHealthLabel(result.score, result.health);
    var topConcern = reportConcerns[0];
    var circumference = 2 * Math.PI * 48;
    var offset = circumference - (result.score / 100) * circumference;
    var scoreText =
      "Your overall skin health is <strong>" +
      escapeHTML(healthLabel.toLowerCase()) +
      "</strong>. Your biggest opportunity right now is <strong>" +
      escapeHTML(topConcern.name.toLowerCase()) +
      "</strong> - review each concern image below for a closer look.";
    var concernsHtml = [];
    for (var i = 0; i < reportConcerns.length; i++) {
      var concern = reportConcerns[i];
      var sevClass = concern.severityClass;
      concernsHtml.push(
        '<button class="cg-result-item' +
          (i === 0 ? " is-active" : "") +
          '" type="button" data-idx="' +
          i +
          '">' +
          '<span class="cg-result-item-top">' +
          '<span class="cg-result-status ' +
          sevClass +
          '"></span>' +
          '<span class="cg-result-item-name">' +
          escapeHTML(concern.name) +
          "</span>" +
          "</span>" +
          '<span class="cg-result-item-score-row">' +
          '<span class="cg-result-item-score">' +
          escapeHTML(concern.value) +
          "</span>" +
          '<span class="cg-result-item-max">/ 100</span>' +
          "</span>" +
          '<span class="cg-result-bar"><span class="cg-result-fill ' +
          sevClass +
          '" style="width:' +
          concern.value +
          '%"></span></span>' +
          '<span class="cg-result-sev ' +
          sevClass +
          '">' +
          escapeHTML(concern.severity) +
          "</span>" +
          "</button>"
      );
    }
    var imageSrc = getConcernImage(topConcern, result);
    var html =
      '<div class="cg-result-page' +
      (needsLoginGate ? " cg-result-page--login-gated" : "") +
      '">' +
      '<div class="cg-result-nav" aria-label="Result navigation">' +
      '<button class="cg-result-nav-btn cg-result-nav-back" id="cg-result-back" type="button" aria-label="Back to tips">' +
      "<span>&larr; Back</span>" +
      "</button>" +
      '<button class="cg-result-nav-btn cg-result-nav-close" id="cg-result-close" type="button" aria-label="Close skin analysis">' +
      "<span>Close</span>" +
      "</button>" +
      "</div>" +
      '<div class="cg-result-header">' +
      '<h2 class="cg-result-title">Your Skin<br>Analysis</h2>' +
      '<div class="cg-result-subtitle">AI-powered results - ' +
      reportConcerns.length +
      " parameters</div>" +
      "</div>" +
      '<div class="cg-result-shell">' +
      '<section class="cg-result-card cg-result-overview">' +
      '<div class="cg-result-overview-row">' +
      '<div class="cg-result-ring">' +
      '<svg viewBox="0 0 108 108" aria-hidden="true">' +
      '<circle class="cg-result-ring-bg" cx="54" cy="54" r="48"></circle>' +
      '<circle class="cg-result-ring-fill" cx="54" cy="54" r="48" stroke-dasharray="' +
      circumference +
      '" stroke-dashoffset="' +
      offset +
      '"></circle>' +
      "</svg>" +
      '<div class="cg-result-score-center">' +
      '<div class="cg-result-score">' +
      escapeHTML(result.score) +
      "</div>" +
      '<div class="cg-result-score-label">' +
      escapeHTML(healthLabel) +
      "</div>" +
      "</div>" +
      "</div>" +
      '<div class="cg-result-meta">' +
      '<div class="cg-result-kicker">Overall Skin Score</div>' +
      '<div class="cg-result-health">' +
      escapeHTML(healthLabel) +
      "</div>" +
      '<div class="cg-result-pill-row">' +
      '<span class="cg-result-pill"><span>Age</span>' +
      escapeHTML(result.skinAge) +
      "</span>" +
      '<span class="cg-result-pill"><span>Type</span>' +
      escapeHTML(result.skinType) +
      "</span>" +
      (result.skinTone
        ? '<span class="cg-result-pill"><span>Tone</span>' +
          escapeHTML(result.skinTone) +
          "</span>"
        : "") +
      "</div>" +
      "</div>" +
      "</div>" +
      '<div class="cg-result-copy">' +
      scoreText +
      "</div>" +
      "</section>" +
      '<section class="cg-result-card cg-result-face-card">' +
      '<div class="cg-result-face-header">' +
      '<div class="cg-result-section-label">Concern Image</div>' +
      '<div class="cg-result-face-name" id="cg-result-face-name">' +
      escapeHTML(topConcern.name) +
      "</div>" +
      "</div>" +
      '<div class="cg-result-face-wrap">' +
      '<img class="cg-result-face-img" id="cg-result-face-img" src="' +
      escapeAttr(imageSrc) +
      '" alt="' +
      escapeAttr(topConcern.name) +
      ' analysis image" referrerPolicy="no-referrer">' +
      "</div>" +
      "</section>" +
      '<section class="cg-result-concerns">' +
      '<div class="cg-result-section-row">' +
      '<div class="cg-result-section-label">Detected Concerns</div>' +
      '<div class="cg-result-concern-meta"><span id="cg-result-selected-label">' +
      escapeHTML(topConcern.name) +
      "</span><span>" +
      reportConcerns.length +
      " concerns</span></div>" +
      "</div>" +
      '<div class="cg-result-scroll" id="cg-result-scroll">' +
      concernsHtml.join("") +
      "</div>" +
      "</section>" +
      '<section class="cg-result-card cg-result-desc">' +
      '<h3 class="cg-result-desc-title" id="cg-result-desc-title">' +
      escapeHTML(topConcern.name) +
      "</h3>" +
      '<div class="cg-result-desc-text" id="cg-result-desc-text">' +
      getConcernDetailHTML(topConcern) +
      "</div>" +
      "</section>" +
      '<section class="cg-result-recommendations" id="cg-result-recommendations" hidden></section>' +
      '<div class="cg-result-bottom">' +
      '<button class="cg-result-cta" id="cg-result-cta" type="button">Continue to Personalize</button>' +
      '<div class="cg-result-cta-sub">Answer a few questions for your custom routine</div>' +
      "</div>" +
      "</div>" +
      getPoweredFooterHTML("") +
      "</div>";
    var container = $("cg-report-container");
    if (container) container.innerHTML = html;
    closeSDKAfterReportReady();
    showResultStage();
    wireResultControls();
    if (needsLoginGate) {
      skinAnalysisLoginGateActive = true;
      pendingSkinAnalysisPayloadForLogin = value;
      resetSkinAnalysisLoginPopupState();
      setSkinAnalysisLoginGateScrollLock(true);
      ensureKwikpassLoginGateListener();
      startSkinAnalysisLoginUnlockWatch();
      renderResultAnnotations(0);
      renderRecommendationPanel(latestRecommendationPayload);
      openKwikpassLoginForSkinAnalysisSoon();
    } else {
      renderResultAnnotations(0);
      renderRecommendationPanel(latestRecommendationPayload);
      dispatchCustomEvent("skin:result", result);
      window.__CG_LATEST_SKIN_ANALYSIS = {
        result: result,
        recommendation: latestRecommendationPayload,
        scanMetadata: latestSkinScanMetadata
      };
      syncBolticLoginRecordAfterLogin(value);
    }
  }
  /*
    Concern-detail updater for the result screen. It avoids rebuilding the page
    when the user switches concern tabs, which keeps scroll and login-gate state
    stable.
  */
  function renderResultAnnotations(idx) {
    var concern = reportConcerns[idx];
    if (!concern) return;
    selectedConcernIdx = idx;
    var image = $("cg-result-face-img");
    var faceName = $("cg-result-face-name");
    var selectedLabel = $("cg-result-selected-label");
    var descTitle = $("cg-result-desc-title");
    var descText = $("cg-result-desc-text");
    if (image) {
      image.referrerPolicy = "no-referrer";
      image.src = getConcernImage(concern, latestSkinResult);
      image.alt = concern.name + " analysis image";
    }
    if (faceName) faceName.textContent = concern.name;
    if (selectedLabel) selectedLabel.textContent = concern.name;
    if (descTitle) descTitle.textContent = concern.name;
    if (descText) descText.innerHTML = getConcernDetailHTML(concern);
    var cards = document.querySelectorAll("#cg-result-scroll .cg-result-item");
    for (var c = 0; c < cards.length; c++) {
      cards[c].classList.toggle(
        "is-active",
        Number(cards[c].getAttribute("data-idx")) === idx
      );
    }
  }
  function wireResultControls() {
    var scroller = $("cg-result-scroll");
    if (scroller) {
      scroller.onclick = function (event) {
        var target = event.target;
        while (
          target &&
          target !== scroller &&
          !target.classList.contains("cg-result-item")
        ) {
          target = target.parentNode;
        }
        if (
          target &&
          target.classList &&
          target.classList.contains("cg-result-item")
        ) {
          renderResultAnnotations(Number(target.getAttribute("data-idx")));
          if (target.scrollIntoView)
            target.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
              inline: "center"
            });
        }
      };
    }
    var cta = $("cg-result-cta");
    if (cta) {
      cta.onclick = function () {
        if (skinAnalysisLoginGateActive) {
          openKwikpassLoginForSkinAnalysis();
          return;
        }
        dispatchCustomEvent("skin:continue-personalize", {
          result: latestSkinResult,
          recommendation: latestRecommendationPayload
        });
        showQuizStage();
      };
    }
    var resultBack = $("cg-result-back");
    if (resultBack) resultBack.onclick = handleSDKBackButton;
    var resultClose = $("cg-result-close");
    if (resultClose) resultClose.onclick = handleSDKCloseButton;
  }
  /*
    Optional GlamAR recommendation panel.

    GlamAR can send recommendation payloads before or after the final scan
    result. These helpers keep the payload cached and render a compact summary
    only when a result page has a panel available.
  */
  function summarizeRecommendationPayload(value) {
    if (!value) return "";
    if (Array.isArray(value))
      return (
        value.length +
        " recommendation item" +
        (value.length === 1 ? "" : "s") +
        " received from GlamAR."
      );
    if (typeof value !== "object")
      return "Recommendation data received from GlamAR.";
    var rows = [];
    Object.keys(value).forEach(function (key) {
      var entry = value[key];
      var count = 1;
      if (Array.isArray(entry)) count = entry.length;
      else if (entry && typeof entry === "object") {
        if (Array.isArray(entry.products)) count = entry.products.length;
        else if (Array.isArray(entry.items)) count = entry.items.length;
        else if (Array.isArray(entry.recommendations))
          count = entry.recommendations.length;
      }
      rows.push(toTitle(key) + " (" + count + ")");
    });
    if (!rows.length) return "Recommendation data received from GlamAR.";
    return (
      "Recommendations are ready for " +
      rows.slice(0, 4).join(", ") +
      (rows.length > 4 ? " and " + (rows.length - 4) + " more" : "") +
      "."
    );
  }
  function renderRecommendationPanel(value) {
    var panel = $("cg-result-recommendations");
    if (!panel) return;
    var summary = summarizeRecommendationPayload(value);
    if (!summary) {
      panel.hidden = true;
      panel.innerHTML = "";
      return;
    }
    panel.hidden = false;
    panel.innerHTML =
      '<div class="cg-result-recommend-title">Recommended Routine Ready</div><div class="cg-result-recommend-text">' +
      escapeHTML(summary) +
      "</div>";
  }
  function handleRecommendationPayload(value) {
    latestRecommendationPayload = value;
    renderRecommendationPanel(value);
    dispatchCustomEvent("skin:recs", value);
    window.__CG_LATEST_SKIN_ANALYSIS = {
      result: latestSkinResult,
      recommendation: latestRecommendationPayload,
      scanMetadata: latestSkinScanMetadata
    };
  }
  function handleScanMetadataPayload(value) {
    latestSkinScanMetadata = jsonSafeCloneForBoltic(value);
    dispatchCustomEvent("skin:scan-metadata", latestSkinScanMetadata);
    window.__CG_LATEST_SKIN_ANALYSIS = {
      result: latestSkinResult,
      recommendation: latestRecommendationPayload,
      scanMetadata: latestSkinScanMetadata
    };
  }
  function handleSkinAnalysisPayload(eventData) {
    var option = getSkinAnalysisOption(eventData);
    var value = getSkinAnalysisValue(eventData);
    if (option === "recommendation") {
      handleRecommendationPayload(value);
      return;
    }
    if (option === "scan-metadata") {
      handleScanMetadataPayload(value);
      return;
    }
    if (option === "result" || normalizeSkinResult(value)) {
      renderSkinAnalysisResult(value);
    }
  }
  /*
    Camera-stage state and layout helpers.

    Shopify headers/announcement bars can be sticky or injected late, so camera
    height is recalculated several times and observed while the camera is active.
    The CSS custom properties are consumed by the Shopify page styles.
  */
  function setCameraMessage(message, isError) {
    var note = $("cg-camera-note");
    if (!note) return;
    note.textContent = message;
    note.classList.toggle("is-error", !!isError);
  }
  function clearStartupTimeout() {
    if (startupTimeoutId) {
      clearTimeout(startupTimeoutId);
      startupTimeoutId = null;
    }
  }
  function clearAnalysisStartRetry() {
    if (analysisStartRetryId) {
      clearTimeout(analysisStartRetryId);
      analysisStartRetryId = null;
    }
  }
  function resetAnalysisFlow() {
    clearAnalysisStartRetry();
    startRequested = false;
    analysisStarted = false;
    analysisStartAttempts = 0;
    sdkClosedAfterResult = false;
    latestSkinScanMetadata = null;
    resetBolticSkinSyncFlag();
    clearSkinAnalysisLoginGateState();
  }
  function getViewportHeight() {
    var viewportHeight =
      (window.visualViewport && window.visualViewport.height) ||
      window.innerHeight ||
      document.documentElement.clientHeight ||
      760;
    return Math.max(1, Math.floor(viewportHeight));
  }
  function getHeaderOffsetSelectors() {
    return [
      "#shopify-section-announcement-bar",
      "#shopify-section-header",
      ".announcement-bar-section",
      ".announcement-bar-wrapper",
      ".announcement-bar",
      ".shopify-section-group-header-group",
      ".shopify-section-header",
      "sticky-header",
      ".header-wrapper",
      ".site-header",
      ".section-header",
      '[data-section-type="header"]',
      'header[role="banner"]',
      "header"
    ];
  }
  function getHeaderOffsetNodes(wrapper) {
    var selectors = getHeaderOffsetSelectors();
    var out = [];
    for (var s = 0; s < selectors.length; s++) {
      var nodes = document.querySelectorAll(selectors[s]);
      for (var i = 0; i < nodes.length; i++) {
        var node = nodes[i];
        if (wrapper && wrapper.contains(node)) continue;
        if (out.indexOf(node) === -1) out.push(node);
      }
    }
    return out;
  }
  function getVisibleHeaderOffset(wrapper, viewportHeight) {
    var nodes = getHeaderOffsetNodes(wrapper);
    var maxBottom = 0;
    var scanLimit = Math.min(
      viewportHeight - 1,
      Math.max(160, viewportHeight * 0.35)
    );
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      var rect = node.getBoundingClientRect && node.getBoundingClientRect();
      if (!rect || rect.width < 1 || rect.height < 1) continue;
      if (rect.bottom <= 0 || rect.top > scanLimit) continue;
      var style = window.getComputedStyle
        ? window.getComputedStyle(node)
        : null;
      if (style && (style.display === "none" || style.visibility === "hidden"))
        continue;
      maxBottom = Math.max(
        maxBottom,
        Math.min(viewportHeight, Math.max(0, rect.bottom))
      );
    }
    return Math.round(maxBottom);
  }
  function getCameraTopOffset(wrapper, viewportHeight) {
    var headerOffset = getVisibleHeaderOffset(wrapper, viewportHeight);
    if (headerOffset > 0) return headerOffset;
    if (wrapper && !wrapper.classList.contains("cg-camera-mode")) {
      var rect =
        wrapper.getBoundingClientRect && wrapper.getBoundingClientRect();
      if (rect && Number.isFinite(rect.top))
        return Math.round(Math.max(0, Math.min(viewportHeight - 1, rect.top)));
    }
    var current = wrapper
      ? parseFloat(
          window.getComputedStyle(wrapper).getPropertyValue("--cg-camera-top")
        )
      : 0;
    return Number.isFinite(current)
      ? Math.round(Math.max(0, Math.min(viewportHeight - 1, current)))
      : 0;
  }
  function updateCameraViewportHeight() {
    var wrapper = getWrapperNode();
    if (!wrapper) return;
    var viewportHeight = getViewportHeight();
    var topOffset = Math.min(
      viewportHeight - 1,
      getCameraTopOffset(wrapper, viewportHeight) + CAMERA_HEADER_CLEARANCE
    );
    var availableHeight = Math.max(
      1,
      Math.floor(viewportHeight - topOffset - CAMERA_BOTTOM_CLEARANCE)
    );
    wrapper.style.setProperty("--cg-camera-top", topOffset + "px");
    wrapper.style.setProperty("--cg-available-height", availableHeight + "px");
  }
  function refreshCameraViewportHeightIfActive() {
    var wrapper = getWrapperNode();
    if (wrapper && wrapper.classList.contains("cg-camera-mode")) {
      updateCameraViewportHeight();
    }
  }
  function clearCameraViewportHeightRefreshes() {
    for (var i = 0; i < cameraLayoutRefreshIds.length; i++) {
      window.clearTimeout(cameraLayoutRefreshIds[i]);
    }
    cameraLayoutRefreshIds = [];
  }
  function scheduleCameraViewportHeightRefreshes() {
    clearCameraViewportHeightRefreshes();
    [0, 32, 90, 180, 360, 720].forEach(function (delay) {
      cameraLayoutRefreshIds.push(
        window.setTimeout(refreshCameraViewportHeightIfActive, delay)
      );
    });
  }
  function stopCameraHeaderResizeObserver() {
    if (cameraHeaderResizeObserver) {
      cameraHeaderResizeObserver.disconnect();
      cameraHeaderResizeObserver = null;
    }
  }
  function startCameraHeaderResizeObserver() {
    stopCameraHeaderResizeObserver();
    if (!window.ResizeObserver) return;
    var wrapper = getWrapperNode();
    var nodes = getHeaderOffsetNodes(wrapper);
    if (!nodes.length) return;
    cameraHeaderResizeObserver = new ResizeObserver(function () {
      scheduleCameraViewportHeightRefreshes();
    });
    for (var i = 0; i < nodes.length; i++) {
      cameraHeaderResizeObserver.observe(nodes[i]);
    }
  }
  function setCameraMode(isActive) {
    var wrapper = getWrapperNode();
    if (!wrapper) return;
    if (isActive) {
      updateCameraViewportHeight();
      wrapper.classList.add("cg-camera-mode");
      if (document.documentElement)
        document.documentElement.classList.add("cg-camera-active-page");
      if (document.body) document.body.classList.add("cg-camera-active-page");
      updateCameraViewportHeight();
      startCameraHeaderResizeObserver();
      scheduleCameraViewportHeightRefreshes();
    } else {
      clearCameraViewportHeightRefreshes();
      stopCameraHeaderResizeObserver();
      wrapper.classList.remove("cg-camera-mode");
      wrapper.style.removeProperty("--cg-camera-top");
      wrapper.style.removeProperty("--cg-available-height");
      if (document.documentElement)
        document.documentElement.classList.remove("cg-camera-active-page");
      if (document.body)
        document.body.classList.remove("cg-camera-active-page");
    }
  }
  function bindViewportResize() {
    if (viewportResizeBound) return;
    viewportResizeBound = true;
    window.addEventListener("resize", function () {
      var wrapper = getWrapperNode();
      if (wrapper && wrapper.classList.contains("cg-camera-mode"))
        updateCameraViewportHeight();
    });
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", function () {
        var wrapper = getWrapperNode();
        if (wrapper && wrapper.classList.contains("cg-camera-mode"))
          updateCameraViewportHeight();
      });
    }
  }
  function setCgLoading(isLoading) {
    if (document.body) {
      document.body.classList.toggle("cg-sdk-loading", !!isLoading);
    }
  }
  function removeAllDebugOverlays() {
    var ids = ["cg-debug-overlay", "debug-overlay", "cg-debug"];
    for (var i = 0; i < ids.length; i++) {
      var node = document.getElementById(ids[i]);
      if (node) node.remove();
    }
  }
  function isSDKModuleLoaded() {
    if (!window.GlamAR || typeof window.GlamAR.isLoaded !== "function")
      return false;
    try {
      return !!window.GlamAR.isLoaded();
    } catch (error) {
      return false;
    }
  }
  /*
    GlamAR iframe preparation.

    The SDK creates iframes dynamically. These helpers grant camera permission,
    allow scrolling when needed, and patch transparent/video layout styles inside
    same-origin frames when accessible.
  */
  function patchSDKFrameLayout(frame) {
    if (!frame || frame.tagName !== "IFRAME") return;
    frame.style.background = "transparent";
    try {
      var doc =
        frame.contentDocument ||
        (frame.contentWindow && frame.contentWindow.document);
      if (!doc || !doc.documentElement) return;
      var head =
        doc.head || doc.getElementsByTagName("head")[0] || doc.documentElement;
      var style = doc.getElementById("cg-sdk-camera-layout-fix");
      if (!style) {
        style = doc.createElement("style");
        style.id = "cg-sdk-camera-layout-fix";
        head.appendChild(style);
      }
      style.textContent =
        "html,body,#app{background:transparent!important;} .video-container{top:0!important;bottom:0!important;}";
    } catch (error) {}
  }
  function applyCameraPermission(frame) {
    if (!frame || frame.tagName !== "IFRAME") return;
    var allow = frame.getAttribute("allow") || "";
    if (allow.indexOf("camera") === -1) {
      frame.setAttribute("allow", allow ? allow + "; camera" : "camera");
    }
    frame.setAttribute("scrolling", "yes");
    frame.style.overflow = "auto";
    patchSDKFrameLayout(frame);
    if (!frame.__cgLoadBound) {
      frame.__cgLoadBound = true;
      frame.addEventListener("load", function () {
        patchSDKFrameLayout(frame);
        setTimeout(function () {
          patchSDKFrameLayout(frame);
        }, 120);
        if (isSDKModuleLoaded()) {
          markSDKReady("Camera module loaded. Opening skin analysis.");
        } else if (startRequested) {
          requestSkinAnalysisStart();
        }
      });
    }
  }
  function ensureCameraPermission(node) {
    if (!node) return;
    applyCameraPermission(node);
    if (!node.querySelectorAll) return;
    Array.prototype.forEach.call(
      node.querySelectorAll("iframe"),
      applyCameraPermission
    );
  }
  function monitorContainer() {
    var container = $(CONTAINER);
    if (!container) {
      return;
    }
    Array.prototype.forEach.call(
      container.querySelectorAll("iframe"),
      applyCameraPermission
    );
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type !== "childList") return;
        for (var i = 0; i < mutation.addedNodes.length; i++) {
          var added = mutation.addedNodes[i];
          ensureCameraPermission(mutation.addedNodes[i]);
        }
      });
    });
    observer.observe(container, { childList: true, subtree: true });
  }
  /*
    SDK loading and start retry loop.

    loadSDKScript() only injects the wrapper once. requestSkinAnalysisStart()
    retries until GlamAR reports the skin-analysis module loaded, then calls
    skinAnalysis("start"). This protects slower networks and late iframe loads.
  */
  function loadSDKScript() {
    return new Promise(function (resolve, reject) {
      if (window.GlamAR) {
        resolve();
        return;
      }
      var existing = document.querySelector('script[data-cg-sdk="glamar"]');
      if (existing) {
        var attempts = 0;
        var waitForGlobal = setInterval(function () {
          if (window.GlamAR) {
            clearInterval(waitForGlobal);
            resolve();
          } else if (++attempts > 80) {
            clearInterval(waitForGlobal);
            reject(
              new Error(
                "GlamAR global not available after existing script load"
              )
            );
          }
        }, 100);
        existing.addEventListener(
          "error",
          function () {
            clearInterval(waitForGlobal);
            reject(new Error("SDK script failed to load"));
          },
          { once: true }
        );
        return;
      }
      var script = document.createElement("script");
      script.src = SDK_SRC;
      script.async = true;
      script.setAttribute("data-cg-sdk", "glamar");
      script.onload = function () {
        if (window.GlamAR) resolve();
        else reject(new Error("GlamAR global not available after load"));
      };
      script.onerror = function () {
        reject(new Error("SDK script failed to load"));
      };
      document.head.appendChild(script);
    });
  }
  function startSkinAnalysis() {
    if (analysisStarted) {
      return true;
    }
    if (!window.GlamAR || !window.GlamAR.skinAnalysis) {
      return false;
    }
    if (!isSDKModuleLoaded()) {
      return false;
    }
    try {
      latestSkinScanMetadata = null;
      setCameraMessage(
        "Align your face inside the frame and look straight at the camera."
      );
      window.GlamAR.skinAnalysis("start");
      analysisStarted = true;
      clearAnalysisStartRetry();
      return true;
    } catch (error) {
      showError(
        "Unable to start the camera flow. Please reload and try again."
      );
      return false;
    }
  }
  function requestSkinAnalysisStart() {
    startRequested = true;
    clearAnalysisStartRetry();
    analysisStartAttempts = 0;
    function tryStart() {
      if (analysisStarted) return;
      analysisStartAttempts += 1;
      if (isSDKModuleLoaded() && !sdkReady) {
        markSDKReady("Camera module loaded. Opening skin analysis.");
      }
      startSkinAnalysis();
      if (analysisStarted) return;
      if (analysisStartAttempts < 80) {
        analysisStartRetryId = setTimeout(tryStart, 250);
      } else {
        showError(
          "Camera module loaded, but skin analysis did not open. Please reload and try again."
        );
      }
    }
    tryStart();
  }
  function markSDKReady(message) {
    sdkReady = true;
    clearStartupTimeout();
    setCgLoading(false);
    setCameraMessage(message || DEFAULT_CAMERA_MESSAGE);
    if (startRequested) startSkinAnalysis();
  }
  function handleSDKLifecycleEvent(eventName, payload) {
    var name = eventName;
    var data = payload;
    if (eventName && typeof eventName === "object") {
      data =
        eventName.payload ||
        eventName.value ||
        eventName.detail ||
        eventName.data ||
        eventName;
      name =
        eventName.event ||
        eventName.type ||
        eventName.name ||
        eventName.options ||
        "";
    }
    name = String(name || "");
    switch (name) {
      case "loaded":
      case "ready":
      case "initialized":
      case "render":
      case "mount":
        markSDKReady(DEFAULT_CAMERA_MESSAGE);
        break;
      case "skin-analysis":
        if (data && (data.options === "result" || data.option === "result")) {
          markSDKReady("Analysis complete.");
        } else {
          markSDKReady(
            "Align your face inside the outline and look straight at the camera."
          );
        }
        break;
      case "camera-opened":
        markSDKReady("Camera is ready. Align your face inside the frame.");
        break;
      default:
        break;
    }
  }
  function tryRenderFromRawPayload(payload) {
    if (!payload || typeof payload !== "object") return false;
    var result = normalizeSkinResult(payload);
    if (result) {
      renderSkinAnalysisResult(payload);
      return true;
    }
    var keys = Object.keys(payload);
    for (var i = 0; i < keys.length; i++) {
      var val = payload[keys[i]];
      if (val && typeof val === "object") {
        result = normalizeSkinResult(val);
        if (result) {
          renderSkinAnalysisResult(val);
          return true;
        }
      }
    }
    return false;
  }
  function bindPostMessageFallback() {
    window.addEventListener("message", function (event) {
      var data = event.data;
      if (!data || typeof data !== "object") return;
      var eventType = data.event || data.type || data.name || data.action || "";
      if (
        typeof eventType === "string" &&
        (eventType.indexOf("skin") > -1 ||
          eventType.indexOf("analysis") > -1 ||
          eventType.indexOf("result") > -1 ||
          eventType.indexOf("scan") > -1)
      ) {
        console.log("[GlamAR postMessage]", eventType, data);
        handleSkinAnalysisPayload(data);
        if (!latestSkinResult)
          tryRenderFromRawPayload(
            data.payload || data.data || data.value || data
          );
      }
    });
  }
  /*
    SDK event binding.

    GlamAR has emitted several event names across versions, so we listen to the
    canonical skin-analysis event plus common result aliases and a postMessage
    fallback. All final payloads converge through handleSkinAnalysisPayload().
  */
  function bindSDKEvents() {
    if (sdkEventsBound) return;
    var GA = window.GlamAR;
    if (!GA || !GA.addEventListener) {
      throw new Error("GlamAR.addEventListener not available");
    }
    sdkEventsBound = true;
    GA.addEventListener("loaded", function () {
      handleSDKLifecycleEvent("loaded");
    });
    ["ready", "initialized", "render", "mount"].forEach(function (eventName) {
      try {
        GA.addEventListener(eventName, function () {
          handleSDKLifecycleEvent(eventName);
        });
      } catch (error) {}
    });
    try {
      GA.addEventListener("skin-analysis", function (payload) {
        console.log(
          "[GlamAR skin-analysis]",
          JSON.stringify(payload).slice(0, 300)
        );
        handleSkinAnalysisPayload(payload);
        handleSDKLifecycleEvent("skin-analysis", payload);
      });
    } catch (error) {}
    [
      "scan-complete",
      "scanComplete",
      "analysisResult",
      "analysis-result",
      "skinAnalysis",
      "skin_analysis"
    ].forEach(function (eventName) {
      try {
        GA.addEventListener(eventName, function (payload) {
          console.log(
            "[GlamAR " + eventName + "]",
            JSON.stringify(payload).slice(0, 300)
          );
          handleSkinAnalysisPayload(payload);
          if (!latestSkinResult) tryRenderFromRawPayload(payload);
        });
      } catch (error) {}
    });
    try {
      GA.addEventListener("analysis-started", function (payload) {
        setCgLoading(false);
        setCameraMessage(
          "Analyzing your skin. Hold still for the best result."
        );
      });
    } catch (error) {}
    try {
      GA.addEventListener("analysis-complete", function (payload) {
        console.log(
          "[GlamAR analysis-complete]",
          JSON.stringify(payload).slice(0, 300)
        );
        setCgLoading(false);
        setCameraMessage("Analysis complete.");
        handleSkinAnalysisPayload(payload);
        if (!latestSkinResult) tryRenderFromRawPayload(payload);
      });
    } catch (error) {}
    GA.addEventListener("camera-opened", function (payload) {
      handleSDKLifecycleEvent("camera-opened");
    });
    GA.addEventListener("camera-access-issue", function (payload) {
      showError(
        "Camera access is blocked. Allow camera permission in your browser settings and try again."
      );
    });
    GA.addEventListener("error", function (eventData) {
      console.log("[GlamAR error]", eventData);
      var message =
        "Something went wrong while opening the camera. Please reload and try again.";
      if (eventData && eventData.message) message = eventData.message;
      showError(message);
    });
    GA.addEventListener("add-to-cart", function (eventData) {
      addToCart(eventData);
    });
    GA.addEventListener("ui-interaction", function (eventData) {
      handleSDKUIInteraction(eventData);
    });
    bindPostMessageFallback();
  }
  /*
    SDK navigation bridge. The GlamAR top nav can send back/close/cart events;
    these handlers translate them into this page's stage transitions.
  */
  function handleSDKUIInteraction(eventData) {
    var data = getEventPayload(eventData);
    if (!data || data.screenType !== "topNavigationPanel") return;
    if (sdkStageExitInProgress) {
      return;
    }
    switch (data.buttonType) {
      case "back":
        handleSDKBackButton();
        break;
      case "close":
        handleSDKCloseButton();
        break;
      case "globalCart":
        openCart();
        break;
      default:
        break;
    }
  }
  function closeSDKCanvasForStageExit(reason) {
    if (!window.GlamAR || !window.GlamAR.close) return false;
    sdkStageExitInProgress = true;
    try {
      window.GlamAR.close();
    } catch (error) {}
    setTimeout(function () {
      sdkStageExitInProgress = false;
    }, 500);
    return true;
  }
  function closeSDKAfterReportReady() {
    if (sdkClosedAfterResult) return;
    if (closeSDKCanvasForStageExit("report ready")) {
      sdkClosedAfterResult = true;
    }
  }
  function handleSDKBackButton() {
    var loginGateBack = skinAnalysisLoginGateActive;
    closeSDKCanvasForStageExit("sdk back navigation");
    resetAnalysisFlow();
    setCgLoading(false);
    if (loginGateBack) {
      showLandingStage();
      return;
    }
    showIntroStage();
  }
  function handleSDKCloseButton() {
    closeSDKCanvasForStageExit("sdk close navigation");
    resetAnalysisFlow();
    showLandingStage();
  }
  /*
    Initializes GlamAR for skin analysis.

    sdkInitPromise prevents duplicate init calls if users double-click. The
    startup timeout shows a friendly camera error if neither the SDK nor the
    analysis flow becomes ready in time.
  */
  function initSDK() {
    if (sdkInited) {
      return Promise.resolve(true);
    }
    if (sdkInitPromise) {
      return sdkInitPromise;
    }
    sdkInitPromise = loadSDKScript()
      .then(function () {
        if (!window.GlamAR || !window.GlamAR.init) {
          throw new Error("GlamAR.init method missing");
        }
        bindSDKEvents();
        monitorContainer();
        var glamarOptions = {};
        glamarOptions.platform = "web";
        glamarOptions.category = "skinanalysis";
        glamarOptions.configuration = {};
        glamarOptions.configuration.skinAnalysis = {};
        glamarOptions.configuration.skinAnalysis.appId = APP_ID;
        glamarOptions.meta = {};
        glamarOptions.meta.sdkVersion = "2.0.0";
        window.GlamAR.init(CONTAINER, ACCESS_KEY, glamarOptions);
        sdkInited = true;
        clearStartupTimeout();
        startupTimeoutId = setTimeout(function () {
          if (!sdkReady && !analysisStarted) {
            showError(
              "The camera is taking longer than expected to load. Please reload and try again."
            );
          } else {
          }
        }, 15000);
        return true;
      })
      .catch(function (error) {
        sdkInitPromise = null;
        sdkEventsBound = false;
        throw error;
      });
    return sdkInitPromise;
  }
  /*
    Shopify cart adapters used by SDK-originated add-to-cart events.

    The custom routine cards use the quieter cart-update path above, but GlamAR
    can also emit add-to-cart events. These helpers normalize SDK SKUs and use
    Shopify's /cart/add.js endpoint plus theme drawer refresh.
  */
  function parseCartEventData(data) {
    if (typeof data === "string" || typeof data === "number") {
      return { skuId: String(data), quantity: 1 };
    }
    var payload = data && data.payload ? data.payload : {};
    var rawSku =
      (data &&
        (data.skuId ||
          data.skuID ||
          data.variantId ||
          data.variantID ||
          data.id ||
          data.sku ||
          data.productVariantId)) ||
      (payload &&
        (payload.skuId ||
          payload.variantId ||
          payload.id ||
          payload.sku ||
          payload.productVariantId)) ||
      null;
    var quantity = Number(
      (data && data.quantity) || (payload && payload.quantity) || 1
    );
    if (!Number.isFinite(quantity) || quantity < 1) quantity = 1;
    return {
      skuId: rawSku ? String(rawSku) : null,
      quantity: quantity
    };
  }
  function normalizeVariantRef(rawSku) {
    if (!rawSku) return { variantId: null };
    var raw = String(rawSku).trim();
    if (!raw) return { variantId: null };
    if (raw.indexOf("gid://shopify/ProductVariant/") === 0) {
      return { variantId: raw.split("/").pop() };
    }
    var base = raw.indexOf("_") > -1 ? raw.split("_")[0] : raw;
    var parts = base.split("/");
    return { variantId: parts[parts.length - 1] };
  }
  function fetchCartData() {
    return fetch("/cart.json")
      .then(function (response) {
        return response.json();
      })
      .then(function (cartData) {
        routineCartDataCache = cartData;
        return cartData;
      });
  }
  function getCartDataForQuietUpdate() {
    if (routineCartDataCache) return Promise.resolve(routineCartDataCache);
    return fetchCartData();
  }
  function getCartVariantQuantity(cartData, variantId) {
    var target = String(variantId || "");
    var items = cartData && Array.isArray(cartData.items) ? cartData.items : [];
    var quantity = 0;
    for (var i = 0; i < items.length; i++) {
      var itemId = String(items[i].id || items[i].variant_id || "");
      if (itemId === target) quantity += Number(items[i].quantity) || 0;
    }
    return quantity;
  }
  function updateCartQuantities(updates) {
    return fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: JSON.stringify({ updates: updates })
    }).then(function (response) {
      if (!response.ok) {
        return response.json().then(function (errorData) {
          throw new Error(errorData.description || "Failed to update cart");
        });
      }
      return response.json().then(function (cartData) {
        routineCartDataCache = cartData;
        return cartData;
      });
    });
  }
  function addCartVariantQuietly(variantId, quantity) {
  var safeQuantity = Number(quantity) || 1;

  return getCartDataForQuietUpdate()
    .then(function(cartData) {

      var currentQuantity = getCartVariantQuantity(
        cartData,
        variantId
      );

      var updates = {};
      updates[String(variantId)] =
        currentQuantity + safeQuantity;

      return updateCartQuantities(updates);
    })

    .then(function(cartData) {

      routineCartDataCache = cartData;

      updateCartBadge(
        cartData.item_count,
        cartData
      );

      /*
        IMPORTANT:
        Pipeline custom cart refresh event
      */
      document.dispatchEvent(
        new CustomEvent("theme:cart:change", {
          bubbles: true,
          detail: {
            cart: cartData
          }
        })
      );

      return true;
    })

    .catch(function(error) {

      console.error(
        "Routine cart add error:",
        error
      );

      return false;
    });
}
  function addToCart(eventData) {
    var parsed = parseCartEventData(eventData);
    if (!parsed.skuId) return Promise.resolve(false);
    var useRawCartId =
      eventData && typeof eventData === "object" && eventData.useRawCartId;
    var variantId = useRawCartId
      ? parsed.skuId
      : normalizeVariantRef(parsed.skuId).variantId;
    if (!variantId) return Promise.resolve(false);
    return fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: JSON.stringify({ id: variantId, quantity: parsed.quantity })
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (errorData) {
            throw new Error(
              errorData.description || "Failed to add item to cart"
            );
          });
        }
        return response.json();
      })
      .then(function () {
        return refreshCartDrawer();
      })
      .then(function () {
        if (window.GlamAR && window.GlamAR.addedToCart) {
          window.GlamAR.addedToCart(parsed.skuId, { success: true });
        }
        return true;
      })
      .catch(function (error) {
        if (window.GlamAR && window.GlamAR.addedToCart) {
          window.GlamAR.addedToCart(parsed.skuId, {
            success: false,
            error: error.message
          });
        }
        return false;
      });
  }
  /*
    Cart UI refresh helpers.

    openCart() tries the GoKwik side-cart first, then common Shopify drawer
    events/selectors, then falls back to /cart. refreshCartDrawer() keeps the
    theme drawer and badge in sync after SDK-originated cart additions.
  */
  function openCart() {

  var drawer = document.querySelector(
    '[data-drawer="drawer-cart"]'
  );

  if (drawer) {

    drawer.dispatchEvent(
      new CustomEvent("theme:drawer:open", {
        bubbles: false
      })
    );

    return;
  }

  /*
    Fallback only
  */
  var cartLink =
    document.querySelector('[data-cart-toggle]') ||
    document.querySelector('a[href="/cart"]');

  if (cartLink) {
    cartLink.click();
  }
}
  function refreshCartDrawer() {
    return fetch("/cart.json")
      .then(function (response) {
        return response.json();
      })
      .then(function (cartData) {
        routineCartDataCache = cartData;
        updateCartBadge(cartData.item_count, cartData);
        return fetch("/?sections=cart-drawer", { credentials: "same-origin" })
          .then(function (response) {
            return response.json();
          })
          .then(function (sections) {
            var html = sections["cart-drawer"];
            if (html) {
              var drawer = document.querySelector("cart-drawer");
              if (drawer) {
                var parser = new DOMParser();
                var doc = parser.parseFromString(html, "text/html");
                var nextDrawer = doc.querySelector("cart-drawer");
                if (nextDrawer) drawer.replaceWith(nextDrawer);
                else drawer.innerHTML = html;
              }
            }
            fireCartEvents(cartData);
          })
          .catch(function () {
            fireCartEvents(cartData);
          });
      });
  }
  function updateCartBadge(count) {
    var bubble =
      document.querySelector(".cart-link__bubble") ||
      document.querySelector(".cg-cart-count-bubble") ||
      document.querySelector(".cart-count-bubble") ||
      document.querySelector("[data-cart-count-bubble]");
    var number =
      document.querySelector(".cart-link__bubble-num") ||
      (bubble && bubble.querySelector("[data-cart-count]")) ||
      (bubble && bubble.querySelector("span")) ||
      bubble;
    if (!bubble) {
      var parent =
        document.querySelector(".navlink--cart") ||
        document.querySelector('.header__mobile__button[href="/cart"]') ||
        document.querySelector(".cart-link") ||
        document.querySelector('a[href="/cart"]');
      if (!parent) return;
      bubble = document.createElement("span");
      bubble.className = "cg-cart-count-bubble";
      number = document.createElement("span");
      number.setAttribute("data-cart-count", "");
      bubble.appendChild(number);
      parent.appendChild(bubble);
      if (window.getComputedStyle(parent).position === "static") {
        parent.style.position = "relative";
      }
    }
    if (!number) number = bubble.querySelector(".cart-link__bubble-num");
    if (!number) return;
    number.textContent = count;
    var statuses = document.querySelectorAll(".header__cart__status");
    for (var i = 0; i < statuses.length; i++) {
      statuses[i].textContent = "";
    }
    if (count > 0) {
      bubble.classList.add("cart-link__bubble--visible");
      bubble.classList.add("cg-cart-count-bubble--visible");
      bubble.style.display = "";
    } else {
      bubble.classList.remove("cart-link__bubble--visible");
      bubble.classList.remove("cg-cart-count-bubble--visible");
      bubble.style.display = "none";
    }
  }
  function fireCartEvents(cartData) {
    ["cart:updated", "cart:change", "cart:refresh"].forEach(
      function (eventName) {
        var eventObject = new CustomEvent(eventName, {
          detail: cartData,
          bubbles: true
        });
        document.dispatchEvent(eventObject);
        window.dispatchEvent(eventObject);
      }
    );
  }
  function showError(message) {
    clearStartupTimeout();
    clearAnalysisStartRetry();
    setCgLoading(false);
    setCameraMessage(message, true);
  }
  /*
    Stage navigation.

    These functions toggle the high-level page states: landing, intro, camera,
    and result. Result/quiz/diagnosis/routine screens are rendered inside the
    result stage; the camera stage owns SDK loading and scan start.
  */
  function showLandingStage() {
    var app = getAppNode();
    if (!app) return;
    resetBolticSkinSyncFlag();
    setCameraMode(false);
    resetStageScroll("cg-landing-stage");
    app.classList.remove("cg-show-intro");
    app.classList.remove("cg-camera-active");
    app.classList.remove("cg-result-active");
    setCgLoading(false);
    clearAnalysisStartRetry();
    setCameraMessage(DEFAULT_CAMERA_MESSAGE, false);
    scrollWrapperIntoView();
  }
  function showIntroStage() {
    var app = getAppNode();
    if (!app) return;
    setCameraMode(false);
    resetStageScroll("cg-intro-stage");
    app.classList.add("cg-show-intro");
    app.classList.remove("cg-camera-active");
    app.classList.remove("cg-result-active");
    scrollWrapperIntoView();
  }
  function activateCameraStage() {
    var app = getAppNode();
    sdkClosedAfterResult = false;
    resetBolticSkinSyncFlag();
    setCameraMode(true);
    if (app) {
      resetStageScroll("cg-camera-stage");
      app.classList.add("cg-show-intro");
      app.classList.add("cg-camera-active");
      app.classList.remove("cg-result-active");
    }
    setCameraMessage(
      "Preparing your camera. Please allow access when prompted."
    );
    setCgLoading(true);
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        initSDK()
          .then(function () {
            requestSkinAnalysisStart();
          })
          .catch(function (error) {
            showError(
              "Failed to load the camera. Please reload and try again."
            );
          });
      });
    });
  }
  function handleBack() {
    var app = getAppNode();
    if (
      app &&
      app.classList.contains("cg-show-intro") &&
      !app.classList.contains("cg-camera-active")
    ) {
      showLandingStage();
      return;
    }
    if (window.history.length > 1) {
      window.history.back();
    }
  }
  function bindIntroActions() {
    var start = $("cg-asaya-start");
    var proceed = $("cg-asaya-proceed");
    var skip = $("cg-asaya-skip");
    var back = $("cg-asaya-back");
    var cameraBack = $("cg-camera-back");
    var cameraClose = $("cg-camera-close");
    if (start) start.addEventListener("click", showIntroStage);
    if (proceed) proceed.addEventListener("click", activateCameraStage);
    if (skip) skip.addEventListener("click", activateCameraStage);
    if (back) back.addEventListener("click", handleBack);
    if (cameraBack) cameraBack.addEventListener("click", handleSDKBackButton);
    if (cameraClose)
      cameraClose.addEventListener("click", handleSDKCloseButton);
  }
  /*
    Entrypoint.

    Runs after DOM ready, cleans stale overlays, binds viewport/stage handlers,
    and leaves the landing stage ready for user interaction.
  */
  function boot() {
    removeAllDebugOverlays();
    bindViewportResize();
    setCameraMessage(DEFAULT_CAMERA_MESSAGE, false);
    setCgLoading(false);
    bindIntroActions();
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  
})();

